/** Poli Poker — browser app (single-player vs 4 AI). Classical whiskey/gold design. */
import { faceHtml, chipHtml } from "./cards/face.ts";
import { applyTheme, savedThemeId, saveThemeId, currentTheme, assetReady } from "./themes/apply.ts";
import { music, initMusic, hasMusic } from "./music.ts";
import { playAnthem, stopAnthem } from "./anthem.ts";
import { initTables, openTables, multiplayerOpen, sendToTable, setAvatarDrawer, tableLooks } from "./tables.ts";
import type { ServerMessage } from "../shared/table-protocol.ts";
import { songNote } from "./songs.ts";
import { readyThemes, themeById, THEMES } from "./themes/registry.ts";
import USAMapRaw from "@svg-maps/usa";
import {
  PoliGame,
  stateName,
  type Action,
  type BoardSnapshot,
  type Controller,
  type GameConfig,
  type GameObserver,
  type HandRecord,
  type PlayerConfig,
  type PublicPlayer,
  type StateMethod,
  type TurnView,
  type AfterSwap,
  type PeekRecord,
  type PlayerTotals,
} from "../engine/whiskey.ts";
import { PEEK_COST } from "../engine/peek.ts";
import { AIController } from "../engine/ai.ts";
import { initAccount, showAccountButton, recordMatch, myPlayerId, openSignIn, cloudList, cloudSave, cloudEnd, myProfile } from "./account.ts";
import { initHallOfFame, openHallOfFame, hofChanged } from "./hof.ts";
import { encodeCard, MATCH_HANDS, type SubmitHand, type SubmitMatch } from "../shared/match-record.ts";
import { PALETTE, assignAiColours } from "./colours.ts";
import { colourWheel } from "./colour-wheel.ts";
import { SmartAIController } from "../engine/ai/smart.ts";
import { BUDGETS, PROFILES, PROFILE_ORDER, type Profile, type ProfileName } from "../engine/ai/config.ts";
import { PERSONAS, persona, personaProfile, pickPersonas } from "../engine/personas.ts";
import { personaPic, personaPicById, pickOpponents } from "./opponents.ts";
import { makeRng } from "../engine/rng.ts";
import { borders } from "../engine/states.ts";
import { evaluate, CATEGORY_NAME, HandCategory } from "../engine/handEval.ts";
import { shuffledDeck, type Card } from "../engine/cards.ts";
import { seatByDraw } from "../engine/draw.ts";
import { nextFact, type ResolvedFact } from "./facts.ts";

const ACCESS_CODE = "POLI2026";
const AI_DELAY = 620;

const USAMap = USAMapRaw as unknown as { viewBox: string; locations: { id: string; name: string; path: string }[] };
const COLOURS = PALETTE.map((p) => p.colour);
const COLOUR_NAMES = PALETTE.map((p) => p.name);
const ordinalWord = (n: number) => ["", "1st", "2nd", "3rd", "4th", "5th"][n] ?? `${n}th`;
const AI_PARTIES = ["Reform", "Progress", "Liberty", "Unity", "Green", "National"];
const POS_PTS = [0, 5, 3, 2, 1, 0];
const SEAT_ANCHOR = ["left:5%;top:1%", "right:5%;top:1%", "left:0%;top:44%", "right:0%;top:44%"];
// Which player sits in each slot above (top-left, top-right, middle-left, middle-right). Play and the
// deal go clockwise round the table: you (bottom) → middle-left → top-left → top-right → middle-right.
const SLOT_PLAYER = [2, 3, 1, 4];
// A judge's gavel — marks the player who knocked (plaque, results, banner).
const GAVEL = `<svg class="gavel" viewBox="0 0 24 24" aria-hidden="true"><g transform="rotate(-40 12 10)" fill="currentColor"><rect x="5" y="2.5" width="12" height="6" rx="1.4"/><rect x="3.4" y="3.4" width="2.2" height="4.2" rx=".7"/><rect x="16.4" y="3.4" width="2.2" height="4.2" rx=".7"/><rect x="9.9" y="8.5" width="2.2" height="11" rx="1"/></g><rect x="11" y="19.6" width="11" height="2.6" rx="1.1" fill="currentColor"/></svg>`;
// The President's seal: a ring of thirteen stars in the President's colour around a spade and a laurel.
const SEAL_SVG = `<svg viewBox="0 0 200 200" aria-hidden="true"><defs><path id="seal-arc" d="M30 100a70 70 0 1 1 140 0"/></defs>` +
  `<circle cx="100" cy="100" r="96" fill="#1a130b" stroke="#d4af37" stroke-width="4"/><circle cx="100" cy="100" r="84" fill="var(--pc,#2f7a4f)" stroke="#f3cf7a" stroke-width="2"/>` +
  `<circle cx="100" cy="100" r="56" fill="#1a130b" stroke="#f3cf7a" stroke-width="2"/>` +
  `<text font-family="Cinzel,serif" font-size="15" font-weight="700" fill="#fff6dc" letter-spacing="3"><textPath href="#seal-arc" startOffset="50%" text-anchor="middle">PRESIDENT · POLIPOKER</textPath></text>` +
  Array.from({ length: 13 }, (_, i) => { const a = Math.PI * (0.95 + (i * 1.1) / 12); return `<text x="${100 + 70 * Math.cos(a)}" y="${106 - 70 * Math.sin(a)}" font-size="13" fill="#f3cf7a" text-anchor="middle">★</text>`; }).join("") +
  `<path d="M100 66c14 16 30 26 30 40a14 14 0 0 1-24 10l4 16h-20l4-16a14 14 0 0 1-24-10c0-14 16-24 30-40z" fill="#f3cf7a"/>` +
  `<path d="M58 132c10 12 26 18 42 18s32-6 42-18" fill="none" stroke="#7fbf6a" stroke-width="5" stroke-linecap="round" stroke-dasharray="2 9"/></svg>`;
// Compact-screen icons (stroked, so they take the button's colour).
const ico = (d: string) => `<svg class="ci" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
const CI = {
  hand: ico('<rect x="3" y="5" width="11" height="15" rx="1.5"/><path d="M9 4.5 10.2 3.4a1.5 1.5 0 0 1 2 0l7.5 6.8a1.5 1.5 0 0 1 .1 2.1L16 16.5"/>'),
  map: ico('<path d="M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20z"/><path d="M9 4v13.5M15 6.5V20"/>'),
  players: ico('<circle cx="9" cy="8" r="3.2"/><path d="M3 19c.6-3.4 3-5 6-5s5.4 1.6 6 5"/><circle cx="17" cy="9" r="2.5"/><path d="M16 14c2.7 0 4.4 1.4 5 4"/>'),
  menu: ico('<path d="M4 7h16M4 12h16M4 17h16"/>'),
  swap: ico('<path d="M4 8h14l-3.5-3.5M20 16H6l3.5 3.5"/>'),
  bones: ico('<rect x="4" y="5" width="11" height="15" rx="1.5"/><path d="M8 3h10.5A1.5 1.5 0 0 1 20 4.5V17"/><path d="M7.5 15.5l4-6M7.5 9.5l4 6"/>'),
  take: ico('<path d="M8 13V5.5a1.5 1.5 0 0 1 3 0V12M11 11V4.5a1.5 1.5 0 0 1 3 0V12M14 11.5V6a1.5 1.5 0 0 1 3 0v8c0 4-2.6 6.5-6 6.5-2.4 0-4-1.2-5.3-3.2L3.6 14a1.5 1.5 0 0 1 2.5-1.6L8 15"/>'),
  pass: ico('<path d="M4 6l7 6-7 6zM13 6l7 6-7 6z"/>'),
  reveal: ico('<rect x="5" y="4" width="11" height="16" rx="1.5"/><path d="M19 8.5a6 6 0 0 1-1 7.5M17 18l1 2.5 2.5-.5"/>'),
  undo: ico('<path d="M9 7 4 12l5 5"/><path d="M4 12h10a5 5 0 0 1 0 10h-3"/>'),
  end: ico('<path d="M5 12.5 10 17l9-10"/>'),
  trophy: ico('<path d="M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8.5 20h7M10 17h4"/>'),
  list: ico('<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 8h6M9 12h6M9 16h4"/>'),
  chart: ico('<path d="M4 20h16M7 16v-5M12 16V6M17 16v-8"/>'),
};
// Caricatures live in public/assets as <Name>.jpg. Any that fail to load fall back to
// the player's initial (see avatarEl), so a missing file degrades gracefully.
const AVATARS: Record<string, string> = { Botty: "assets/Chris.jpg", Xris: "assets/Chris.jpg" /* Chris's AI likeness */, Steve: "assets/Steve.jpg", Rhett: "assets/Rhett.jpg", Susanne: "assets/Susanne.jpg",
  Susan: "assets/Susanne.jpg" /* games saved before the rename */ };

// State capitals. Dots are placed from real coordinates: the capital's lon/lat is mapped into the
// state's lon/lat bounding box, then onto that state's drawn shape — accurate enough at this scale,
// and the dot is then snapped onto land if it would fall in water. Alaska and Hawaii are drawn as
// scaled insets, so they keep a hand-set position (fx 0=west→1=east, fy 0=north→1=south).
// [capital, lat, lon, state lon min, lon max, lat min, lat max]
type CapGeo = [string, number, number, number, number, number, number];
const CAPITAL_GEO: Record<string, CapGeo> = {
  AL: ["Montgomery", 32.377, -86.3, -88.47, -84.89, 30.22, 35.01], AZ: ["Phoenix", 33.448, -112.074, -114.82, -109.04, 31.33, 37.0],
  AR: ["Little Rock", 34.746, -92.29, -94.62, -89.64, 33.0, 36.5], CA: ["Sacramento", 38.576, -121.494, -124.41, -114.13, 32.53, 42.01],
  CO: ["Denver", 39.739, -104.99, -109.06, -102.04, 36.99, 41.0], CT: ["Hartford", 41.764, -72.682, -73.73, -71.79, 40.98, 42.05],
  DE: ["Dover", 39.158, -75.524, -75.79, -75.05, 38.45, 39.84], FL: ["Tallahassee", 30.438, -84.281, -87.63, -80.03, 24.52, 31.0],
  GA: ["Atlanta", 33.749, -84.388, -85.61, -80.84, 30.36, 35.0], ID: ["Boise", 43.615, -116.202, -117.24, -111.04, 41.99, 49.0],
  IL: ["Springfield", 39.798, -89.654, -91.51, -87.5, 36.97, 42.51], IN: ["Indianapolis", 39.768, -86.158, -88.1, -84.78, 37.77, 41.76],
  IA: ["Des Moines", 41.586, -93.625, -96.64, -90.14, 40.38, 43.5], KS: ["Topeka", 39.048, -95.678, -102.05, -94.59, 36.99, 40.0],
  KY: ["Frankfort", 38.2, -84.873, -89.57, -81.96, 36.5, 39.15], LA: ["Baton Rouge", 30.451, -91.187, -94.04, -88.82, 28.93, 33.02],
  ME: ["Augusta", 44.311, -69.78, -71.08, -66.95, 43.06, 47.46], MD: ["Annapolis", 38.978, -76.492, -79.49, -75.05, 37.91, 39.72],
  MA: ["Boston", 42.36, -71.058, -73.51, -69.93, 41.24, 42.89], MI: ["Lansing", 42.733, -84.555, -90.42, -82.41, 41.7, 48.31],
  MN: ["St. Paul", 44.954, -93.09, -97.24, -89.49, 43.5, 49.38], MS: ["Jackson", 32.299, -90.185, -91.66, -88.1, 30.17, 35.0],
  MO: ["Jefferson City", 38.577, -92.173, -95.77, -89.1, 35.99, 40.61], MT: ["Helena", 46.589, -112.039, -116.05, -104.04, 44.36, 49.0],
  NE: ["Lincoln", 40.814, -96.703, -104.05, -95.31, 40.0, 43.0], NV: ["Carson City", 39.164, -119.767, -120.01, -114.04, 35.0, 42.0],
  NH: ["Concord", 43.207, -71.538, -72.56, -70.7, 42.7, 45.31], NJ: ["Trenton", 40.217, -74.743, -75.56, -73.89, 38.93, 41.36],
  NM: ["Santa Fe", 35.687, -105.938, -109.05, -103.0, 31.33, 37.0], NY: ["Albany", 42.653, -73.757, -79.76, -71.86, 40.5, 45.02],
  NC: ["Raleigh", 35.78, -78.639, -84.32, -75.46, 33.84, 36.59], ND: ["Bismarck", 46.808, -100.784, -104.05, -96.55, 45.94, 49.0],
  OH: ["Columbus", 39.961, -82.999, -84.82, -80.52, 38.4, 41.98], OK: ["Oklahoma City", 35.468, -97.516, -103.0, -94.43, 33.62, 37.0],
  OR: ["Salem", 44.943, -123.035, -124.57, -116.46, 41.99, 46.29], PA: ["Harrisburg", 40.264, -76.884, -80.52, -74.69, 39.72, 42.27],
  RI: ["Providence", 41.824, -71.413, -71.86, -71.12, 41.15, 42.02], SC: ["Columbia", 34.0, -81.035, -83.35, -78.54, 32.03, 35.22],
  SD: ["Pierre", 44.368, -100.351, -104.06, -96.44, 42.48, 45.95], TN: ["Nashville", 36.163, -86.781, -90.31, -81.65, 34.98, 36.68],
  TX: ["Austin", 30.267, -97.743, -106.65, -93.51, 25.84, 36.5], UT: ["Salt Lake City", 40.761, -111.891, -114.05, -109.04, 36.99, 42.0],
  VT: ["Montpelier", 44.26, -72.576, -73.44, -71.46, 42.73, 45.02], VA: ["Richmond", 37.541, -77.436, -83.68, -75.24, 36.54, 39.47],
  WA: ["Olympia", 47.038, -122.901, -124.85, -116.92, 45.54, 49.0], WV: ["Charleston", 38.35, -81.633, -82.64, -77.72, 37.2, 40.64],
  WI: ["Madison", 43.073, -89.401, -92.89, -86.25, 42.49, 47.31], WY: ["Cheyenne", 41.14, -104.82, -111.06, -104.05, 40.99, 45.01],
};
const INSET_CAPITALS: Record<string, { cap: string; fx: number; fy: number }> = {
  AK: { cap: "Juneau", fx: 0.78, fy: 0.62 }, HI: { cap: "Honolulu", fx: 0.62, fy: 0.5 },
};
const STATE_CAPITALS: Record<string, { cap: string; fx: number; fy: number }> = { ...INSET_CAPITALS };
for (const [code, [cap, lat, lon, x0, x1, y0, y1]] of Object.entries(CAPITAL_GEO))
  STATE_CAPITALS[code] = { cap, fx: (lon - x0) / (x1 - x0), fy: (y1 - lat) / (y1 - y0) };
/** "Sacramento, California": the state's real capital city with the state. */
const capitalPlace = (code: string): string => STATE_CAPITALS[code] ? `${STATE_CAPITALS[code].cap}, ${stateName(code)}` : stateName(code);

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const RED = (s: string) => s === "H" || s === "D";
const GLYPH: Record<string, string> = { C: "♣", D: "♦", H: "♥", S: "♠" };
const RLBL: Record<number, string> = { 11: "J", 12: "Q", 13: "K", 14: "A" };
const rankStr = (c: Card) => RLBL[c.rank] ?? String(c.rank);

type Kid = Node | string | null | undefined;
interface HOpts { class?: string; style?: string; html?: string; text?: string; on?: Record<string, (e: any) => void>; attr?: Record<string, string>; }
function h(tag: string, opts: HOpts = {}, kids: Kid[] = []): HTMLElement {
  const e = document.createElement(tag);
  if (opts.class) e.className = opts.class;
  if (opts.style) e.setAttribute("style", opts.style);
  if (opts.html != null) e.innerHTML = opts.html;
  if (opts.text != null) e.textContent = opts.text;
  if (opts.on) for (const k in opts.on) e.addEventListener(k, opts.on[k]);
  if (opts.attr) for (const k in opts.attr) e.setAttribute(k, opts.attr[k]);
  for (const c of kids) if (c != null) e.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  return e;
}
function svgEl(tag: string, attrs: Record<string, string>): SVGElement {
  const e = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const k in attrs) e.setAttribute(k, attrs[k]);
  return e;
}

// ── Lightbox: the lobby sign full size, or a theme's cards up close. × / Esc / backdrop close it.
function openLightbox(content: HTMLElement, label: string): void {
  const lb = $("lightbox"), back = document.activeElement as HTMLElement | null;
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); close(); } };
  const close = () => { lb.classList.add("hidden"); lb.replaceChildren(); document.removeEventListener("keydown", onKey, true); back?.focus(); };
  const x = h("button", { class: "lb-x", text: "×", attr: { type: "button", "aria-label": "Close", title: "Close" }, on: { click: close } });
  lb.setAttribute("aria-label", label);
  lb.onclick = (e) => { if (e.target === lb) close(); };
  lb.replaceChildren(x, content);
  lb.classList.remove("hidden");
  document.addEventListener("keydown", onKey, true);
  x.focus();
}
function openLobbyView(): void {
  const t = currentTheme();
  const src = assetReady(t.lobbyFullImage) ? t.lobbyFullImage! : t.lobbyImage!;
  openLightbox(h("div", { class: "lobby-sign", style: `background-image: url("${src}")`, attr: { role: "img", "aria-label": t.displayName } }), t.displayName);
}
// A theme's whole deck, one suit at a time (all 13 ranks + the back); tapping a card shows it on
// its own, as large as the screen allows. Previewing never changes the selected theme.
function openCardPreview(id: string): void {
  const t = themeById(id)!;
  const SUITS = [["S", "♠ Spades"], ["H", "♥ Hearts"], ["D", "♦ Diamonds"], ["C", "♣ Clubs"]] as const;
  const RANKS = [14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
  const grid = h("div", { class: "lb-cards" });
  const hint = h("div", { class: "lb-hint", text: "Tap a card to see it larger" });
  const tabs = h("div", { class: "lb-tabs", attr: { role: "tablist", "aria-label": "Suit" } });
  const show = (suit: string) => {
    grid.classList.remove("one");
    grid.innerHTML = RANKS.map((rank) => cardMini({ rank, suit } as Card)).join("") + `<div class="lb-back" role="img" aria-label="Card back"></div>`;
    for (const b of tabs.children) b.setAttribute("aria-selected", String((b as HTMLElement).dataset.suit === suit));
    hint.textContent = "Tap a card to see it larger";
  };
  for (const [suit, label] of SUITS) tabs.appendChild(h("button", { class: "lb-tab", text: label, attr: { type: "button", role: "tab", "data-suit": suit }, on: { click: () => show(suit) } }));
  grid.onclick = (e) => {
    const card = (e.target as HTMLElement).closest(".lb-cards > div");
    if (!card) return;
    const one = !grid.classList.contains("one");
    grid.classList.toggle("one", one);
    for (const c of grid.children) c.classList.toggle("on", one && c === card);
    hint.textContent = one ? "Tap the card to see them all" : "Tap a card to see it larger";
  };
  const deck = h("div", { class: "lb-deck" }, [h("h2", { text: `${t.displayName} cards` }), tabs, grid, hint]);
  applyTheme(id, deck);
  show("S");
  openLightbox(deck, `${t.displayName} cards`);
}

interface LogEntry { hand: number; colour: string; who: string; stateName: string; cat: string; posPts: number; hv: number; contig: boolean; total: number; }
interface SaveState { v: 1; players: PlayerConfig[]; method: StateMethod; startHand: number; dealerSeat: number; totals: { id: string; score: number; states: string[] }[]; pool: string[]; history: LogEntry[]; savedAt: number; aiProfiles?: string[];
  /** For recording the finished game: its id, when it started, and every hand played so far. */
  matchKey?: string; startedAt?: number; played?: SubmitHand[];
  /** The account this game belongs to (saved with it on the server, so any device can resume it). */
  owner?: string;
  /** Bob has already grumbled about sticky cards this game (once per game). */
  stickySaid?: boolean;
  /** The shuffler as this hand began: resuming deals this same hand again (games saved before this re-deal). */
  rng?: number;
  /** Every choice made so far in this hand, in order: resuming replays them, so nothing is dealt or decided again. */
  log?: Decision[];
  /** Peeks bought so far (each charged once, never again on resume). */
  peeks?: PeekRecord[]; }
/** One choice in a hand: the seat, what it was asked (a = card move, k = knock?, w = after a swap,
 *  st = state, cap = capital) and its answer. */
interface Decision { s: number; k: "a" | "k" | "w" | "st" | "cap"; v: unknown }
/** Your games in progress on this device, by game (matchKey). Games used to be one at a time (OLD_SAVE_KEY). */
const SAVES_KEY = "poli-saves", OLD_SAVE_KEY = "poli-save";
/** Games in progress one player can have at a time (the server holds the same limit). */
const MAX_GAMES = 5;
const validSave = (s: SaveState | null | undefined): s is SaveState => !!s && s.v === 1 && s.startHand >= 1 && s.startHand <= 50;
function readSaveMap(): Record<string, SaveState> {
  let m: Record<string, SaveState> = {};
  try { m = JSON.parse(localStorage.getItem(SAVES_KEY) || "{}") || {}; } catch { /* unreadable: start empty */ }
  try {
    const old = localStorage.getItem(OLD_SAVE_KEY);
    if (old) {
      const s = JSON.parse(old) as SaveState;
      if (validSave(s)) { s.matchKey ||= newMatchKey(); m[s.matchKey] ??= s; }
      localStorage.setItem(SAVES_KEY, JSON.stringify(m));
      localStorage.removeItem(OLD_SAVE_KEY);
    }
  } catch { /* storage unavailable */ }
  return m;
}
function writeSaveMap(m: Record<string, SaveState>): void { try { localStorage.setItem(SAVES_KEY, JSON.stringify(m)); } catch { /* storage unavailable */ } }

// ── Hand formatting (display-only sorting + plain-language descriptions) ──────
const RANK_SINGULAR: Record<number, string> = { 2: "Two", 3: "Three", 4: "Four", 5: "Five", 6: "Six", 7: "Seven", 8: "Eight", 9: "Nine", 10: "Ten", 11: "Jack", 12: "Queen", 13: "King", 14: "Ace" };
const RANK_PLURAL: Record<number, string> = { 2: "Twos", 3: "Threes", 4: "Fours", 5: "Fives", 6: "Sixes", 7: "Sevens", 8: "Eights", 9: "Nines", 10: "Tens", 11: "Jacks", 12: "Queens", 13: "Kings", 14: "Aces" };
const SUIT_ORD: Record<string, number> = { C: 1, D: 2, H: 3, S: 4 };
const rankCounts = (cards: Card[]) => { const m = new Map<number, number>(); for (const c of cards) m.set(c.rank, (m.get(c.rank) || 0) + 1); return m; };
const isWheel = (cards: Card[]) => cards.map((c) => c.rank).sort((a, b) => a - b).join() === "2,3,4,5,14";
const nBy = (m: Map<number, number>, n: number) => [...m.entries()].filter(([, c]) => c === n).map(([r]) => r).sort((a, b) => b - a);

/** Group cards for display (gap shown between groups). Display-only; recorded order is untouched. */
function sortForDisplay(cards: Card[], category: number): Card[][] {
  const bySuit = (a: Card, b: Card) => SUIT_ORD[a.suit] - SUIT_ORD[b.suit];
  if (category === HandCategory.Straight || category === HandCategory.StraightFlush || category === HandCategory.RoyalFlush) {
    const seq = [...cards].sort((a, b) => a.rank - b.rank);
    if (isWheel(cards)) seq.unshift(seq.pop()!); // A,2,3,4,5
    return [seq];
  }
  if (category === HandCategory.Flush || category === HandCategory.Zipper) return [[...cards].sort((a, b) => b.rank - a.rank || bySuit(a, b))];
  const counts = rankCounts(cards);
  const multi = [...counts.entries()].filter(([, n]) => n >= 2).sort((a, b) => b[1] - a[1] || b[0] - a[0]);
  const singles = [...counts.entries()].filter(([, n]) => n === 1).map(([r]) => r).sort((a, b) => b - a);
  const groups: Card[][] = [];
  for (const [rank] of multi) groups.push(cards.filter((c) => c.rank === rank).sort(bySuit));
  if (singles.length) groups.push(singles.map((r) => cards.find((c) => c.rank === r)!));
  return groups;
}
function handDescriptor(cards: Card[], category: number): string {
  const counts = rankCounts(cards);
  const high = Math.max(...cards.map((c) => c.rank));
  const sHigh = isWheel(cards) ? 5 : high;
  switch (category) {
    case HandCategory.RoyalFlush: return "Royal flush";
    case HandCategory.StraightFlush: return `${RANK_SINGULAR[sHigh]} high`;
    case HandCategory.FourOfAKind: return `Four ${RANK_PLURAL[nBy(counts, 4)[0]].toLowerCase()}`;
    case HandCategory.FullHouse: return `${RANK_PLURAL[nBy(counts, 3)[0]]} over ${RANK_PLURAL[nBy(counts, 2)[0]].toLowerCase()}`;
    case HandCategory.Flush: return `${RANK_SINGULAR[high]}-high flush`;
    case HandCategory.Straight: return `${RANK_SINGULAR[sHigh]} high`;
    case HandCategory.ThreeOfAKind: return `Three ${RANK_PLURAL[nBy(counts, 3)[0]].toLowerCase()}`;
    case HandCategory.TwoPair: { const ps = nBy(counts, 2); return `${RANK_PLURAL[ps[0]]} and ${RANK_PLURAL[ps[1]].toLowerCase()}`; }
    case HandCategory.Pair: return `Pair of ${RANK_PLURAL[nBy(counts, 2)[0]].toLowerCase()}`;
    default: return `${RANK_SINGULAR[high]} high`;
  }
}
function handSubtitle(cards: Card[], category: number): string {
  if (category === HandCategory.FullHouse) {
    const c = rankCounts(cards);
    return `Full house · ${RANK_PLURAL[nBy(c, 3)[0]]} full of ${RANK_PLURAL[nBy(c, 2)[0]].toLowerCase()}`;
  }
  return `${CATEGORY_NAME[category as HandCategory]} · ${handDescriptor(cards, category)}`;
}
function explainWin(win: { cards: Card[]; category: number }, run: { cards: Card[]; category: number }): string {
  if (win.category !== run.category) return `${CATEGORY_NAME[win.category as HandCategory]} beats ${CATEGORY_NAME[run.category as HandCategory].toLowerCase()}.`;
  const wC = rankCounts(win.cards), rC = rankCounts(run.cards);
  const wH = Math.max(...win.cards.map((c) => c.rank)), rH = Math.max(...run.cards.map((c) => c.rank));
  switch (win.category) {
    case HandCategory.FullHouse: return `Three ${RANK_PLURAL[nBy(wC, 3)[0]].toLowerCase()} beat three ${RANK_PLURAL[nBy(rC, 3)[0]].toLowerCase()}. Compare the triple first.`;
    case HandCategory.FourOfAKind: return `Four ${RANK_PLURAL[nBy(wC, 4)[0]].toLowerCase()} beat four ${RANK_PLURAL[nBy(rC, 4)[0]].toLowerCase()}.`;
    case HandCategory.ThreeOfAKind: return `Three ${RANK_PLURAL[nBy(wC, 3)[0]].toLowerCase()} beat three ${RANK_PLURAL[nBy(rC, 3)[0]].toLowerCase()}. Compare the matching cards first.`;
    case HandCategory.TwoPair: { const wp = nBy(wC, 2), rp = nBy(rC, 2); return wp[0] !== rp[0] ? `${RANK_PLURAL[wp[0]]} beat ${RANK_PLURAL[rp[0]].toLowerCase()} on the top pair.` : `Same top pair — the second pair decides.`; }
    case HandCategory.Pair: { const wp = nBy(wC, 2)[0], rp = nBy(rC, 2)[0]; return wp !== rp ? `Pair of ${RANK_PLURAL[wp].toLowerCase()} beats pair of ${RANK_PLURAL[rp].toLowerCase()}.` : `Same pair — the high kicker decides.`; }
    case HandCategory.Straight: case HandCategory.StraightFlush: { const wh = isWheel(win.cards) ? 5 : wH, rh = isWheel(run.cards) ? 5 : rH; return `${RANK_SINGULAR[wh]}-high straight beats ${RANK_SINGULAR[rh]} high.`; }
    case HandCategory.Flush: return `${RANK_SINGULAR[wH]}-high flush beats ${RANK_SINGULAR[rH]} high.`;
    default: return `${RANK_SINGULAR[wH]} high beats ${RANK_SINGULAR[rH]} high.`;
  }
}
const cardMini = (c: Card) => `<div class="sd2-card cface${RED(c.suit) ? " red" : ""}${c.rank === 14 ? " ace" : ""}">${faceHtml(c)}</div>`;

// ── Your avatar ─────────────────────────────────────────────────────────────
// Built on the setup screen; drawn as an SVG face, and its skin tone colours the hands holding
// your cards. Saved in localStorage.
interface AvatarCfg { look: "man" | "woman"; skin: number; hair: string; hairCol: number; facial: string; extra: string; photo?: string }
const SKIN_TONES = [
  { base: "#f6d6c0", shade: "#d9a98c", nail: "#fbe7dc" }, { base: "#ebbd9b", shade: "#c99573", nail: "#f6dccb" },
  { base: "#d39a72", shade: "#ad7550", nail: "#ecc6ad" }, { base: "#ae744c", shade: "#8a5633", nail: "#d8ab8e" },
  { base: "#83532f", shade: "#633c1f", nail: "#bf9479" }, { base: "#58361f", shade: "#3f2513", nail: "#9e7a64" },
];
const HAIR_COLOURS = ["#1e1915", "#4a2f1d", "#7a4a2a", "#8e3b1f", "#d6b066", "#b9b4ae", "#eceae4"];
const HAIR_STYLES = [["short", "Short"], ["swept", "Swept"], ["long", "Long"], ["curly", "Curly"], ["bun", "Bun"], ["bald", "Bald"]] as const;
const FACIAL = [["none", "None"], ["moustache", "Moustache"], ["handlebar", "Handlebar"], ["beard", "Beard"]] as const;
const EXTRAS = [["none", "None"], ["glasses", "Glasses"], ["hat", "Cowboy hat"], ["hatglasses", "Hat + glasses"]] as const;
const AVATAR_KEY = "poli-avatar";
const LOOKS = [["man", "Man"], ["woman", "Woman"]] as const;
const DEFAULT_AVATAR: AvatarCfg = { look: "man", skin: 1, hair: "short", hairCol: 1, facial: "none", extra: "none" };
function loadAvatar(): AvatarCfg {
  try { const v = JSON.parse(localStorage.getItem(AVATAR_KEY) || "null"); if (v && typeof v.skin === "number") return { ...DEFAULT_AVATAR, ...v }; } catch { /* ignore */ }
  return { ...DEFAULT_AVATAR };
}
function saveAvatar(a: AvatarCfg): void { try { localStorage.setItem(AVATAR_KEY, JSON.stringify(a)); } catch { /* ignore */ } }
/** Someone else's drawn avatar from their saved profile (JSON), or "" if it can't be read. */
function drawnAvatar(cfgJson: string, shirt: string): string {
  try { const a = JSON.parse(cfgJson) as AvatarCfg; return a && typeof a === "object" && a.look ? avatarSvg(a, shirt) : ""; } catch { return ""; }
}
setAvatarDrawer(drawnAvatar);
function avatarSvg(a: AvatarCfg, shirt: string): string {
  const t = SKIN_TONES[a.skin] ?? SKIN_TONES[1], hc = HAIR_COLOURS[a.hairCol] ?? HAIR_COLOURS[1];
  const hatOn = a.extra === "hat" || a.extra === "hatglasses", glasses = a.extra === "glasses" || a.extra === "hatglasses";
  const woman = a.look === "woman";
  const back = a.hair === "long" ? `<path fill="${hc}" d="M26 44 C 24 14, 76 14, 74 44 L 77 78 C 70 74, 68 62, 68 50 L 32 50 C 32 62, 30 74, 23 78 Z"/>`
    : a.hair === "curly" ? `<g fill="${hc}"><circle cx="50" cy="36" r="27"/><circle cx="27" cy="46" r="10"/><circle cx="73" cy="46" r="10"/><circle cx="34" cy="20" r="10"/><circle cx="66" cy="20" r="10"/><circle cx="50" cy="13" r="11"/></g>`
    : a.hair === "bun" ? `<circle fill="${hc}" cx="50" cy="13" r="10"/>` : "";
  const top = a.hair === "short" ? `<path fill="${hc}" d="M27 44 C 26 16, 74 16, 73 44 C 68 31, 32 31, 27 44 Z"/>`
    : a.hair === "swept" ? `<path fill="${hc}" d="M27 46 C 24 16, 70 10, 74 42 C 66 26, 52 30, 41 25 C 35 30, 30 37, 27 46 Z"/>`
    : a.hair === "long" || a.hair === "bun" ? `<path fill="${hc}" d="M27 44 C 26 16, 74 16, 73 44 C 66 32, 50 28, 44 30 C 38 32, 31 36, 27 44 Z"/>`
    : a.hair === "curly" ? `<path fill="${hc}" d="M28 40 C 30 22, 70 22, 72 40 C 64 32, 36 32, 28 40 Z"/>`
    : `<path fill="${hc}" opacity=".55" d="M26 46 C 25 40, 27 36, 29 34 L 29 46 Z M74 46 C 75 40, 73 36, 71 34 L 71 46 Z"/>`;
  const facial = a.facial === "beard" ? `<path fill="${hc}" d="M29 50 C 29 80, 71 80, 71 50 C 68 64, 60 68, 50 68 C 40 68, 32 64, 29 50 Z"/><path fill="${hc}" d="M40 60 C 44 56, 56 56, 60 60 C 56 62, 44 62, 40 60 Z"/>`
    : a.facial === "moustache" ? `<path fill="${hc}" d="M38 61 C 42 55, 48 57, 50 59 C 52 57, 58 55, 62 61 C 57 60, 53 62, 50 61 C 47 62, 43 60, 38 61 Z"/>`
    // handlebar: thick across the top lip, with bars running straight down both sides of the mouth to the jaw
    : a.facial === "handlebar" ? `<path fill="${hc}" d="M50 56.5 C 45 55.5, 39 56, 36 59 L 35.6 70.5 C 35.9 72, 39.6 72, 40.3 70.5 L 40.7 62.2 C 44 61.2, 47 60.8, 50 61.2 C 53 60.8, 56 61.2, 59.3 62.2 L 59.7 70.5 C 60.4 72, 64.1 72, 64.4 70.5 L 64 59 C 61 56, 55 55.5, 50 56.5 Z"/>` : "";
  const hat = hatOn ? `<g fill="#5b3a1e"><ellipse cx="50" cy="31" rx="38" ry="7"/><path d="M30 31 C 31 12, 40 8, 45 13 C 48 15, 52 15, 55 13 C 60 8, 69 12, 70 31 Z"/></g><path d="M31 27 L 69 27" stroke="#2f1d0e" stroke-width="3"/>` : "";
  const specs = glasses ? `<g fill="none" stroke="#2a2420" stroke-width="2"><circle cx="41" cy="48" r="6.5"/><circle cx="59" cy="48" r="6.5"/><path d="M47.5 48 L 52.5 48"/></g>` : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50" fill="#332e2a"/>` +
    (woman ? `<path fill="${shirt}" d="M14 100 C 16 82, 30 76, 40 75 C 44 82, 56 82, 60 75 C 70 76, 84 82, 86 100 Z"/>`
           : `<path fill="${shirt}" d="M14 100 C 16 80, 32 74, 50 74 C 68 74, 84 80, 86 100 Z"/>`) + back +
    `<rect x="43" y="62" width="14" height="${woman ? 21 : 14}" rx="5" fill="${t.shade}"/>` +
    `<ellipse cx="27" cy="50" rx="4" ry="6" fill="${t.shade}"/><ellipse cx="73" cy="50" rx="4" ry="6" fill="${t.shade}"/>` +
    (woman ? `<path fill="${t.base}" d="M28 45 C 28 28, 38 22, 50 22 C 62 22, 72 28, 72 45 C 72 60, 62 71, 50 71 C 38 71, 28 60, 28 45 Z"/>`
           : `<ellipse cx="50" cy="47" rx="22" ry="25" fill="${t.base}"/>`) + top +
    (woman ? `<path d="M36 41.5 Q 40.5 38.5, 45 40.5 M55 40.5 Q 59.5 38.5, 64 41.5" stroke="${hc}" stroke-width="1.6" fill="none" stroke-linecap="round"/>` +
             `<path d="M37.5 46.5 l -1.6 -1.4 M39.2 45.6 l -1 -1.6 M62.5 46.5 l 1.6 -1.4 M60.8 45.6 l 1 -1.6" stroke="#1f1a17" stroke-width="1" stroke-linecap="round"/>` +
             `<ellipse cx="36" cy="56" rx="4" ry="2.4" fill="#e0707a" opacity=".22"/><ellipse cx="64" cy="56" rx="4" ry="2.4" fill="#e0707a" opacity=".22"/>`
           : `<path d="M36 41 L 45 40 M55 40 L 64 41" stroke="${hc}" stroke-width="2.4" stroke-linecap="round"/>`) +
    `<circle cx="41" cy="48" r="2.4" fill="#1f1a17"/><circle cx="59" cy="48" r="2.4" fill="#1f1a17"/>` +
    `<path d="M50 49 C 48 54, 47 56, 51 57" fill="none" stroke="${t.shade}" stroke-width="1.8" stroke-linecap="round"/>` +
    facial + (woman
      ? `<path d="M44 63 C 46.5 61.6, 48.5 62.2, 50 62.8 C 51.5 62.2, 53.5 61.6, 56 63 C 53.5 66.2, 46.5 66.2, 44 63 Z" fill="#b5434f"/>`
      : `<path d="M43 63 C 47 66, 53 66, 57 63" fill="none" stroke="#7a3b2c" stroke-width="2" stroke-linecap="round"/>`) +
    specs + hat + `</svg>`;
}
// Your hand is shown as a photo: real hands (cut out of Scott's "Royal Flush Card Fan" image) laid
// over the game's own cards, which sit exactly where the photo's cards were. Slots are the cards'
// top-left corners in the 1541×1020 photo plus each card's tilt (degrees), back card first.
const PHOTO_FAN = { w: 1541, h: 1020, cardW: 531 };
const PHOTO_SLOTS: [number, number, number][] = [[169, 245, -25.1], [345, 160, -12], [508, 112, -2.5], [660, 85, 7], [826, 54, 16.5]];
// The photo's skin sits at tone 2; other tones are a filter on the photo.
const HAND_TONE_FILTER = [
  "brightness(1.16) saturate(.72)", "brightness(1.07) saturate(.86)", "none",
  "brightness(.84) saturate(1.05)", "brightness(.62) saturate(1.1) contrast(1.05)", "brightness(.45) saturate(1.05) contrast(1.1)",
];
// Text colour for a player's colour chip. White reads best on the saturated mid-tones players pick
// (blue, teal, pink, orange) even where pure WCAG maths says black, so black is kept for genuinely
// light colours only (yellow, pastels, white). White text also gets a faint shadow — see .pt.ink-light.
function inkOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#fff";
  const n = parseInt(m[1], 16);
  const lin = (c: number) => { c /= 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin(n >> 16) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return L > 0.45 ? "#111" : "#fff";
}
const handsImg = (tone: number) =>
  h("img", { class: "hands-photo", attr: { src: "assets/hands/hands-photo.webp", alt: "" }, style: `filter:${HAND_TONE_FILTER[tone] ?? "none"}` });


// ── Controllers ──────────────────────────────────────────────────────────────
/**
 * An AI seat at the table, paced so moves are watchable. Plays the smart AI (engine/ai/) by default;
 * `localStorage["poli-ai"] = "baseline"` switches back to the original heuristic AI (rollback).
 * Thinking time counts towards the pause, so a slow decision never adds to it.
 */
class PacedAI implements Controller {
  private ai: Controller;
  constructor(seat: number, seed: number, profile: Profile, isCurrent: () => boolean) {
    let useBaseline = false;
    try { useBaseline = localStorage.getItem("poli-ai") === "baseline"; } catch { /* storage unavailable */ }
    this.ai = useBaseline ? new AIController(seed) : new SmartAIController({ seat, seed, profile, budget: BUDGETS.live, isCurrent });
  }
  async decideAction(v: TurnView): Promise<Action> {
    const t0 = performance.now(); const a = await this.ai.decideAction(v);
    await sleep(Math.max(0, AI_DELAY - (performance.now() - t0))); return a;
  }
  async decideKnock(v: TurnView): Promise<boolean> {
    const t0 = performance.now(); const k = (await this.ai.decideKnock?.(v)) ?? false;
    await sleep(Math.max(0, AI_DELAY / 2 - (performance.now() - t0))); return k;
  }
  chooseState(u: string[], o: string[], v: { handNumber: number }) { return this.ai.chooseState!(u, o, v); }
  onObserve(e: Parameters<NonNullable<Controller["onObserve"]>>[0]) { this.ai.onObserve?.(e); }
  onHandEnd(r: Parameters<NonNullable<Controller["onHandEnd"]>>[0]) { this.ai.onHandEnd?.(r); }
  onDeal(h: Card[], n: number) { return this.ai.onDeal?.(h, n); }
}

class HumanController implements Controller {
  view?: TurnView;
  private rAction?: (a: Action) => void;
  private rKnock?: (b: boolean) => void;
  private rState?: (s: string) => void;
  constructor(private app: App) {}
  decideAction(v: TurnView): Promise<Action> { this.view = v; this.app.beginYourTurn(v); return new Promise((r) => (this.rAction = r)); }
  submit(a: Action): void { const r = this.rAction; this.rAction = undefined; this.app.closeTurn(); r?.(a); }
  decideKnock(v: TurnView): Promise<boolean> { this.view = v; return new Promise((r) => { this.rKnock = r; this.app.askKnock(v); }); }
  submitKnock(b: boolean): void { const r = this.rKnock; this.rKnock = undefined; this.app.closeTurn(); r?.(b); }
  private rAfterSwap?: (c: AfterSwap) => void;
  decideAfterSwap(v: TurnView, opts: { canUndo: boolean; canKnock: boolean }): Promise<AfterSwap> {
    this.view = v; return new Promise((r) => { this.rAfterSwap = r; this.app.askAfterSwap(v, opts); });
  }
  submitAfterSwap(c: AfterSwap): void { const r = this.rAfterSwap; this.rAfterSwap = undefined; this.app.closeTurn(); r?.(c); }
  chooseState(u: string[]): Promise<string> { this.app.enterMapSelect(u); return new Promise((r) => (this.rState = r)); }
  submitState(c: string): void { const r = this.rState; this.rState = undefined; r?.(c); }
  private rCapital?: (s: string) => void;
  /** You won the game: name your capital (one of your own states) on the final map. */
  chooseCapital(owned: string[]): Promise<string> { return new Promise((r) => { this.rCapital = r; this.app.enterCapitalSelect(owned); }); }
  submitCapital(c: string): void { const r = this.rCapital; this.rCapital = undefined; r?.(c); }
  onDeal(hand: Card[], handNumber: number): Promise<void> { return this.app.receiveDeal(hand, handNumber); }
  onOwnHand(hand: Card[]): void { this.app.receiveOwnHand(hand); }
}

/**
 * Plays a multiplayer game on this screen. The table (server) runs the game; this turns what it sends
 * into the same calls the local engine makes — so every animation, the results page and the map work
 * as in a solo game — and sends your choices back as moves. Seats are turned so that you are seat 0
 * (bottom of the screen) and your player id reads "you", as the screen expects.
 */
/** Phones held sideways (and other short landscape screens) get the compact game screen. */
const COMPACT = matchMedia("(orientation: landscape) and (max-height: 520px)");
// Inside the Android app (Capacitor) the page draws edge to edge; the compact layout keeps clear of the
// camera cut-out with safe-area insets. (Only in the app: in a browser this would tuck content under notches.)
if ((window as unknown as { Capacitor?: unknown }).Capacitor) {
  document.querySelector('meta[name="viewport"]')?.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
  document.documentElement.classList.add("in-app");
}

/** The Android app's native side (Capacitor's App plugin), when running inside the app. */
function capApp(): { exitApp(): Promise<void>; addListener(ev: string, cb: (e: unknown) => void): Promise<unknown> } | undefined {
  return (window as unknown as { Capacitor?: { Plugins?: Record<string, never> } }).Capacitor?.Plugins?.App;
}

class RemoteGame {
  private seat = 0;
  private myId = "";
  private started = false;
  private queue: Promise<void> = Promise.resolve();
  private latest?: Extract<ServerMessage, { type: "game" }>;
  private prompting = "";
  private contTimer = 0;
  private chipTimer = 0;
  constructor(private app: App, private human: HumanController) {}

  receive(m: Extract<ServerMessage, { type: "game" }>, myPlayerId: string): void {
    this.latest = m;
    this.seat = m.seat; this.myId = myPlayerId;
    this.queue = this.queue.then(() => this.process(m)).catch((e) => console.error(e));
  }
  /** A move was refused (or arrived late): ask again from the newest view. */
  refused(error: string): void {
    if (this.app.onPeekRefused(error)) return;
    this.app.showToast("That move wasn't accepted", error);
    this.prompting = "";
    if (this.latest) { const m = this.latest; this.queue = this.queue.then(() => this.prompt(m)); }
  }

  private rot = (s: number) => (s < 0 ? s : (s - this.seat + 5) % 5);
  /** Server data → screen data: seats turned so you're 0, your id → "you", seat-indexed arrays turned. */
  private tr<T>(x: T): T {
    const s = JSON.parse(JSON.stringify(x).split(`"${this.myId}"`).join('"you"'));
    const turnArr = <U>(a: U[]) => a.map((_, i) => a[(i + this.seat) % 5]);
    const fix = (o: any): any => {
      if (!o || typeof o !== "object") return o;
      if (Array.isArray(o)) return o.map(fix);
      for (const k of Object.keys(o)) {
        const v = o[k];
        if (["seat", "dealerSeat", "currentSeat", "winnerSeat", "knockerSeat"].includes(k) && typeof v === "number") o[k] = this.rot(v);
        else if ((k === "turnsTaken" || k === "totals") && Array.isArray(v) && v.length === 5) o[k] = k === "totals" ? turnArr(v).map(fix) : turnArr(v);
        else if (k === "players" && Array.isArray(v)) { if (v.length !== 5) throw new Error("a table always has five seats"); o[k] = turnArr(v).map(fix); }
        else o[k] = fix(v);
      }
      return o;
    };
    return fix(s);
  }

  private async process(m: Extract<ServerMessage, { type: "game" }>): Promise<void> {
    if (!this.started) {
      this.started = true;
      // turned like every other seat-indexed list, so the names line up with the seats (and you're seat 0)
      const players = this.tr({ players: m.view.players }).players;
      this.app.startRemote(players, m.view.table?.stateMethod ?? "CHOOSE");
      // announcements come from the table, so everyone hears them together: "sticky" only on the table's
      // chosen hand — and not at all for someone who rejoins after its deal
      const st = m.stickyHand ?? null, dealtNow = m.events.some((e) => e.type === "handStart" && e.board.handNumber === st);
      this.app.setStickyHand(st !== null && (m.view.handNumber < st || dealtNow) ? st : Infinity);
      // rejoining part-way: who won which hand so far (for the map's droughts)
      for (const r of this.tr(m.view.history ?? [])) this.app.noteWin(r.winnerId, r.handNumber);
      if (m.full && m.view.board) {
        // joining (or rejoining) part-way: show the hand in play as it stands
        await this.app.onHandStart(this.tr({ ...m.view.board, remainingPool: [] }) as BoardSnapshot);
        if (m.view.hand.length) await this.human.onDeal(m.view.hand, m.view.handNumber);
      }
    }
    for (const raw of m.events) {
      this.app.hurry = this.latest !== m;          // newer moves are already waiting: catch up
      const e = this.tr(raw);
      switch (e.type) {
        case "handStart": await this.app.onHandStart(e.board); break;
        case "deal": await this.human.onDeal(e.hand, e.handNumber); break;
        case "ownHand": this.human.onOwnHand(e.hand); break;
        case "action": await this.app.onAction({ seat: e.seat, action: e.action, board: e.board }); break;
        case "handReveal": await this.app.onHandReveal({ record: e.record, board: e.board }); break;
        case "handEnd": await this.app.onShowdown({ record: e.record, board: e.board }); break;
        case "stateWon":
          await this.app.onStateWon({ seat: e.seat, state: e.state, board: e.board });
          sendToTable({ type: "continue", hand: e.board.handNumber });   // you've seen this hand's results
          break;
        case "peek": this.app.onPeek(e.peek); break;
        case "scores": this.app.onScores(e); break;
        case "president": await this.app.onPresident(e); break;
        case "gameEnd": this.app.onGameEnd(e.standings); break;
      }
    }
    this.app.setRemotePeeks(m.view.peek, this.tr({ peeks: m.view.peeks }).peeks);
    // the chip and the flashing box both say whose move the board is now waiting for
    this.chip(m);
    const ph = m.view.phase;
    if ((ph.kind === "turn" || ph.kind === "afterSwap" || ph.kind === "afterExchange") && ph.seat !== m.seat) this.app.showTurn(this.rot(ph.seat));
    if (m !== this.latest) return;
    await this.prompt(m);
  }

  /** If the table is waiting for YOUR move, ask for it on screen and send it. */
  private async prompt(m: Extract<ServerMessage, { type: "game" }>): Promise<void> {
    const ph = m.view.phase;
    // someone else is President and naming a capital: everyone watches on the map
    if (ph.kind === "capital" && ph.seat !== m.seat) this.app.watchCapital(this.rot(ph.seat), [...(m.view.totals[ph.seat]?.states ?? [])]);
    if (!("seat" in ph) || ph.seat !== m.seat || !m.view.board || !m.view.table) return;
    const key = `${m.v}:${ph.kind}`;
    if (this.prompting === key) return;
    this.prompting = key;
    const b = m.view.board, t = this.tr(m.view.table);
    const view: TurnView = { handNumber: m.view.handNumber, seat: 0, hand: m.view.hand, middle: b.middle, middleRevealed: b.middleRevealed,
      stockCount: b.stockCount, discardCount: b.discardCount, someoneKnocked: b.someoneKnocked, turnsTaken: t.turnsTaken[0],
      targetState: b.targetState, legal: m.view.legal, table: t };
    if (ph.kind === "turn") sendToTable({ type: "move", move: { kind: "action", action: await this.human.decideAction(view) } });
    else if (ph.kind === "afterSwap") sendToTable({ type: "move", move: { kind: "afterSwap", choice: await this.human.decideAfterSwap(view, { canUndo: true, canKnock: ph.canKnock }) } });
    else if (ph.kind === "afterExchange") sendToTable({ type: "move", move: { kind: "knock", knock: await this.human.decideKnock(view) } });
    else if (ph.kind === "chooseState") sendToTable({ type: "move", move: { kind: "state", state: await this.human.chooseState(t.unclaimed) } });
    else if (ph.kind === "capital") sendToTable({ type: "move", move: { kind: "capital", state: await this.human.chooseCapital([...(m.view.totals[m.seat]?.states ?? [])]) } });
  }

  /** The status chip: connection, whose move it is, the results countdown, a paused table. */
  private chip(m: Extract<ServerMessage, { type: "game" }>): void {
    clearTimeout(this.contTimer); clearInterval(this.chipTimer);
    const el = $("mp-chip"), ph = m.view.phase;
    const names = m.view.players.map((p) => p.name.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`));   // shown as HTML
    const draw = () => {
      let txt = "";
      if (m.paused) txt = "Table paused — everyone else is away";
      else if (m.results) {
        const secs = Math.max(0, Math.round((m.results.deadline - Date.now()) / 1000));
        txt = m.results.waiting.length ? `Next hand in ${secs}s · waiting for ${m.results.waiting.join(", ")}` : "Dealing the next hand…";
      } else if ("seat" in ph) {
        const who = ph.seat === m.seat ? "Your" : `${names[ph.seat]}'s`;
        txt = ph.kind === "chooseState" ? (ph.seat === m.seat ? "You're choosing a state" : `${names[ph.seat]} is choosing a state`)
          : ph.kind === "capital" ? (ph.seat === m.seat ? "You're President: choose your capital" : `President ${names[ph.seat]} is choosing a capital`) : `${who} turn`;
      } else if (ph.kind === "gameOver") txt = "Game over";
      // people who've dropped out: a countdown to the AI covering them, then who it's covering
      const away = m.paused ? [] : (m.away ?? []).filter((a) => a.seat !== m.seat);
      const waiting = away.filter((a) => a.coverAt > Date.now()), covered = away.filter((a) => a.coverAt <= Date.now());
      const list = (xs: { seat: number }[]) => xs.map((a) => names[a.seat]).join(", ");
      let note = "";
      if (covered.length) note += `<span class="mp-away">AI is playing for ${list(covered)} until they're back</span>`;
      for (const a of waiting) note += `<span class="mp-away">${names[a.seat]} dropped out · AI takes over in ${Math.max(0, Math.ceil((a.coverAt - Date.now()) / 1000))}s</span>`;
      el.innerHTML = `<span class="mp-dot"></span><span>${txt}</span>${note}<a href="/" title="Your seat stays yours — rejoin from Play with friends">Leave screen</a>`;
    };
    el.hidden = false; draw();
    if (m.results || (m.away ?? []).some((a) => a.coverAt > Date.now())) this.chipTimer = window.setInterval(draw, 1000);
    if (m.results) {
      // the table deals on at the deadline: close the results here too
      this.contTimer = window.setTimeout(() => this.app.forceContinue(), Math.max(0, m.results.deadline - Date.now()));
    }
  }
}

type Mode = "idle" | "knock" | "map-select";

// ── App ──────────────────────────────────────────────────────────────────────
class App implements GameObserver {
  readonly human = new HumanController(this);
  /** Bumped for every new or resumed game, so AI thinking for an abandoned game stops. */
  private gameToken = 0;
  /** Each seat's AI, fixed for the whole match (saved with the game): a persona id (a real player's
   *  likeness, personas.ts) — or, in games saved before personas, a personality name. */
  private aiProfiles: string[] = [];
  private makeAIs(seed: number): Controller[] {
    const token = ++this.gameToken;
    if (this.aiProfiles.length !== 5) { const order = makeRng(seed).shuffle([...PROFILE_ORDER]); this.aiProfiles = ["observant", ...order]; }
    const profileOf = (x: string): Profile => { const pp = persona(x); return pp ? personaProfile(pp) : PROFILES[x as ProfileName] ?? PROFILES.observant; };
    return this.players.map((p, i) => (p.isHuman ? this.human : new PacedAI(i, (seed + i * 97) >>> 0, profileOf(this.aiProfiles[i]), () => token === this.gameToken)));
  }
  players: PlayerConfig[] = [];
  private colour = COLOURS[0];
  private method: StateMethod = "CHOOSE";
  /** A multiplayer game: the table runs it, so nothing is saved or recorded from this browser. */
  remote = false;
  /** Multiplayer: this screen is behind the table, so moves show with short pauses until it catches up. */
  hurry = false;
  layout: "a" | "b" | "c" = "c";   // Band is the default view
  private sheetTab: "standings" = "standings";

  private board?: BoardSnapshot;
  private yourHand: Card[] = [];
  private activeSeat = 0;
  private mode: Mode = "idle";
  private giveIndex = -1;
  /** Your turn is waiting for a move (or the knock question) — drags and the prompt text are live. */
  turnOpen = false;
  private status = "";
  private handName = "—";
  private actions: { label: string; primary?: boolean; side?: "l" | "r"; onClick: () => void }[] = [];   // side "r" (Knock) = right end of the rail, the rest at the left
  private selectable = new Set<string>();
  private history: LogEntry[] = [];
  private focusCode: string | null = null;
  private mapOpen = false;
  private claimMode = false;
  private pendingPick: string | null = null;   // state clicked while claiming, awaiting Yes/No
  private targetState?: string;
  private pendingCat = 0;
  private pendingWinnerCards = "";
  private stateInfo = new Map<string, { who: string; party: string; colour: string; cat: string; cards: string; points: number; fact: ResolvedFact; hand: number; quock?: string }>();
  /** Who quocked the hand being shown (knocked on their first turn), for the map. */
  private pendingQuock?: string;
  private browse = new Map<string, ResolvedFact>();

  private modalPaths = new Map<string, SVGPathElement>();
  private miniPaths = new Map<string, SVGPathElement>();
  private mapZoom = 1;
  private mapPanX = 0;
  private mapPanY = 0;
  private suppressClick = false;
  private gameActive = false;
  private pendingFact: ResolvedFact | null = null;
  private handOrder: number[] = [];        // display order → engine index (lets you rearrange your cards)
  private freshDeal = false;               // true for the one render where your new hand deals in
  private dealtYouHand = 0;                 // hand number your cards last dealt in for
  private dragState: { di: number; startX: number; startY: number; el: HTMLElement; row: HTMLElement; moved: boolean; pid: number; swap: boolean; bones: boolean } | null = null;
  private claimReview = false;             // you claimed a state — stay on the map with review actions
  private rClaimContinue?: () => void;      // resolves when you press "Continue to next hand"
  private lastRecord?: HandRecord;          // last showdown, so the map can re-open the results
  private records: HandRecord[] = [];       // every hand's record this game, for the history browser
  /** The hand each player last won a state (by player id), and the latest hand a state was won — for the
   *  map's "Drought": hands since a player last won a state. */
  private lastWin = new Map<string, number>();
  private lastClaim = 0;
  noteWin(playerId: string, hand: number): void {
    if (hand > (this.lastWin.get(playerId) ?? 0)) this.lastWin.set(playerId, hand);
    this.lastClaim = Math.max(this.lastClaim, hand);
  }
  /** Hands since this player last won a state (since the start of the game if they haven't yet). */
  private drought(playerId: string): number { return this.lastClaim - (this.lastWin.get(playerId) ?? 0); }
  /** This game's hands as they're sent to be recorded (kept in the save, so a resumed game has them all). */
  private played: SubmitHand[] = [];
  private matchKey = "";
  private startedAt = 0;
  private totalsAfter = new Map<number, number[]>();   // hand number → each seat's game total after that hand
  private handsPlayed = 0;
  private stats = new Map<string, { name: string; colour: string; party: string; wins: number; knocks: number; knockWins: number; knockR: [number, number, number]; contigs: number; cats: number[] }>();

  // ---- save / resume ----
  isActive(): boolean { return this.gameActive; }
  /** The signed-in player's games in progress, most recently played first (games saved here for someone else aren't offered). */
  mySaves(): SaveState[] {
    const me = myPlayerId();
    if (!me) return [];
    return Object.values(readSaveMap()).filter((x) => validSave(x) && (!x.owner || x.owner === me)).sort((x, y) => y.savedAt - x.savedAt);
  }
  /**
   * Signed in: bring this device's games in step with the account's. For each game the copy that has
   * played more hands wins; games ended elsewhere are dropped here; games only this device has are sent up.
   */
  async syncSave(): Promise<void> {
    const me = myPlayerId();
    if (!me || this.gameActive) return;
    const cloud = await cloudList();
    if (!cloud || this.gameActive || myPlayerId() !== me) return;
    const m = readSaveMap();
    for (const k of cloud.ended) delete m[k];
    for (const c of cloud.saves) {
      const l = m[c.matchKey];
      if (!l || l.startHand < c.hand) try { m[c.matchKey] = JSON.parse(c.state) as SaveState; } catch { /* skip a broken copy */ }
    }
    writeSaveMap(m);
    const known = new Set([...cloud.saves.map((x) => x.matchKey), ...cloud.ended]);
    for (const x of Object.values(m)) {
      if (!validSave(x) || (x.owner && x.owner !== me) || !x.matchKey || known.has(x.matchKey) || x.played?.length !== x.startHand - 1) continue;
      x.owner = me; m[x.matchKey] = x; writeSaveMap(m);
      await cloudSave(x.matchKey, x.startHand, JSON.stringify(x));
    }
  }
  /** The running solo game (for its shuffler state and peeks). */
  private game?: PoliGame;
  /** Choices still to replay after a resume (this hand's, in order), and the choices made so far this hand. */
  private replay: Decision[] = [];
  private handLog: Decision[] = [];
  /** Resuming: the hand is being played back to where it was saved (no animations, no prompts). */
  get replaying(): boolean { return this.replay.length > 0; }
  /** Where this hand began (what a save needs besides the choices made since). */
  private snap: Pick<SaveState, "startHand" | "dealerSeat" | "totals" | "pool" | "rng" | "history" | "played"> | null = null;
  private saveSnapshot(b: BoardSnapshot): void {
    if (this.remote) return;
    this.snap = { startHand: b.handNumber, dealerSeat: b.dealerSeat, totals: b.players.map((p) => ({ id: p.id, score: p.score, states: [...p.states] })),
      pool: b.remainingPool, rng: this.game?.handStartRng, history: [...this.history], played: [...this.played] };
    void this.saveProgress(true);
  }
  /** The game as it stands: where this hand began, every choice since, and the peeks bought. */
  private saveState(): SaveState | null {
    if (this.remote || !this.snap) return null;
    return { v: 1, players: this.players, method: this.method, ...this.snap, savedAt: Date.now(), aiProfiles: this.aiProfiles,
      matchKey: this.matchKey, startedAt: this.startedAt, owner: myPlayerId() ?? undefined,
      stickySaid: this.nextStickyHand === Infinity, log: [...this.handLog], peeks: (this.game?.peeks ?? []).map(({ charged: _, ...p }) => p) };
  }
  /**
   * Save the game: on this device after every choice; with your account too when `cloud` (each new hand,
   * Save & exit, leaving the app). Resolves "saved" only once the account has it (or there's no account).
   */
  async saveProgress(cloud = false): Promise<"saved" | "local" | "failed"> {
    const save = this.saveState();
    if (!save || this.replaying) return "local";
    try { const m = readSaveMap(); m[this.matchKey] = save; writeSaveMap(m); } catch { return "failed"; }
    if (!cloud || !save.owner) return save.owner ? "local" : "saved";
    const r = await cloudSave(this.matchKey, save.startHand, JSON.stringify(save));
    if (r === "stale") this.showToast("Game moved on elsewhere", "You've played this game further on another device. Reload to carry on from there.");
    else if (r === "gone") this.showToast("Game ended elsewhere", "This game was ended on another device, so it's no longer saved with your account.");
    else if (r === "full") this.showToast("Saved on this device only", `You have ${MAX_GAMES} games in progress on your account. End one to save this game with your account too.`);
    return r === "saved" ? "saved" : "failed";
  }
  /** Every seat's choices go through here: played back from the save while resuming, recorded (and saved) otherwise. */
  private recorded(ctls: Controller[]): Controller[] {
    const decide = async <T,>(s: number, k: Decision["k"], live: () => T | Promise<T>): Promise<T> => {
      const q = this.replay[0];
      if (q) {
        if (q.s === s && q.k === k) { this.replay.shift(); this.handLog.push(q); if (!this.replay.length) this.endReplay(); return q.v as T; }
        this.replay = []; this.endReplay();          // the saved choices don't fit (shouldn't happen): play on live
      }
      const v = await live();
      this.handLog.push({ s, k, v });
      void this.saveProgress();
      return v;
    };
    return ctls.map((c, s): Controller => ({
      decideAction: (v) => decide(s, "a", () => c.decideAction(v)),
      decideKnock: c.decideKnock && ((v) => decide(s, "k", () => c.decideKnock!(v))),
      decideAfterSwap: c.decideAfterSwap && ((v, o) => decide(s, "w", () => c.decideAfterSwap!(v, o))),
      chooseState: c.chooseState && ((u, o, v) => decide(s, "st", () => c.chooseState!(u, o, v))),
      chooseCapital: (owned) => decide(s, "cap", () => (c.chooseCapital ? c.chooseCapital(owned) : owned[0])),
      onObserve: (e) => c.onObserve?.(e),
      onHandEnd: (r) => c.onHandEnd?.(r),
      onDeal: (h, n) => c.onDeal?.(h, n),
      onOwnHand: (h) => c.onOwnHand?.(h),
    }));
  }
  /** The resumed hand has caught up with where it was saved: show it as it stands. */
  private endReplay(): void {
    this.hurry = false;
    this.status = "";
    this.renderAll();
  }
  private removeSave(key: string): void { const m = readSaveMap(); delete m[key]; writeSaveMap(m); }
  /** End a game without finishing it: gone from this device and your account (it isn't recorded). */
  async endSavedGame(key: string): Promise<void> {
    this.removeSave(key);
    await cloudEnd(key);
  }


  /**
   * Your colour changed (profile, Edit Player): recolour you in the game being played — seats, map,
   * standings, logs — and in the saved game, so a reload or resume keeps it. Any AI whose colour
   * looks too close to yours (or to another AI's) moves to a clearly different one, your old colour
   * first, so no two players look alike.
   */
  private recolourGame(next: string): void {
    /** old colour → new colour for every seat that changes. */
    const remapFor = (players: PlayerConfig[]): Map<string, string> => {
      const m = new Map<string, string>(), you = players.find((p) => p.id === "you");
      if (!you) return m;
      const ais = players.filter((p) => p.id !== "you");
      const aiNext = assignAiColours([next], ais.map((p) => p.colour), you.colour === next ? [] : [you.colour]);
      if (you.colour !== next) m.set(you.colour, next);
      ais.forEach((p, i) => { if (p.colour !== aiNext[i]) m.set(p.colour, aiNext[i]); });
      return m;
    };
    const swap = (c: string, m: Map<string, string>) => m.get(c) ?? c;
    const swapHtml = (s: string, m: Map<string, string>) => s.replace(/#[0-9a-f]{6}/gi, (c) => swap(c.toLowerCase(), m));

    if (this.gameActive) {
      const m = remapFor(this.players);
      if (m.size) {
        for (const p of this.players) p.colour = swap(p.colour, m);   // shared with the running engine
        for (const p of this.board?.players ?? []) p.colour = swap(p.colour, m);
        for (const v of this.stateInfo.values()) v.colour = swap(v.colour, m);
        for (const e of this.history) e.colour = swap(e.colour, m);
        for (const s of this.lastStandings) s.colour = swap(s.colour, m);
        for (const s of this.stats.values()) s.colour = swap(s.colour, m);
        this.turnLog = this.turnLog.map((l) => swapHtml(l, m));
        this.renderAll();
        if (this.mapOpen) this.renderMap();
      }
    }
    // the saved games (also ones that aren't running — "Your Player & Table" before resuming)
    try {
      const all = readSaveMap();
      let changed = false;
      for (const save of Object.values(all)) {
        if (!validSave(save)) continue;
        const active = this.gameActive && save.matchKey === this.matchKey;
        const m = active ? new Map(this.players.map((p, i) => [save.players[i]?.colour ?? "", p.colour])) : remapFor(save.players);
        if (!m.size) continue;
        for (const p of save.players) p.colour = swap(p.colour, m);
        for (const e of save.history ?? []) e.colour = swap(e.colour, m);
        changed = true;
      }
      if (changed) writeSaveMap(all);
    } catch { /* storage unavailable */ }
  }
  resumeGame(save: SaveState): void {
    this.players = save.players.map((p) => (p.name === "Susan" ? { ...p, name: "Susanne" } : p));   // games saved before the rename
    // your seat shows your account's saved name (games started before it loaded were saved as "You")
    const acct = myProfile()?.name;
    if (acct) this.players = this.players.map((p) => (p.id === "you" ? { ...p, name: acct } : p));
    // Games saved before recording existed have no hands to send; they finish but can't be recorded.
    this.matchKey = save.matchKey ?? newMatchKey(); this.startedAt = save.startedAt ?? save.savedAt;
    this.played = save.played ?? []; this.records = [];
    this.lastWin.clear(); this.lastClaim = 0;
    for (const hd of this.played) { const w = this.players[hd.winner]; if (w) this.noteWin(w.id, hd.hand); }
    this.method = save.method;
    this.history = save.history || [];
    this.nextStickyHand = save.stickySaid ? Infinity : save.startHand + 2 + Math.floor(Math.random() * 5);
    $("setup").classList.add("hidden");
    $("app").classList.remove("hidden");
    this.buildMaps();
    this.status = "Resuming…";
    this.gameActive = true;
    // games saved before colours were kept clearly apart: separate any AI that looks like you
    const you = this.players.find((p) => p.id === "you");
    if (you) this.recolourGame(you.colour);
    this.renderAll();
    this.aiProfiles = save.aiProfiles ?? [];
    const controllers = this.makeAIs(Date.now() >>> 0);
    const cfg: GameConfig = { players: this.players, stateMethod: this.method, seed: Date.now() >>> 0,
      resume: { startHand: save.startHand, dealerSeat: save.dealerSeat, totals: save.totals, pool: save.pool, rng: save.rng, peeks: save.peeks } };
    // the same hand is dealt again (saved shuffler), and every choice already made in it is played back
    this.replay = save.rng !== undefined ? [...(save.log ?? [])] : [];
    this.handLog = [];
    this.hurry = this.replaying;
    this.myPeeks = new Map((save.peeks ?? []).filter((p) => this.players[p.seat]?.id === "you").map((p) => [p.hand, p]));
    this.game = new PoliGame(cfg, this.recorded(controllers), this);
    this.game.play().catch((e) => console.error(e));
  }

  // ---- setup ----
  buildColourPicker(): void {
    const box = $("setup-colours");
    box.innerHTML = "";
    const pick = (c: string, btn: HTMLElement) => { this.colour = c; [...box.children].forEach((x) => x.classList.remove("sel")); btn.classList.add("sel"); this.redrawAvatarPreview?.(); this.showPlayerBar(); this.recolourGame(c); this.onProfileEdit?.(); };
    COLOURS.forEach((c) => {
      const b = h("button", { class: "colour-opt" + (c === this.colour ? " sel" : ""), style: `background:${c}`, attr: { title: c }, on: { click: () => pick(c, b) } });
      box.appendChild(b);
    });
    // Any colour at all: the colour wheel (applies as you choose).
    const wheel = colourWheel({ onPick: (c) => pick(c, wheel.button) });
    if (!COLOURS.includes(this.colour)) { wheel.mark(this.colour); wheel.button.classList.add("sel"); }
    box.appendChild(wheel.button);
    box.after(wheel.pop);
  }
  // Avatar builder: skin tone, hair, hair colour, facial hair, extras — with a live preview of the
  // face and the hand that will hold your cards.
  buildAvatarPicker(): void {
    const box = $("setup-avatar");
    box.innerHTML = "";
    const a = this.avatar;
    const face = h("div", { class: "face" }), hand = h("div", { class: "handpv" });
    const redraw = (report = true) => {
      face.innerHTML = a.photo ? `<img src="${a.photo}" alt="You" style="width:100%;height:100%;object-fit:cover;display:block">` : avatarSvg(a, this.colour);
      hand.replaceChildren(handsImg(a.skin));
      $("ng-face").innerHTML = face.innerHTML;          // the Your Player bar shows the same face
      saveAvatar(a);
      if (report) this.onProfileEdit?.();
    };
    box.appendChild(h("div", { class: "av-preview" }, [face, hand,
      h("div", { class: "av-cap" }, [h("b", { text: "Live Avatar Preview" }), h("small", { text: "All changes reflected immediately" })])]));
    const group = (label: string, items: HTMLElement[]) => {
      box.appendChild(h("div", { class: "av-group" + (items.length > 4 ? " wide" : ""), attr: { "data-g": label } }, [h("div", { class: "av-sub", text: label }), h("div", { class: "av-opts" }, items)]));
    };
    const pick = (row: HTMLElement[], el: HTMLElement, apply: () => void) => {
      row.forEach((x) => x.classList.remove("sel")); el.classList.add("sel"); apply(); redraw();
    };
    const swatches = (cols: string[], cur: number, set: (i: number) => void, title: string) => {
      const row: HTMLElement[] = [];
      cols.forEach((c, i) => {
        const b = h("button", { class: "av-swatch" + (i === cur ? " sel" : ""), style: `background:${c}`, attr: { title: `${title} ${i + 1}` },
          on: { click: () => pick(row, b, () => set(i)) } });
        row.push(b);
      });
      return row;
    };
    const chips = (opts: readonly (readonly [string, string])[], cur: string, set: (v: string) => void) => {
      const row: HTMLElement[] = [];
      for (const [v, lbl] of opts) {
        const b = h("button", { class: "av-chip" + (v === cur ? " sel" : ""), text: lbl, on: { click: () => pick(row, b, () => set(v)) } });
        row.push(b);
      }
      return row;
    };
    group("Appearance", chips(LOOKS, a.look, (v) => {
      a.look = v as AvatarCfg["look"];
      if (a.look === "woman") { a.facial = "none"; if (a.hair === "short" || a.hair === "bald") a.hair = "long"; }
      saveAvatar(a); this.buildAvatarPicker(); this.onProfileEdit?.();   // rebuild: facial-hair options depend on the look
    }));
    group("Skin tone", swatches(SKIN_TONES.map((t) => t.base), a.skin, (i) => (a.skin = i), "Skin tone"));
    group("Hair style", chips(HAIR_STYLES, a.hair, (v) => (a.hair = v)));
    group("Hair colour", swatches(HAIR_COLOURS, a.hairCol, (i) => (a.hairCol = i), "Hair colour"));
    if (a.look === "man") group("Facial hair", chips(FACIAL, a.facial, (v) => (a.facial = v)));
    group("Accessories", chips(EXTRAS, a.extra, (v) => (a.extra = v)));
    // Or use a real photo: centre-cropped to a 256px square JPEG and kept in this browser only.
    const file = h("input", { attr: { type: "file", accept: "image/*" }, style: "display:none" }) as HTMLInputElement;
    const clear = h("button", { class: "av-chip", text: "Remove photo", on: { click: () => { delete a.photo; clear.hidden = true; redraw(); } } });
    clear.hidden = !a.photo;
    file.onchange = () => {
      const f = file.files?.[0]; if (!f) return;
      const img = new Image();
      img.onload = () => {
        const n = 256, side = Math.min(img.width, img.height), cv = document.createElement("canvas");
        cv.width = cv.height = n;
        cv.getContext("2d")!.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, n, n);
        a.photo = cv.toDataURL("image/jpeg", 0.85); clear.hidden = false; redraw();
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(f);
    };
    group("Or use your own photo", [h("button", { class: "av-chip", text: "📷 Upload a photo", on: { click: () => file.click() } }), clear, file]);
    redraw(false);
    this.redrawAvatarPreview = () => redraw(false);
  }
  redrawAvatarPreview?: () => void;
  /** Called after any change to your name, party, colour or look (the account saves it). */
  onProfileEdit?: () => void;
  onAccountChange?: () => void;
  profileSnapshot(): { name: string; party: string; colour: string; avatar: AvatarCfg } {
    return {
      name: ($("setup-name") as HTMLInputElement).value.trim(),
      party: ($("setup-party") as HTMLInputElement).value.trim(),
      colour: this.colour,
      avatar: { ...this.avatar },
    };
  }
  palette(): { colour: string; name: string }[] { return COLOURS.map((colour, i) => ({ colour, name: COLOUR_NAMES[i] })); }
  /** Your face as HTML (photo or drawn avatar), for small badges. */
  faceHtml(): string {
    return this.avatar.photo ? `<img src="${this.avatar.photo}" alt="" style="width:100%;height:100%;object-fit:cover;display:block">` : avatarSvg(this.avatar, this.colour);
  }
  /** Load a saved profile (from your account) into the New Game screen. Doesn't report an edit. */
  applyProfile(p: { name?: string; party?: string; colour?: string; avatar?: Partial<AvatarCfg> | null; photo?: string | null }): void {
    const edit = this.onProfileEdit; this.onProfileEdit = undefined;
    if (p.name !== undefined) ($("setup-name") as HTMLInputElement).value = p.name;
    if (p.party !== undefined) ($("setup-party") as HTMLInputElement).value = p.party;
    if (p.colour) { this.colour = p.colour; this.recolourGame(p.colour); }
    if (p.avatar) Object.assign(this.avatar, p.avatar);
    if (p.photo !== undefined) { if (p.photo) this.avatar.photo = p.photo; else delete this.avatar.photo; }
    saveAvatar(this.avatar);
    if ($("setup-colours").childElementCount) { $("setup-colours").nextElementSibling?.classList.contains("cw-pop") && $("setup-colours").nextElementSibling!.remove(); this.buildColourPicker(); }
    if ($("setup-avatar").childElementCount) this.buildAvatarPicker();
    this.showPlayerBar();
    this.onProfileEdit = edit;
  }
  // New Game's Your Player bar: name · party and the party colour, kept in step with the Edit Player drawer.
  showPlayerBar(): void {
    const name = ($("setup-name") as HTMLInputElement).value.trim() || "You";
    const party = ($("setup-party") as HTMLInputElement).value.trim() || "Independents";
    $("ng-who-line").textContent = `${name} · ${party}`;
    $("ng-col-dot").style.background = this.colour;
    const i = COLOURS.indexOf(this.colour);
    $("ng-col-name").textContent = i >= 0 ? COLOUR_NAMES[i] : "Custom";
  }
  // Theme tiles: live mini preview (the theme's own card faces + back), or its preview image once
  // supplied. Clicking previews the theme on the whole page; onPick decides whether it sticks.
  private themeTiles(box: HTMLElement, selected: string, onPick: (id: string) => void): void {
    box.innerHTML = "";
    const sample: Card[] = [{ rank: 14, suit: "S" } as Card, { rank: 7, suit: "H" } as Card];
    for (const t of readyThemes()) {
      const prev = assetReady(t.previewImage)
        ? h("img", { class: "theme-thumb", attr: { src: t.previewImage!, alt: "", loading: "lazy" } })
        : h("div", { class: "pair", html: sample.map(cardMini).join("") + `<div class="mini-back"></div>` });
      const tile = h("div", { class: "skin-tile" + (t.id === selected ? " sel" : ""), attr: { role: "button", tabindex: "0", title: t.description, "data-id": t.id },
        on: { click: () => { [...box.children].forEach((x) => x.classList.remove("sel")); tile.classList.add("sel"); onPick(t.id); } } }, [
        prev, h("span", { class: "nm", text: t.displayName }),
        h("button", { class: "preview-cards", text: "Preview cards", attr: { type: "button" },
          on: { click: (e: Event) => { e.stopPropagation(); openCardPreview(t.id); } } }),
      ]);
      applyTheme(t.id, tile);
      box.appendChild(tile);
    }
  }
  // New Game: a preview of the selected theme's room (its landscape background on landscape screens,
  // the portrait one on portrait screens; it opens full size), else its lobby sign, else a felt with cards.
  buildThemePicker(): void {
    const t = applyTheme(savedThemeId());
    const sample: Card[] = [{ rank: 14, suit: "S" } as Card, { rank: 13, suit: "H" } as Card];
    const bg = t.backgroundImage;
    const roomPic = () => h("picture", {}, [
      ...(assetReady(bg?.mobile) ? [h("source", { attr: { media: "(orientation: portrait)", srcset: bg!.mobile! } })] : []),
      h("img", { attr: { src: bg!.desktop!, alt: `The ${t.displayName} room` } })]);
    const view = () => openLightbox(h("div", { class: "lb-room" }, [roomPic()]), t.displayName);
    const art = assetReady(bg?.desktop)
      ? h("div", { class: "ng-preview", attr: { role: "button", tabindex: "0", "aria-label": `View the ${t.displayName} room full size`, title: "View full size" },
          on: { click: view, keydown: (e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); view(); } } } },
          [roomPic(), h("span", { class: "lobby-zoom", text: "⤢", attr: { "aria-hidden": "true" } })])
      : assetReady(t.lobbyImage)
      ? h("div", { class: "lobby-sign ng-sign", attr: { role: "button", tabindex: "0", "aria-label": `View the ${t.displayName} sign full size`, title: "View full size" },
          on: { click: openLobbyView, keydown: (e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLobbyView(); } } } },
          [h("span", { class: "lobby-zoom", text: "⤢", attr: { "aria-hidden": "true" } })])
      : h("div", { class: "ng-felt" }, [h("div", { class: "pair", html: sample.map(cardMini).join("") + `<div class="mini-back"></div>` })]);
    $("ng-theme-count").textContent = `Theme ${THEMES.indexOf(t) + 1} of ${THEMES.length}`;
    $("ng-theme").replaceChildren(art, h("div", { class: "ng-theme-name", text: t.displayName }), h("p", { class: "ng-theme-desc", text: t.description }));
  }
  // Theme gallery: all themes (ready ones selectable, planned ones "coming soon"). Selecting a theme
  // selects its card set too; Preview Cards only looks.
  openGallery(): void {
    const grid = $("gallery-grid"), cur = currentTheme().id;
    const sample: Card[] = [{ rank: 14, suit: "S" } as Card, { rank: 13, suit: "H" } as Card];
    grid.replaceChildren(...THEMES.map((t) => {
      const ready = t.status === "ready", sel = t.id === cur;
      const art = ready && assetReady(t.lobbyImage)
        ? h("img", { class: "gal-art", attr: { src: t.lobbyImage!, alt: "", loading: "lazy" } })
        : ready
          ? h("div", { class: "gal-art gal-felt" }, [h("div", { class: "pair", html: sample.map(cardMini).join("") + `<div class="mini-back"></div>` })])
          : h("div", { class: "gal-art gal-soon", style: `background: radial-gradient(circle at 50% 40%, ${t.accentColour}55, ${t.secondaryColour} 70%)` }, [h("span", { text: "Coming soon" })]);
      const status = h("span", { class: "gal-status" + (sel ? " sel" : ready ? "" : " soon"), text: sel ? "✓ Selected" : ready ? "Available" : "Coming soon" });
      const actions = h("div", { class: "gal-actions" }, ready ? [
        h("button", { class: "ui-link", text: "Preview Cards", attr: { type: "button" }, on: { click: () => openCardPreview(t.id) } }),
        h("button", { class: sel ? "ui-btn-ghost" : "ui-btn", text: sel ? "Selected" : "Select Theme", attr: { type: "button", ...(sel ? { disabled: "" } : {}) },
          on: { click: () => { applyTheme(t.id); saveThemeId(t.id); this.redrawAvatarPreview?.(); this.buildThemePicker(); this.closeGallery(); } } }),
      ] : []);
      if (ready) applyTheme(t.id, art);
      return h("article", { class: "gal-tile" + (sel ? " sel" : "") + (ready ? "" : " planned") }, [art,
        h("div", { class: "gal-body" }, [h("div", { class: "gal-top" }, [h("h3", { text: t.displayName }), status]),
          h("p", { class: "gal-desc", text: t.description }), actions])]);
    }));
    $("setup").classList.add("hidden");
    $("gallery").classList.remove("hidden");
    $("gallery").scrollTop = 0;
    ($("gallery-back") as HTMLButtonElement).focus();
  }
  closeGallery(): void {
    $("gallery").classList.add("hidden");
    $("setup").classList.remove("hidden");
    ($("ng-change-theme") as HTMLButtonElement).focus();
  }
  // Mid-game theme switch: preview live, Apply keeps it, Cancel restores. Never touches game state.
  // ---- Peek at the bones ----
  /** Your peeks, by hand. */
  private myPeeks = new Map<number, PeekRecord>();
  /** Multiplayer: the hand whose peeks can be bought now and why you can't ("" = you can). */
  private remotePeek: { hand: number; blocked: string } | null = null;
  private peekWait: ((p: PeekRecord | string) => void) | null = null;
  private mySeat(): number { return this.players.findIndex((p) => p.id === "you"); }
  /** Can you peek at `hand`? null: not now (closed, and you didn't); "" yes; otherwise why not. */
  private peekState(hand: number): string | null {
    if (this.myPeeks.has(hand)) return "";
    if (this.remote) return this.remotePeek?.hand === hand ? this.remotePeek.blocked : null;
    const why = this.game?.peekBlocked(this.mySeat(), hand) ?? "closed";
    return /closed/.test(why) ? null : why;
  }
  private async buyPeek(hand: number): Promise<PeekRecord | string> {
    const had = this.myPeeks.get(hand);
    if (had) return had;
    if (this.remote) {
      const got = new Promise<PeekRecord | string>((r) => { this.peekWait = r; });
      sendToTable({ type: "peek", hand });
      const t = setTimeout(() => this.peekWait?.("No answer from the table — try again."), 10_000);
      const res = await got; clearTimeout(t); this.peekWait = null;
      return res;
    }
    const r = this.game?.buyPeek(this.mySeat(), hand);
    if (!r) return "There's no game in play.";
    if (!r.ok) return r.reason;
    this.myPeeks.set(hand, r.peek);
    if (r.charged) this.peekCharged(r.peek.seat, hand);
    void this.saveProgress();                         // bought once: a resume never charges it again
    return r.peek;
  }
  /** A peek's cost (now 0: peeks are free) comes off the totals shown for that hand and the board. */
  private peekCharged(seat: number, hand: number): void {
    const t = this.totalsAfter.get(hand); if (t) t[seat] -= PEEK_COST;
    if (this.board && this.scoredHand === hand) { const p = this.board.players[seat]; if (p) p.score -= PEEK_COST; }
    this.renderAll();
  }
  /** Multiplayer: what the table says about peeks (your own only). */
  setRemotePeeks(peek: { hand: number; blocked: string } | null, mine: PeekRecord[]): void {
    this.remotePeek = peek;
    for (const p of mine) this.myPeeks.set(p.hand, p);
  }
  /** Multiplayer: the table turned down a peek (true if one was waiting). */
  onPeekRefused(error: string): boolean { const w = this.peekWait; if (!w) return false; w(error); return true; }
  /** Multiplayer: your peek arrived. */
  onPeek(p: PeekRecord): void { this.myPeeks.set(p.hand, p); this.peekWait?.(p); }
  /** Multiplayer: someone bought a peek — everyone's totals as the table has them now. */
  onScores(e: { seat: number; cost: number; totals: PlayerTotals[] }): void {
    const hand = this.lastRecord?.handNumber ?? 0, t = this.totalsAfter.get(hand);
    if (t) t[e.seat] -= e.cost;
    if (this.board) e.totals.forEach((x, i) => { const p = this.board!.players[i]; if (p) p.score = x.score; });
    if (e.seat !== this.mySeat()) this.showToast("A peek at the bones", `${this.players[e.seat]?.name} peeked at their next card.`);
    this.renderAll();
    if (!$("showdown").classList.contains("hidden")) this.sdRedraw?.();
  }
  /** The peek button for the results screen (empty when you can't peek at this hand and didn't). */
  private peekBtnHtml(hand: number, cls: string): string {
    const st = this.peekState(hand);
    if (st === null) return "";
    if (this.myPeeks.has(hand)) return `<button class="${cls} peek-btn" data-peek="${hand}" type="button">👁 Your peek at the bones</button>`;
    if (st) return `<button class="${cls} peek-btn" type="button" disabled title="${st}">👁 Peek at the bones</button><span class="peek-why">${st}</span>`;
    return `<button class="${cls} peek-btn" data-peek="${hand}" type="button">👁 Peek at the bones</button>`;
  }
  /** The peek panel over the results: confirm, then the card and what it would (hypothetically) have done. */
  private openPeek(host: HTMLElement, hand: number, rec: HandRecord | undefined): void {
    host.querySelector(".peek-panel")?.remove();
    const panel = h("div", { class: "peek-panel", attr: { role: "dialog", "aria-modal": "true", "aria-label": "Peek at the bones" } });
    const close = () => panel.remove();
    const show = (p: PeekRecord) => {
      const me = rec?.results.find((x) => x.seat === p.seat), win = rec?.results[0];
      const wname = win ? this.players[win.seat].name : "the winner", wcat = win ? CATEGORY_NAME[win.rank.category] : "";
      const a = p.analysis, ct = App.cardPlain;
      const verdict = !a.improves ? `It wouldn't have improved your ${me ? CATEGORY_NAME[me.rank.category] : "hand"}.`
        : `Swapped in, it would have made <b>${CATEGORY_NAME[a.bestCategory]}</b>. ` + (a.wasWinner ? "You won the hand anyway."
          : a.beatsWinner ? `That would have beaten ${wname}'s ${wcat}.` : a.tiesWinner ? `That would have tied ${wname}'s ${wcat} rank for rank (suits would decide).`
          : `Still not enough to beat ${wname}'s ${wcat}.`);
      panel.replaceChildren(h("div", { class: "peek-card" }, [
        h("div", { class: "peek-k", text: `Hand ${p.hand} · the bones after your last turn` }),
        h("div", { class: "peek-row" }, [
          h("div", { class: "peek-hand" }, (me?.cards ?? []).map((c) => this.card(c, "mid", {}))),
          h("span", { class: "peek-plus", text: "+" }),
          h("div", { class: "peek-next" }, [this.card(p.card, "mid", {})]),
        ]),
        h("p", { class: "peek-verdict", html: `Your next card was the <b>${ct(p.card)}</b>. ${verdict}` }),
        h("p", { class: "peek-note", text: "Hypothetical: the hand's result, the state and its points stand as played." }),
        h("div", { class: "peek-btns" }, [h("button", { class: "sd3-btn gold", text: "Close", attr: { type: "button" }, on: { click: close } })]),
      ]));
    };
    const had = this.myPeeks.get(hand);
    if (had) show(had);
    else {
      // Free: no confirmation, the card shows as soon as the engine (or the table) hands it over.
      const err = h("p", { class: "peek-err" });
      const go = async () => {
        err.textContent = "";
        const r = await this.buyPeek(hand);
        if (typeof r === "string") {
          err.textContent = r;
          const retry = h("button", { class: "sd3-btn gold", text: "Try again", attr: { type: "button" }, on: { click: () => void go() } });
          panel.querySelector(".peek-btns")?.replaceChildren(retry, h("button", { class: "sd3-btn", text: "Close", attr: { type: "button" }, on: { click: close } }));
          return;
        }
        show(r);
        this.sdRedraw?.(panel);
      };
      panel.replaceChildren(h("div", { class: "peek-card" }, [
        h("div", { class: "peek-k", text: `Hand ${hand}` }),
        h("p", { class: "peek-q", text: "Peeking at the bones…" }),
        err,
        h("div", { class: "peek-btns" }, [h("button", { class: "sd3-btn", text: "Close", attr: { type: "button" }, on: { click: close } })]),
      ]));
      void go();
    }
    panel.addEventListener("click", (e) => { if (e.target === panel) close(); });
    host.appendChild(panel);
    (panel.querySelector("button") as HTMLButtonElement | null)?.focus();
  }
  /** Redraw the results screen in place (keeping an open peek panel). */
  private sdRedraw?: (keep?: HTMLElement) => void;
  private scoredHand = 0;

  // ---- the President ----
  private capitalMode = false;
  /** The game's winner, once announced, and their capital. */
  private president: { seat: number; capital: string | null } | null = null;
  /** You won the game: the final map opens and you name one of your states as the capital. */
  enterCapitalSelect(owned: string[]): void {
    for (const id of ["showdown", "endscreen"]) $(id).classList.add("hidden");
    this.capitalMode = true;
    this.mode = "map-select"; this.claimMode = true; this.claimReview = false; this.aiPickNote = null; this.mapOpen = true;
    this.selectable = new Set(owned);
    this.status = "You're President — choose your capital from your states.";
    this.actions = [];
    this.renderAll(); this.renderMap();
    this.showBanner("🏛️ You're President!", "Choose one of your states as your capital", { ms: 3600 });
  }
  /** While the President (someone else) chooses: everyone is on the map, with the President's states lit up. */
  private capitalWatch: { name: string; owned: string[] } | null = null;
  async onCapitalChoosing(e: { seat: number; owned: string[] }): Promise<void> {
    if (this.replaying || this.players[e.seat]?.id === "you") return;    // yours: you choose on the map (enterCapitalSelect)
    this.watchCapital(e.seat, e.owned);
    if (!this.remote) await sleep(this.hurry ? 300 : 2600);                // an AI President takes a moment, so it can be seen
  }
  /** Take everyone to the map while the President names a capital. */
  watchCapital(seat: number, owned: string[]): void {
    const p = this.players[seat]; if (!p || this.capitalWatch) return;
    for (const id of ["showdown", "endscreen"]) $(id).classList.add("hidden");
    this.capitalWatch = { name: p.name, owned };
    this.claimMode = false; this.claimReview = false; this.aiPickNote = null; this.mapOpen = true;
    this.status = `${p.name} is President — choosing a capital.`;
    this.renderAll(); this.renderMap();
    this.showBanner(`🏛️ ${esc(p.name)} is President`, "Choosing a capital from their states…", { ms: 2600, over: true });
  }
  /** The President and capital: everyone is on the map, the capital's state lights up, a star with the capital's
   *  name zooms down into it, then fireworks and the opening of the anthem; then the inauguration. */
  async onPresident(e: { seat: number; capital: string | null; standings: PlayerTotals[] }): Promise<void> {
    this.president = { seat: e.seat, capital: e.capital };
    this.capitalMode = false; this.claimMode = false; this.mode = "idle"; this.selectable.clear();
    this.capitalWatch = null;
    if (this.replaying) { this.mapOpen = false; $("map-modal").classList.add("hidden"); this.markCapital(); return; }
    const p = this.players[e.seat];
    let sang = false;
    if (e.capital && p) {
      for (const id of ["showdown", "endscreen"]) $(id).classList.add("hidden");
      this.claimReview = false; this.aiPickNote = null; this.mapOpen = true; this.focusCode = e.capital;
      this.renderAll(); this.renderMap();
      this.modalPaths.get(e.capital)?.classList.add("capital-hl");
      await sleep(500);
      await this.capitalLanding(e.capital, p.colour);
      this.markCapital();
      this.showBanner(`★ ${esc(capitalPlace(e.capital))}`, `The capital of President ${esc(p.name)}`, { ms: 5200, win: true, over: true });
      const stop = matchMedia("(prefers-reduced-motion: reduce)").matches ? () => {} : this.fireworks(p.colour, 6000);
      if (music.audible) { this.playAnthem(true); sang = true; } else this.playFanfare();
      await sleep(6200);
      stop();
      this.modalPaths.get(e.capital)?.classList.remove("capital-hl");
    } else this.markCapital();
    this.mapOpen = false; $("map-modal").classList.add("hidden");
    await this.inauguration(e.seat, e.capital, sang);
  }
  /** A big star with the capital's name, zooming down from the middle of the screen into its state. */
  private capitalLanding(code: string, colour: string): Promise<void> {
    const path = this.modalPaths.get(code);
    if (!path || matchMedia("(prefers-reduced-motion: reduce)").matches) return Promise.resolve();
    const r = path.getBoundingClientRect(), size = Math.min(260, innerHeight * 0.5);
    const el = h("div", { class: "cap-fly", style: `--pc:${colour};width:${size}px`, attr: { "aria-hidden": "true" } }, [
      h("div", { class: "cap-fly-star", html: `<svg viewBox="-50 -50 100 100" width="${size}" height="${size}"><polygon points="${
        Array.from({ length: 10 }, (_, i) => { const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? 19 : 46; return `${(rr * Math.cos(a)).toFixed(1)},${(rr * Math.sin(a)).toFixed(1)}`; }).join(" ")}"/></svg>` }),
      h("div", { class: "cap-fly-name", text: capitalPlace(code) }),
    ]);
    document.body.appendChild(el);
    const dx = r.left + r.width / 2 - innerWidth / 2, dy = r.top + r.height / 2 - innerHeight / 2;
    const end = Math.max(0.05, Math.min(r.width, r.height) * 0.75 / size);
    const fly = el.animate([
      { transform: "translate(-50%, -50%) scale(1.8) rotate(-30deg)", opacity: 0 },
      { transform: "translate(-50%, -50%) scale(1) rotate(0deg)", opacity: 1, offset: 0.3 },
      { transform: "translate(-50%, -50%) scale(1) rotate(0deg)", opacity: 1, offset: 0.5 },
      { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(${end})`, opacity: 1 },
    ], { duration: 3200, easing: "cubic-bezier(.45,0,.25,1)", fill: "forwards" });
    el.querySelector<HTMLElement>(".cap-fly-name")!.animate([{ opacity: 1, offset: 0 }, { opacity: 1, offset: 0.5 }, { opacity: 0, offset: 0.68 }, { opacity: 0 }], { duration: 3200, fill: "forwards" });
    return fly.finished.then(() => { el.remove(); }, () => { el.remove(); });
  }
  /** A star on the capital, on both maps. */
  private markCapital(): void {
    const c = this.president?.capital; if (!c) return;
    for (const paths of [this.modalPaths, this.miniPaths]) {
      const p = paths.get(c), svg = p?.ownerSVGElement; if (!p || !svg) continue;
      svg.querySelector(".capital-star")?.remove();
      const b = p.getBBox(), r = Math.max(7, Math.min(16, Math.min(b.width, b.height) * 0.38));
      const pts = Array.from({ length: 10 }, (_, i) => { const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.45 : r; return `${b.x + b.width / 2 + rr * Math.cos(a)},${b.y + b.height / 2 + rr * Math.sin(a)}`; }).join(" ");
      const star = svgEl("polygon", { class: "capital-star", points: pts });
      star.appendChild(svgEl("title", {})).textContent = `${capitalPlace(c)} — the President's capital`;
      svg.appendChild(star);
    }
  }
  /** The inauguration: portrait, name and colour, the seal, a little confetti and a fanfare. Skippable;
   *  quieter with reduced motion, silent with the music muted. */
  private inauguration(seat: number, capital: string | null, sang = false): Promise<void> {
    const p = this.players[seat]; if (!p) return Promise.resolve();
    const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const host = h("div", { class: "inaug" + (still ? " still" : ""), style: `--pc:${p.colour}`, attr: { role: "dialog", "aria-modal": "true", "aria-label": `${p.name} is President` } }, [
      h("div", { class: "inaug-card" }, [
        h("div", { class: "inaug-seal", html: SEAL_SVG }),
        h("div", { class: "inaug-face" }, [this.avatarEl(p, 120)]),
        h("div", { class: "inaug-k", text: "Sworn in as President" }),
        h("div", { class: "inaug-name", text: p.name }),
        h("div", { class: "inaug-party", text: p.party }),
        capital ? h("div", { class: "inaug-cap", html: `Capital: <b>${capitalPlace(capital)}</b>` }) : h("div", { class: "inaug-cap", text: "No state, so no capital to name" }),
        h("button", { class: "sd3-btn gold inaug-skip", text: "Continue", attr: { type: "button" } }),
      ]),
    ]);
    document.body.appendChild(host);
    let stopFireworks = () => {};
    if (!still) { this.confetti(p.colour, true, 36); stopFireworks = this.fireworks(p.colour, 7000); }
    if (!sang) { if (music.audible) this.playAnthem(); else this.playFanfare(); }   // the anthem's opening already played as the star landed
    (host.querySelector(".inaug-skip") as HTMLButtonElement).focus();
    return new Promise((done) => {
      const end = () => { clearTimeout(t); stopFireworks(); host.remove(); document.removeEventListener("keydown", key); done(); };
      const key = (e: KeyboardEvent) => { if (e.key === "Escape" || e.key === "Enter") end(); };
      const t = setTimeout(end, still ? 5000 : 7000);
      host.addEventListener("click", end);
      document.addEventListener("keydown", key);
    });
  }
  /** The national anthem for the new President. The game's songs fade out under it; it plays on
   *  under the final results and fades out within a second once they're closed. */
  private playAnthem(opening = false): void {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx ||= new Ctx();
      music.hush(true);
      playAnthem(this.audioCtx, music.prefs.volume, () => music.audible, opening);
    } catch { /* audio unavailable */ }
  }
  /** Fireworks over the inauguration: rockets climb and burst in the President's colour, gold and white. */
  private fireworks(colour: string, ms: number): () => void {
    const cv = document.createElement("canvas"); cv.className = "fireworks"; cv.setAttribute("aria-hidden", "true");
    document.body.appendChild(cv);
    const ctx = cv.getContext("2d"); if (!ctx) { cv.remove(); return () => {}; }
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const size = () => { cv.width = innerWidth * dpr; cv.height = innerHeight * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    size(); addEventListener("resize", size);
    const cols = [colour, "#f3cf7a", "#ffffff", "#e8463c", "#5aa0ff"];
    type P = { x: number; y: number; vx: number; vy: number; life: number; max: number; c: string; rocket?: boolean; ty?: number };
    let ps: P[] = [];
    const launch = () => {
      const W = innerWidth, H = innerHeight, x = W * (0.15 + Math.random() * 0.7);
      ps.push({ x, y: H + 10, vx: (Math.random() - 0.5) * 1.2, vy: -(7 + Math.random() * 3.5) * Math.min(1, H / 700) - 2, life: 0, max: 200, c: "#fff6dc", rocket: true, ty: H * (0.12 + Math.random() * 0.3) });
    };
    const burst = (x: number, y: number) => {
      const c = cols[Math.floor(Math.random() * cols.length)], c2 = cols[Math.floor(Math.random() * cols.length)];
      const n = 46 + Math.floor(Math.random() * 30), sp = 2.2 + Math.random() * 2.2;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2, v = sp * (0.55 + Math.random() * 0.5);
        ps.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 0, max: 50 + Math.random() * 30, c: i % 3 ? c : c2 });
      }
    };
    const start = performance.now(); let next = 0, last = start, stopped = false;
    const frame = (now: number) => {
      const dt = Math.min(2.5, (now - last) / 16.7); last = now;
      const running = now - start < ms && !stopped;
      if (running && now - start > next) { launch(); if (Math.random() < 0.45) launch(); next += 380 + Math.random() * 420; }
      ctx.globalCompositeOperation = "destination-out"; ctx.fillStyle = "rgba(0,0,0,.24)"; ctx.fillRect(0, 0, innerWidth, innerHeight);
      ctx.globalCompositeOperation = "lighter";
      const keep: P[] = [];
      for (const p of ps) {
        p.life += dt; p.x += p.vx * dt; p.y += p.vy * dt;
        if (p.rocket) {
          p.vy += 0.05 * dt;
          if (p.y <= p.ty! || p.vy >= -0.5) { burst(p.x, p.y); continue; }
          ctx.fillStyle = p.c; ctx.fillRect(p.x - 1.2, p.y - 1.2, 2.4, 4);
        } else {
          p.vy += 0.045 * dt; p.vx *= 0.985 ** dt; p.vy *= 0.985 ** dt;
          if (p.life > p.max) continue;
          ctx.globalAlpha = Math.max(0, 1 - p.life / p.max); ctx.fillStyle = p.c;
          ctx.beginPath(); ctx.arc(p.x, p.y, 1.9, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1;
        }
        keep.push(p);
      }
      ps = keep;
      if (running || ps.length) requestAnimationFrame(frame);
      else { removeEventListener("resize", size); cv.remove(); }
    };
    requestAnimationFrame(frame);
    return () => { stopped = true; };          // no new rockets; the last bursts fade out
  }
  /** A short fanfare in the room's own voice (the theme decides the instrument and the call). */
  private playFanfare(): void {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx ||= new Ctx();
      const ctx = this.audioCtx;
      if (ctx.state === "suspended") void ctx.resume();
      const theme = document.body.dataset.theme ?? "classic";
      const voice: Record<string, { wave: OscillatorType; notes: number[]; beat: number }> = {
        "las-vegas-high-roller": { wave: "sawtooth", notes: [523, 659, 784, 1047, 784, 1047], beat: 0.14 },
        "wild-west-saloon": { wave: "triangle", notes: [392, 523, 659, 523, 784], beat: 0.18 },
        "frontier-outpost": { wave: "triangle", notes: [392, 494, 587, 784], beat: 0.2 },
        "mississippi-riverboat": { wave: "square", notes: [349, 440, 523, 698, 523, 698], beat: 0.16 },
        "roaring-twenties": { wave: "square", notes: [466, 587, 698, 932, 698, 932], beat: 0.13 },
        "orient-express": { wave: "sine", notes: [440, 554, 659, 880, 659, 880], beat: 0.17 },
      };
      const v = voice[theme] ?? { wave: "sawtooth" as OscillatorType, notes: [392, 523, 659, 784, 659, 784], beat: 0.16 };
      const vol = 0.06 + 0.12 * music.prefs.volume, t0 = ctx.currentTime + 0.05;
      v.notes.forEach((f, i) => {
        const last = i === v.notes.length - 1, at = t0 + i * v.beat, len = last ? v.beat * 4 : v.beat * 0.9;
        for (const mult of [1, 2]) {
          const o = ctx.createOscillator(), g = ctx.createGain();
          o.type = v.wave; o.frequency.value = f * mult;
          g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(vol / mult, at + 0.02); g.gain.exponentialRampToValueAtTime(0.0008, at + len);
          o.connect(g).connect(ctx.destination); o.start(at); o.stop(at + len + 0.05);
        }
      });
    } catch { /* audio unavailable */ }
  }

  /**
   * Save & exit: wait until the save is safely with your account (progress shown, one tap only), then
   * close the app (or go Home in a browser). A failed save keeps the game open with Retry or Cancel.
   * Abandon is separate and asks first. A table game is saved by the table, which carries on.
   */
  openLeave(): void {
    const modal = $("leave-modal"), main = $("leave-main"), sure = $("leave-sure"), status = $("leave-status");
    const keep = $("leave-keep") as HTMLButtonElement, retry = $("leave-retry") as HTMLButtonElement, end = $("leave-end") as HTMLButtonElement;
    main.hidden = false; sure.hidden = true; status.textContent = ""; status.classList.remove("err");
    keep.hidden = false; retry.hidden = true; keep.disabled = false; end.disabled = false;
    $("leave-sub").textContent = this.remote ? "Your progress is saved, but the table will continue. Rejoin it from Play with friends."
      : "Everything up to this moment is saved. Resume it from Home whenever you like.";
    keep.textContent = this.remote ? "Exit" : "Save & exit";
    end.hidden = this.remote;
    const close = () => { if (!keep.disabled) modal.classList.add("hidden"); };
    const save = async () => {
      keep.disabled = true; retry.disabled = true; end.disabled = true; retry.hidden = true; keep.hidden = false;
      status.classList.remove("err"); status.textContent = this.remote ? "" : "Saving…";
      const r = this.remote ? "saved" : await this.saveProgress(true);
      if (r === "failed") {
        status.classList.add("err");
        status.textContent = "Couldn't save to your account (check the connection). The game stays open; it's saved on this device.";
        keep.hidden = true; retry.hidden = false; retry.disabled = false; keep.disabled = false; end.disabled = false;
        retry.focus();
        return;
      }
      status.textContent = "Saved.";
      this.exitGame(false);
    };
    keep.onclick = () => void save();
    retry.onclick = () => void save();
    end.onclick = () => { main.hidden = true; sure.hidden = false; ($("leave-end-no") as HTMLButtonElement).focus(); };
    $("leave-end-no").onclick = () => { main.hidden = false; sure.hidden = true; };
    $("leave-end-yes").onclick = async () => { ($("leave-end-yes") as HTMLButtonElement).disabled = true; if (!this.remote) await this.endSavedGame(this.matchKey); this.exitGame(true); };
    $("leave-cancel").onclick = () => { keep.disabled = false; modal.classList.add("hidden"); };
    modal.onclick = (e) => { if (e.target === modal) close(); };
    modal.onkeydown = (e) => { if (e.key === "Escape") close(); };
    modal.classList.remove("hidden");
    ($("leave-cancel") as HTMLButtonElement).focus();
  }
  /** Out of the game: the app closes itself (Save & exit), or Home in a browser (and after Abandon). */
  private exitGame(abandoned: boolean): void {
    this.gameActive = false;
    try { sessionStorage.setItem("poli-ok", "1"); if (abandoned) sessionStorage.setItem("poli-ended", "1"); } catch { /* */ }
    const app = capApp();
    if (app && !abandoned) { void app.exitApp(); return; }
    location.href = "/";
  }
  /** Android Back: close the phone menu pop-up, if one is open. */
  closeCompactPop(): boolean { if (!this.cmpPop) return false; this.cmpPop = ""; this.renderAll(); return true; }
  /** Android Back on the board: close it, unless you must pick a state or capital there (false then). */
  closeMapIfAllowed(): boolean {
    if (!this.mapOpen) return false;
    if (this.claimMode || this.capitalMode) return false;
    if (this.claimReview || this.aiPickNote) return false;     // the board is the hand's result: Continue moves on
    this.closeMap(); return true;
  }
  /** Leaving the screen (app sent to the background, tab hidden): save with your account too, just in case. */
  saveOnHide(): void { if (this.gameActive && !this.remote) void this.saveProgress(true); }
  openThemeModal(): void {
    const original = currentTheme().id;
    let picked = original;
    const modal = $("theme-modal");
    const desc = $("theme-desc");
    const show = (id: string) => { const t = themeById(id)!; desc.textContent = t.description; };
    this.themeTiles($("theme-grid"), original, (id) => { picked = id; applyTheme(id); show(id); this.renderAll(); });
    show(original);
    const close = (keep: boolean) => {
      if (keep) saveThemeId(picked); else { applyTheme(original); }
      modal.classList.add("hidden"); this.renderAll(); if (this.mapOpen) this.renderMap();
    };
    ($("theme-apply") as HTMLButtonElement).onclick = () => close(true);
    ($("theme-cancel") as HTMLButtonElement).onclick = () => close(false);
    modal.classList.remove("hidden");
  }
  /** Multiplayer: the hand the table chose for "These cards are sticky!" (Infinity: none). */
  setStickyHand(n: number): void { this.nextStickyHand = n; }
  /** Show a multiplayer game: `players` already turned so that you are seat 0 (id "you"). */
  startRemote(players: PlayerConfig[], method: StateMethod): void {
    this.remote = true;
    // the home/lobby volume control doesn't belong over the game (the game has its own music button) — as in solo play
    $("entry-music").hidden = true; music.setScene(""); showAccountButton(false);
    this.players = players;
    this.method = method;
    this.matchKey = ""; this.startedAt = Date.now(); this.played = []; this.records = []; this.lastWin.clear(); this.lastClaim = 0;
    for (const id of ["home", "setup", "mp"]) $(id).classList.add("hidden");
    $("app").classList.remove("hidden");
    this.buildMaps();
    this.gameActive = true;
    this.status = "Joining the table…";
    this.renderAll();
  }
  /** Multiplayer: flash the box of whoever the table is waiting on (people can take a while to decide). */
  showTurn(seat: number): void {
    if (this.activeSeat === seat) return;
    this.activeSeat = seat;
    this.renderAll();
  }
  /** The table moved on without you (the results timer ran out): close the results and the map. */
  forceContinue(): void {
    document.getElementById("sd-continue")?.click();
    this.rClaimContinue?.();
  }

  start(): void {
    // your account's saved name (as on every device), even if this screen hasn't caught up with it yet
    const name = myProfile()?.name || ($("setup-name") as HTMLInputElement).value.trim() || "Player";
    const party = ($("setup-party") as HTMLInputElement).value.trim() || "Independents";
    this.method = (document.querySelector('input[name="method"]:checked') as HTMLInputElement).value as StateMethod;
    // The AI players take colours that clearly don't look like yours (or each other's).
    const aiCol = assignAiColours([this.colour], [undefined, undefined, undefined, undefined]);
    this.players = [{ id: "you", name, party, colour: this.colour, isHuman: true }];
    // A different group of opponents every game, drawn from the real players' likenesses (never your own name)
    const rng = makeRng((Date.now() ^ (Math.random() * 2 ** 32)) >>> 0);
    const chosen = this.opponents.map((id) => persona(id)!).filter((p) => p && p.name.toLowerCase() !== name.toLowerCase()).slice(0, 4);
    const team = rng.shuffle([...chosen, ...pickPersonas(4 - chosen.length, rng, [name, ...chosen.map((p) => p.name)])]);
    for (let i = 0; i < 4; i++) this.players.push({ id: `ai${i}`, name: team[i].name, party: AI_PARTIES[i], colour: aiCol[i] });

    $("setup").classList.add("hidden");
    $("app").classList.remove("hidden");
    this.buildMaps();
    this.matchKey = newMatchKey(); this.nextStickyHand = 3 + Math.floor(Math.random() * 5); this.startedAt = Date.now(); this.played = []; this.records = [];
    this.lastWin.clear(); this.lastClaim = 0;
    this.gameActive = true;
    this.status = "Draw for the deal — the lowest card deals first.";
    this.renderAll();
    const token = ++this.gameToken;
    this.aiProfiles = ["", ...team.map((t) => t.id)];

    // Draw for the deal: the lowest card deals, and everyone sits from the dealer in card order (you stay at
    // the bottom of the screen; the others are seated round you).
    void this.drawForDeal(rng).then(({ seats, dealer }) => {
      if (token !== this.gameToken) return;              // left the game meanwhile
      const ais = this.aiProfiles;
      this.players = seats.map((i) => this.players[i]);
      this.aiProfiles = seats.map((i) => ais[i]);
      this.status = "Dealing…";
      this.renderAll();
      const controllers = this.makeAIs(Date.now() >>> 0);
      const cfg: GameConfig = { players: this.players, stateMethod: this.method, seed: Date.now() >>> 0, firstDealer: dealer };
      this.replay = []; this.handLog = []; this.myPeeks = new Map();
      this.game = new PoliGame(cfg, this.recorded(controllers), this);
      this.game.play().catch((e) => console.error(e));
    });
  }

  /**
   * Draw for the deal (before hand 1): the bones are spread face down; you pick a card and look at it, the AI
   * players draw theirs, and "Reveal cards" turns them all over. The lowest card deals first, and the seats
   * go round from the dealer, lowest to highest (equal cards in a random order).
   */
  private drawForDeal(rng: ReturnType<typeof makeRng>): Promise<{ seats: number[]; dealer: number }> {
    const deck = shuffledDeck(rng), n = this.players.length;
    const spread = deck.slice(0, 12), drawn: (Card | null)[] = this.players.map(() => null);
    const label = (c: Card) => `${({ 11: "J", 12: "Q", 13: "K", 14: "A" } as Record<number, string>)[c.rank] ?? c.rank}${({ C: "♣", D: "♦", H: "♥", S: "♠" } as Record<string, string>)[c.suit]}`;
    const back = h("div", { class: "dd-back", attr: { role: "dialog", "aria-modal": "true", "aria-labelledby": "dd-title" } });
    const panel = h("div", { class: "dd-panel" });
    back.appendChild(panel);
    document.body.appendChild(back);
    return new Promise((resolve) => {
      let revealed: { seats: number[]; dealer: number; order: number[] } | null = null;
      const draw = () => {
        panel.innerHTML = "";
        const mine = drawn[0];
        panel.appendChild(h("h2", { attr: { id: "dd-title" }, text: revealed ? "The draw" : "Draw for the deal" }));
        panel.appendChild(h("p", { class: "dd-sub", text: revealed ? "" : mine ? "Have a look at your card. When everyone's ready, reveal the cards." : "Pick a card from the bones. The lowest card deals first, and everyone sits from the dealer in card order." }));
        // the players, each with the card they drew (face down until the reveal; yours you can see)
        const order = revealed ? revealed.order : this.players.map((_, i) => i);
        const row = h("div", { class: "dd-seats" });
        order.forEach((i, k) => {
          const p = this.players[i], c = drawn[i];
          const faceUp = !!c && (i === 0 || !!revealed);
          row.appendChild(h("div", { class: `dd-seat${revealed && k === 0 ? " dealer" : ""}${i === 0 ? " you" : ""}`, style: `--c:${p.colour}` }, [
            this.avatarEl(p, 44),
            h("b", { text: i === 0 ? "You" : p.name }),
            c ? this.card(faceUp ? c : null, "mid", { pop: faceUp && !!revealed }) : h("div", { class: "dd-empty", text: "?" }),
            revealed ? h("span", { class: "dd-tag", text: k === 0 ? `Dealer · ${label(c!)}` : `${ordinalWord(k + 1)} · ${label(c!)}` }) : null,
          ]));
        });
        panel.appendChild(row);
        if (!mine) {
          const fan = h("div", { class: "dd-fan", attr: { role: "group", "aria-label": "The bones — pick a card" } });
          spread.forEach((c, j) => {
            const el = this.card(null, "mid", {});
            el.classList.add("dd-pick"); el.tabIndex = 0; el.setAttribute("role", "button"); el.setAttribute("aria-label", `Card ${j + 1}`);
            const pick = () => {
              drawn[0] = c;
              // the AI players draw theirs from what's left
              const rest = spread.filter((x) => x !== c);
              for (let i = 1; i < n; i++) drawn[i] = rest.splice(Math.floor(rng.next() * rest.length), 1)[0];
              this.playDeal(1, 0);
              draw();
            };
            el.addEventListener("click", pick);
            el.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } });
            fan.appendChild(el);
          });
          panel.appendChild(fan);
        } else if (!revealed) {
          panel.appendChild(h("p", { class: "dd-mine", text: `You drew the ${label(mine)}.` }));
          panel.appendChild(h("button", { class: "ui-btn ui-btn-lg", attr: { type: "button" }, text: "Reveal cards", on: { click: () => { revealed = seatByDraw(drawn as Card[], rng, 0); this.playDeal(4, 60); draw(); } } }));
        } else {
          const d = revealed.order[0], dealerName = d === 0 ? "You deal" : `${this.players[d].name} deals`;
          const ties = [...new Set((drawn as Card[]).map((c) => c.rank))].filter((r) => (drawn as Card[]).filter((c) => c.rank === r).length > 1)
            .map((r) => `${revealed!.order.filter((i) => drawn[i]!.rank === r).map((i) => (i === 0 ? "you" : this.players[i].name)).join(" and ")} drew the same — seated at random`);
          panel.appendChild(h("p", { class: "dd-mine", text: `${dealerName} first, with the lowest card. Everyone sits from the dealer, lowest to highest.` }));
          for (const t of ties) panel.appendChild(h("p", { class: "dd-sub", text: t[0].toUpperCase() + t.slice(1) + "." }));
          panel.appendChild(h("button", { class: "ui-btn ui-btn-lg", attr: { type: "button" }, text: "Start the game", on: { click: () => { back.remove(); resolve(revealed!); } } }));
        }
      };
      draw();
      (panel.querySelector(".dd-pick") as HTMLElement | null)?.focus();
    });
  }

  // ---- observer ----
  async onHandStart(b: BoardSnapshot): Promise<void> {
    this.board = b; this.activeSeat = -1; this.targetState = b.targetState; this.mode = "idle";
    this.seatTook.clear(); this.statusHtml = ""; this.turnLog = [];
    if (this.replaying) {   // resuming part-way through this hand: its save stays as it is
      this.handLog = [];
      this.snap = { startHand: b.handNumber, dealerSeat: b.dealerSeat, totals: b.players.map((p) => ({ id: p.id, score: p.score, states: [...p.states] })),
        pool: b.remainingPool, rng: this.game?.handStartRng, history: [...this.history], played: [...this.played] };
      this.setHand([]); this.status = "Picking up where you left off…"; this.renderAll();
      return;
    }
    this.handLog = [];
    this.setHand([]); this.handName = "—";   // blank the bottom until the deal lands (receiveDeal)
    // Once a game, a few hands in, an old-timer grumbles about the cards.
    const sticky = b.handNumber >= this.nextStickyHand;
    if (sticky) this.nextStickyHand = Infinity;
    this.saveSnapshot(b); // resume point = start of this hand
    this.gameActive = true;
    if (sticky) setTimeout(() => this.voiceLine("assets/audio/sticky.mp3", "These cards are sticky!", { pitch: 0.55, rate: 0.78, male: true }), 2200);
    this.status = b.targetState ? `New hand — playing for ${stateName(b.targetState)}.` : "New hand dealt — the middle is face down.";
    // the first hand: who you're up against tonight, and how each of them plays
    const lineup = this.lineup().map(([n, st]) => `${n} (${st})`);
    if (b.handNumber === 1 && lineup.length) this.status = `Tonight's table: ${lineup.join(" · ")}.`;
    this.actions = [];
    this.renderAll();
    this.showBanner(`Hand ${b.handNumber} <span style="font-size:.7em;color:var(--n500)">/ 50</span>`,
      b.targetState ? `Playing for ${stateName(b.targetState)}` : "New hand — the cards are dealt", { ms: 2400, low: true });   // + 0.5 s fade: gone within 3 s (Scott)
    await sleep(900);
  }
  // What each opponent last took this hand (seat → short html), shown on their plaque, and the
  // middle slot that just changed, highlighted for one render. Knowing what they took is strategy:
  // it tells you what not to feed them.
  private seatTook = new Map<number, string>();
  private midFlash = -1;
  private turnLog: string[] = [];
  // Rich version of the status line, used only while `status` still equals the text it was written for.
  private statusHtml = ""; private statusHtmlFor = "";
  // Cards in words ("2 of Hearts", "Jack of Clubs") — easier to read at a glance than rank + suit symbol.
  private static cardTxt(c: Card | null | undefined): string {
    if (!c) return "?";
    const r = ({ J: "Jack", Q: "Queen", K: "King", A: "Ace" } as Record<string, string>)[rankStr(c)] ?? rankStr(c);
    const suit = ({ C: "Clubs", D: "Diamonds", H: "Hearts", S: "Spades" } as Record<string, string>)[c.suit];
    return `<b class="ct">${r} of ${suit}</b>`;
  }
  private static cardPlain(c: Card): string { return App.cardTxt(c).replace(/<[^>]+>/g, ""); }
  async onAction(e: { seat: number; action: Action; board: BoardSnapshot }): Promise<void> {
    const prevMid = this.board?.middle ?? [];
    this.board = e.board; this.activeSeat = e.seat;
    const p = e.board.players[e.seat];
    const me = p.id === "you";
    const verb: Record<string, string> = { REVEAL: "turned the middle face up", TAKE_MIDDLE: "took the whole middle",
      SWAP_ONE: "swapped a card", BLIND: "drew from the bones", KNOCK: "knocked", KEEP: "passed" };
    // One line per move in the turn log (left of your hand, in Band).
    {
      const a = e.action, ct = App.cardTxt, who = me ? "You" : p.name;
      const line = a.type === "SWAP_ONE" && a.undo ? `changed ${me ? "your" : "their"} mind: took back ${ct(prevMid[a.take])}, put ${ct(e.board.middle[a.take])} back`
        : a.type === "SWAP_ONE" ? `took ${ct(prevMid[a.take])}, put down ${ct(e.board.middle[a.take])}`
        : a.type === "TAKE_MIDDLE" ? (prevMid.filter(Boolean).length === 5 ? "took the whole middle" : "took the whole middle blind")
        : a.type === "BLIND" ? "drew blind from the bones"
        : a.type === "REVEAL" ? "turned the middle face up"
        : a.type === "KNOCK" ? (e.board.knockRound === 1 ? "<b class=\"tl-knock\">quocked!</b> (knocked on the first turn)" : "<b class=\"tl-knock\">knocked</b>") : "passed";
      this.turnLog.push(`<span class="tl-who" style="--c:${p.colour}">${who}</span> ${line}`);
    }
    let pause = 0;
    // What each player last took shows on their name box — yours too.
    {
      const a = e.action, ct = App.cardTxt;
      if (a.type === "SWAP_ONE" && a.undo) this.seatTook.delete(e.seat);
      else if (a.type === "SWAP_ONE") this.seatTook.set(e.seat, `Took ${ct(prevMid[a.take])}`);
      else if (a.type === "TAKE_MIDDLE") this.seatTook.set(e.seat, prevMid.filter(Boolean).length === 5 ? `Took the middle` : `Took the middle blind`);
      else if (a.type === "BLIND") this.seatTook.set(e.seat, "Drew blind from the bones");
    }
    if (this.replaying) return;   // catching up after a resume: no animation, the board shows it when done
    if (!me) {
      this.status = `${p.name} ${verb[e.action.type]}.`; this.actions = [];
      const a = e.action, ct = App.cardTxt;
      if (a.type === "SWAP_ONE") {
        const took = prevMid[a.take], left = e.board.middle[a.take];
        this.statusHtml = `<b>${p.name}</b> took ${ct(took)} from the middle and put down ${ct(left)}.`;
        this.midFlash = a.take; pause = 1500;
      } else if (a.type === "TAKE_MIDDLE") {
        const cards = prevMid.filter(Boolean);
        this.statusHtml = cards.length === 5 ? `<b>${p.name}</b> took the whole middle: ${cards.map(ct).join(" ")}.` : `<b>${p.name}</b> took the whole middle, blind.`;
        pause = 1500;
      } else if (a.type === "BLIND") pause = 700;
    }
    if (this.statusHtml) this.statusHtmlFor = this.status;
    this.renderAll();
    this.midFlash = -1; this.statusHtml = this.statusHtmlFor === this.status ? this.statusHtml : "";
    if (pause) await sleep(this.hurry ? Math.min(pause, 250) : pause);
    if (e.action.type === "KNOCK" && e.board.knockRound === 1) {
      await this.quock(e.seat, me ? "You" : p.name, me);
    } else if (e.action.type === "KNOCK") {
      this.playKnock();
      this.showBanner(`${GAVEL} ${me ? "You" : p.name} knocked!`, "One last turn each — then the showdown", { knock: true, ms: 2600 });
    }
  }
  private pendingBreak = { position: 0, handValue: 0, contig: 0, total: 0 };
  // The hands are turned up: show the hand results page FIRST (before any state is picked). Its
  // "Continue to the map" leads on to the claim / the colour-in on the map.
  async onHandReveal(e: { record: HandRecord; board: BoardSnapshot }): Promise<void> {
    this.board = e.board; this.activeSeat = -1; this.actions = []; this.turnOpen = false;
    const r = e.record; this.lastRecord = r; this.records.push(r);
    // provisional totals (the contig bonus, if any, is added once the state is picked)
    this.totalsAfter.set(r.handNumber, e.board.players.map((p) => p.score + (r.results.find((x) => x.seat === p.seat)?.breakdown.total ?? 0)));
    const w = this.players[r.winnerSeat]; const iWon = w.id === "you";
    const mine = r.results.find((x) => this.players[x.seat].id === "you");
    if (mine) this.setHand(mine.cards);
    // Announce a Gurge (nothing hand) only now, when the hands are shown — and only if YOURS is one.
    if (mine && mine.rank.category === HandCategory.Zipper) this.speakGurge(r.handNumber);
    // After a knock, let the player SEE their own final hand (their last swap/draw just landed)
    // for a beat before the results cover the table.
    if (this.replaying) return;   // the results were seen before the save
    if (mine && r.knockerSeat >= 0) { this.renderAll(); await sleep(1700); }
    this.status = `Showdown — ${iWon ? "you win" : w.name + " wins"} with ${CATEGORY_NAME[r.winningCategory]}.`;
    this.renderAll();
    await this.showShowdownModal(r, iWon);   // blocking: "Continue to the map"
  }
  async onShowdown(e: { record: HandRecord; board: BoardSnapshot }): Promise<void> {
    this.board = e.board; this.activeSeat = -1; this.actions = [];
    const r = e.record; this.lastRecord = r;
    // replace the provisional record from onHandReveal with the final one (state + contig bonus)
    if (this.records.length && this.records[this.records.length - 1].handNumber === r.handNumber) this.records[this.records.length - 1] = r; else this.records.push(r);
    // The facts of the hand for the game's record (the server works out the points again itself).
    const facts: SubmitHand = { hand: r.handNumber, dealer: r.dealerSeat, state: r.targetState, winner: r.winnerSeat,
      knocker: r.knockerSeat, knockRound: r.knockerSeat < 0 ? 0 : r.knockRound,
      cards: [0, 1, 2, 3, 4].map((seat) => (r.results.find((x) => x.seat === seat)?.cards ?? []).map(encodeCard)) };
    this.played = [...this.played.filter((x) => x.hand !== r.handNumber), facts];
    this.totalsAfter.set(r.handNumber, e.board.players.map((p) => p.score));   // scores already include this hand (and any peek)
    this.scoredHand = r.handNumber;
    const w = this.players[r.winnerSeat];
    this.pendingCat = r.winningCategory;
    const wr = r.results.find((x) => x.seat === r.winnerSeat)!;
    this.pendingBreak = wr.breakdown;
    // The winning hand as large rank+suit text, grouped exactly like the showdown (trips together, pairs together…)
    this.pendingWinnerCards = `<span class="map-cards">${sortForDisplay(wr.cards, wr.rank.category)
      .map((g) => `<span class="mc-group">${g.map((c) => `<span class="mc${RED(c.suit) ? " red" : ""}">${rankStr(c)}${GLYPH[c.suit]}</span>`).join("")}</span>`).join("")}</span>`;
    // Draw + store the fact now (state is already claimed), so "View board" during
    // the showdown shows the fact immediately — not only after Continue.
    this.pendingFact = nextFact(r.targetState);
    this.pendingQuock = r.knockRound === 1 && r.knockerSeat >= 0 ? (this.players[r.knockerSeat].id === "you" ? "You" : this.players[r.knockerSeat].name) : undefined;
    if (this.pendingFact) {
      this.stateInfo.set(r.targetState, { who: w.name, party: w.party, colour: w.colour, cat: CATEGORY_NAME[r.winningCategory], cards: this.pendingWinnerCards, points: wr.breakdown.total, fact: this.pendingFact, hand: r.handNumber, quock: this.pendingQuock });
      this.browse.delete(r.targetState);
    }
    this.accumulateStats(r);
    this.renderAll();
  }
  // Hand results: Horizontal = one row per player (1st at the top); Vertical = one column per player
  // (1st on the left) with the cards stacked large down each column. Remembered per viewer.
  private sdLayout: "h" | "v" = (() => { try { return localStorage.getItem("poli-sd-layout") === "v" ? "v" : "h"; } catch { return "h"; } })();
  private showdownHtml(r: HandRecord, contig: { name: string; sub: string } | undefined, idx: number, viewer: boolean): string {
    if (COMPACT.matches && !this.sdTable) return this.showdownCompactHtml(r, contig, idx, viewer);
    const vert = this.sdLayout === "v" && !COMPACT.matches;   // a phone held sideways always gets the table
    const totals = this.totalsAfter.get(r.handNumber);   // each player's game total after this hand
    const players = r.results.map((x, i) => {
      const p = this.players[x.seat];
      const groups = sortForDisplay(x.cards, x.rank.category);
      // a phone's table is too small for real faces: plain "9♥" cards there
      const mini = COMPACT.matches ? chipHtml : cardMini;
      const cardsHtml = groups.map((g) => `<div class="sd2-group">${g.map(mini).join("")}</div>`).join('<div class="sd2-gap"></div>');
      const knocked = x.seat === r.knockerSeat;
      const party = `<span class="sd3-party" style="background:${p.colour};color:${inkOn(p.colour)}">${p.party}</span>`;
      // A knock on the knocker's first turn is a quock (the duck itself is kept for the Hall of Fame).
      const knockTag = !knocked ? "" : r.knockRound === 1
        ? `<span class="sd3-knock quock" title="Quocked: knocked on the first turn">${GAVEL} Quocked</span>`
        : `<span class="sd3-knock" title="knocked this hand">${GAVEL} Knocked</span>`;
      const tags = `<span class="sd3-tags">${party}${knockTag}</span>`;
      const name = `<span class="sd3-name">${p.name}${i === 0 ? ' <span class="sd3-trophy">🏆</span>' : ""}</span>`;
      const info = `<div class="sd3-info"><div><div class="sd3-cat">${CATEGORY_NAME[x.rank.category]}</div><div class="sd3-desc">${handDescriptor(x.cards, x.rank.category)}</div></div>` +
        `<div class="sd3-score"><span class="sd3-pts">+${x.breakdown.total}<span class="u"> pts</span></span>` +
        (totals ? `<span class="sd3-total">Total <b>${totals[x.seat]}</b></span>` : "") + `</div></div>`;
      return vert
        ? `<div class="sd3-col${i === 0 ? " win" : ""}">` +
          `<div class="sd3-colhead"><span class="sd3-medal m${i + 1}">${i + 1}</span><div class="sd3-av">${this.avatarEl(p, 72).outerHTML}</div></div>` +
          `<div class="sd3-who">${name}${tags}</div>` +
          `<div class="sd3v-cards">${cardsHtml}</div>` + info + `</div>`
        : `<div class="sd3-row${i === 0 ? " win" : ""}">` +
          `<span class="sd3-medal m${i + 1}">${i + 1}</span>` +
          `<div class="sd3-av">${this.avatarEl(p, 72).outerHTML}</div>` +
          `<div class="sd3-who">${name}${tags}</div>` +
          `<div class="sd2-cards">${cardsHtml}</div>` + info + `</div>`;
    }).join("");
    const n = this.records.length;
    // dots: a window of up to 9 hands around the one shown
    const lo = Math.max(0, Math.min(idx - 4, n - 9)), hi = Math.min(n, lo + 9);
    const dots = Array.from({ length: hi - lo }, (_, k) => `<span class="sd3-dot${lo + k === idx ? " on" : ""}"></span>`).join("");
    const nav = n > 1
      ? `<button class="sd3-btn" id="sd-prev"${idx <= 0 ? " disabled" : ""}>‹ Previous</button>` +
        `<span class="sd3-navlbl">Hand ${r.handNumber} of ${this.records[n - 1]?.handNumber ?? n}<span class="sd3-dots">${dots}</span></span>` +
        `<button class="sd3-btn" id="sd-next"${idx >= n - 1 ? " disabled" : ""}>Next ›</button>`
      : `<span class="sd3-navlbl">Hand ${r.handNumber}</span>`;
    // After the hand: on to the map. Reopened from the map: back to it, or straight on to the next hand.
    const actions = (COMPACT.matches ? `<button class="sd3-btn" id="sd-summary">‹ Back</button>` : `<button class="sd3-btn" data-hof title="Leaderboards, records and every game ever played">🏆 Hall of Fame</button>`) + (viewer
      ? `<button class="sd3-btn" id="sd-map">Back to the map</button>` + (this.rClaimContinue ? `<button class="sd3-btn gold" id="sd-continue">Continue to next hand →</button>` : "")
      : `<button class="sd3-btn gold" id="sd-continue">${COMPACT.matches ? "Continue" : "Continue to the map"} →</button>`);
    return (
      `<div class="sd3${vert ? " vert" : ""}">` +
      `<div class="sd3-view" role="group" aria-label="Layout"><button id="sd-view-h"${vert ? "" : ' class="on"'}>Horizontal</button><button id="sd-view-v"${vert ? ' class="on"' : ""}>Vertical</button></div>` +
      `<button class="sd3-close" id="sd-close2" title="${viewer ? "Close" : "Continue to the map"}">×</button>` +
      // (no title box — the rows/columns say it all, and the cards get the room)
      `<div class="sd3-top"></div>` +
      `<section class="sd3-board"><div class="${vert ? "sd3-cols" : "sd3-rows"}">${players}</div>` +
      (contig ? `<div class="sd2-contig"><span class="sd2-cn-title">🎊 ${contig.name} has a contiguous state!</span><span class="sd2-cn-sub">${contig.sub}</span></div>` : "") +
      `</section>` +
      `<footer class="sd3-foot">${nav}<span class="sd3-sp"></span>${this.peekBtnHtml(r.handNumber, "sd3-btn")}${actions}</footer>` +
      `</div>`
    );
  }
  // Phone held sideways: one player in the spotlight (the winner first) with their cards large, all five
  // in a list on the right (tap one to put them in the spotlight), and "Score details" for the full table.
  private sdTable = false;
  private sdFocus = 0;
  private showdownCompactHtml(r: HandRecord, contig: { name: string; sub: string } | undefined, idx: number, viewer: boolean): string {
    const totals = this.totalsAfter.get(r.handNumber);
    const f = Math.min(this.sdFocus, r.results.length - 1);
    const x = r.results[f], p = this.players[x.seat];
    const knockTag = (seat: number) => seat !== r.knockerSeat ? "" : `<span class="sdc-knock" title="${r.knockRound === 1 ? "Quocked: knocked on the first turn" : "Knocked this hand"}">${GAVEL}</span>`;
    const place = ["1st", "2nd", "3rd", "4th", "5th"][f];
    const title = f === 0 ? `${p.name} wins` : `${p.name} · ${place}`;
    const cards = sortForDisplay(x.cards, x.rank.category).map((g) => `<div class="sdc-group">${g.map((c) => this.card(c, "mid", {}).outerHTML).join("")}</div>`).join("");
    const bk = x.breakdown;
    const why = [`${bk.position} for ${place}`, `${bk.handValue} for the hand`, bk.contig ? `+${bk.contig} contig` : ""].filter(Boolean).join(" · ");
    // the hand's finishing places, 1st at the top (the engine's ranking: suits break every tie)
    const list = r.results.map((y, i) => {
      const q = this.players[y.seat];
      return `<button class="sdc-row${i === f ? " on" : ""}${q.id === "you" ? " me" : ""}" data-sd-row="${i}" type="button" aria-label="${y.position}${["st", "nd", "rd", "th", "th"][y.position - 1]}: ${q.name}">` +
        `<b class="sdc-pos p${y.position}">${y.position}</b><span class="sdc-av">${this.avatarEl(q, 30).outerHTML}</span>` +
        `<span class="sdc-nm">${q.name}${knockTag(y.seat)}</span>` +
        `<span class="sdc-pts">+${y.breakdown.total}${totals ? `<small>Total ${totals[y.seat]}</small>` : ""}</span><span class="sdc-chev">›</span></button>`;
    }).join("");
    const n = this.records.length;
    const nav = `<div class="sdc-hand">${n > 1 ? `<button id="sd-prev" type="button" aria-label="Previous hand"${idx <= 0 ? " disabled" : ""}>‹</button>` : ""}` +
      `<span>${CI.hand}Hand ${r.handNumber}</span>` +
      (n > 1 ? `<button id="sd-next" type="button" aria-label="Next hand"${idx >= n - 1 ? " disabled" : ""}>›</button>` : "") + `</div>`;
    const go = viewer
      ? `<button class="sdc-btn" id="sd-map" type="button">${CI.map}Back to the map</button>` + (this.rClaimContinue ? `<button class="sdc-btn gold" id="sd-continue" type="button">${CI.pass}Next hand</button>` : "")
      : `<button class="sdc-btn gold" id="sd-continue" type="button">${CI.map}Continue</button>`;
    return `<div class="sdc">` +
      `<button class="sd3-close" id="sd-close2" title="${viewer ? "Close" : "Continue to the map"}">×</button>` +
      `<section class="sdc-hero${f === 0 ? " win" : ""}">${nav}` +
      `<div class="sdc-head"><div class="sdc-avatar" style="--c:${p.colour}">${this.avatarEl(p, 76).outerHTML}</div>` +
      `<div><div class="sdc-title">${title}${knockTag(x.seat)}</div><div class="sdc-sub">${CATEGORY_NAME[x.rank.category]} · ${handDescriptor(x.cards, x.rank.category)}</div></div></div>` +
      `<div class="sdc-cards">${cards}</div>` +
      `<div class="sdc-points"><b>+${bk.total} point${bk.total === 1 ? "" : "s"}</b><span>${why}</span></div>` +
      (contig && f === 0 ? `<div class="sdc-contig">🎊 ${contig.name} has a contiguous state! <span>${contig.sub}</span></div>` : "") +
      `<button class="sdc-btn" id="sd-details" type="button">${CI.chart}Score details</button>` +
      `</section>` +
      `<aside class="sdc-side"><div class="sdc-list">${list}</div><div class="sdc-go">${this.peekBtnHtml(r.handNumber, "sdc-btn")}${go}</div></aside>` +
      `</div>`;
  }
  private showShowdownModal(r: HandRecord, _iWon: boolean, elevated = false, contig?: { name: string; sub: string }, viewer = false): Promise<void> {
    const el = $("showdown");
    el.className = "overlay";
    this.sdTable = false; this.sdFocus = 0;
    el.style.zIndex = elevated ? "72" : "";   // sit above the map when reopened from claim review
    return new Promise((res) => {
      const close = () => {
        el.classList.add("hidden"); el.style.zIndex = "";
        // A viewer (opened from "See hand results") never advances the game — it only hides itself,
        // leaving the claim/pick review map underneath intact.
        if (!viewer) {
          this.mapContinue = null;
          if (this.mapOpen && !this.claimMode && !this.claimReview) { this.mapOpen = false; const m = $("map-modal"); m.classList.add("hidden"); m.style.zIndex = ""; }
        }
        res();
      };
      if (elevated && !viewer) this.mapContinue = close;   // lets the board's "Continue" advance through this modal
      const last = this.records.length - 1;
      let idx = this.records.lastIndexOf(r); if (idx < 0) idx = last;
      const render = (i: number) => {
        idx = Math.max(0, Math.min(last, i));
        const rec = this.records[idx] ?? r;
        el.innerHTML = this.showdownHtml(rec, idx === last && !viewer ? contig : undefined, idx, viewer);
        el.querySelectorAll<HTMLElement>("[data-peek]").forEach((b) => (b.onclick = () => this.openPeek(el, +b.dataset.peek!, this.records.find((x) => x.handNumber === +b.dataset.peek!))));
        // Straight after the hand, Continue / × go on to the map. Reopened from the map: "Back to the map"
        // just closes this screen; "Continue to next hand" closes it AND releases the waiting map.
        const cont = document.getElementById("sd-continue"); if (cont) cont.onclick = () => { close(); if (viewer) this.rClaimContinue?.(); };
        ($("sd-close2") as HTMLButtonElement).onclick = () => close();
        const back = document.getElementById("sd-map"); if (back) back.onclick = () => close();
        el.querySelectorAll<HTMLElement>("[data-sd-row]").forEach((b) => (b.onclick = () => { this.sdFocus = +b.dataset.sdRow!; render(idx); }));
        const det = document.getElementById("sd-details"); if (det) det.onclick = () => { this.sdTable = true; render(idx); };
        const sum = document.getElementById("sd-summary"); if (sum) sum.onclick = () => { this.sdTable = false; render(idx); };
        const setView = (v: "h" | "v") => { this.sdLayout = v; try { localStorage.setItem("poli-sd-layout", v); } catch { /* ignore */ } render(idx); };
        const vh = document.getElementById("sd-view-h"); if (vh) vh.onclick = () => setView("h");
        const vv = document.getElementById("sd-view-v"); if (vv) vv.onclick = () => setView("v");
        const prev = document.getElementById("sd-prev"); if (prev) prev.onclick = (e) => { e.stopPropagation(); this.sdFocus = 0; render(idx - 1); };
        const next = document.getElementById("sd-next"); if (next) next.onclick = (e) => { e.stopPropagation(); this.sdFocus = 0; render(idx + 1); };
      };
      // a peek or new scores redraw this screen where it is (an open peek panel stays open)
      this.sdRedraw = (keep) => { if (el.classList.contains("hidden")) return; const p = keep ?? el.querySelector(".peek-panel"); render(idx); if (p) el.appendChild(p); };
      render(idx);
    });
  }
  private mapContinue: (() => void) | null = null;
  async onStateWon(e: { seat: number; state: string; board: BoardSnapshot }): Promise<void> {
    this.board = e.board;
    const p = this.players[e.seat];
    this.noteWin(p.id, e.board.handNumber);
    const bk = this.pendingBreak;
    const contig = bk.contig > 0;
    this.colourAll(e.state, p.colour);
    this.targetState = e.state; this.focusCode = e.state;
    const fact = this.pendingFact;
    if (fact) { this.stateInfo.set(e.state, { who: p.name, party: p.party, colour: p.colour, cat: CATEGORY_NAME[this.pendingCat], cards: this.pendingWinnerCards, points: bk.total, fact, hand: e.board.handNumber, quock: this.pendingQuock }); this.browse.delete(e.state); }
    this.pushLog({ hand: e.board.handNumber, colour: p.colour, who: p.id === "you" ? "You take" : `${p.name} takes`,
      stateName: stateName(e.state), cat: CATEGORY_NAME[this.pendingCat], posPts: bk.position, hv: bk.handValue, contig, total: bk.total });
    const yours = p.id === "you";
    if (this.replaying) { this.renderAll(); return; }   // catching up after a resume: the map was seen before the save
    // Confetti and the crowd cheer are only for a SPECIAL hand (Four of a Kind or better) by anyone —
    // not for a contig.
    if (this.pendingCat >= HandCategory.FourOfAKind) { this.confetti(p.colour, false); this.playCheer(); }

    // Human win: you're on the map (claim review). Stay there with the fact + actions,
    // celebrate a contig right here, and wait for "Continue to next hand".
    if (yours && this.mapOpen) {
      this.renderAll();
      if (contig) {
        this.contigNote = { title: "🎊 You have a contiguous state!", sub: `You claim ${stateName(e.state)} — it borders your territory (+1 bonus)` };
      }
      this.renderMap();          // footer shows review actions + fact (+ contig note if any)
      // your claim gets the same felt-tip colour-in, with the gold "claimed" line at the bottom
      await this.awaitMapContinue(Promise.all([this.colourInState(e.state, p.colour), this.claimBanner("You", "claimed", stateName(e.state))]));
      this.claimReview = false; this.mapOpen = false; this.contigNote = null;
      $("map-modal").classList.add("hidden");
      this.setHand([]); this.handName = "—";   // blank the table before the next hand deals
      this.renderAll();
      return;
    }

    // Otherwise (an AI win, or any win in a random-order game) bring up the board on the won state
    // and wait there: "See hand results" shows the hands, "Continue to next hand" moves on.
    const verb = this.method === "RANDOM" ? (yours ? "win" : "wins") : "chose";
    this.mapOpen = true; this.aiPickNote = { title: `${yours ? "You" : p.name} ${verb} ${stateName(e.state)}`, contig, yours };
    this.renderAll();
    this.renderMap();
    await this.awaitMapContinue(Promise.all([this.colourInState(e.state, p.colour), this.claimBanner(yours ? "You" : p.name, this.method === "RANDOM" ? "won" : "claimed", stateName(e.state))]));
    this.mapOpen = false; this.aiPickNote = null;
    const m = $("map-modal"); m.classList.add("hidden"); m.style.zIndex = "";
    this.setHand([]); this.handName = "—";
    this.renderAll();
  }
  // Waits for "Continue to next hand" on the board. Continue works straight away: pressed while the
  // state is still being coloured in or the banner is still growing, it finishes them at once.
  private fxInk?: Animation;
  private fxBanner?: HTMLElement;
  private markerOut?: GainNode;
  private async awaitMapContinue(fx: Promise<unknown>): Promise<void> {
    const cont = new Promise<void>((res) => { this.rClaimContinue = res; });
    await Promise.race([fx, cont]);
    this.fxInk?.finish();
    this.fxBanner?.remove(); this.fxBanner = undefined;
    try { this.markerOut?.disconnect(); } catch { /* already stopped */ }
    this.markerOut = undefined;
    await cont;
    this.rClaimContinue = undefined;
  }
  // The picked state is coloured in like a felt-tip: back-and-forth scribbles in the player's colour,
  // clipped to the state's outline, drawn over 4s with a squeaky marker sound. Then the plain fill.
  private colouringCode: string | null = null;
  private async colourInState(code: string, colour: string): Promise<void> {
    const svg = this._modalSvg, p = this.modalPaths.get(code);
    if (!svg || !p) return;
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const DUR = reduce ? 600 : 4000;
    this.colouringCode = code; p.style.fill = "";
    const bb = (p as SVGGraphicsElement).getBBox();
    const sw = Math.max(1.4, Math.min(16, Math.min(bb.width, bb.height) / 4.5));   // marker nib
    const step = sw * 0.86, rows = Math.max(3, Math.ceil(bb.height / step) + 2);
    const jit = () => (Math.random() - 0.5) * sw * 0.5;
    let d = "";
    for (let r = 0; r < rows; r++) {
      const y = bb.y + r * step, left = r % 2 === 0;
      const x0 = left ? bb.x - sw : bb.x + bb.width + sw, x1 = left ? bb.x + bb.width + sw : bb.x - sw;
      // each stroke leans a little, like a hand going back and forth
      d += `${r ? "L" : "M"}${(x0 + jit()).toFixed(1)} ${(y + jit() + sw * 1.1).toFixed(1)} L${(x1 + jit()).toFixed(1)} ${(y + jit() - sw * 1.1).toFixed(1)} `;
    }
    const id = `colour-clip-${code}`;
    const g = svgEl("g", { class: "colour-in" });
    const clip = svgEl("clipPath", { id }); clip.appendChild(svgEl("path", { d: p.getAttribute("d") ?? "" }));
    g.appendChild(clip);
    const inner = svgEl("g", { "clip-path": `url(#${id})` });
    const ink = svgEl("path", { d, fill: "none", stroke: colour, "stroke-width": String(sw), "stroke-linecap": "round", "stroke-linejoin": "round", "stroke-opacity": "0.8" });   // see-through ink: overlaps go darker, like a real texta
    inner.appendChild(ink); g.appendChild(inner);
    const last = [...this.modalPaths.values()].pop()!;
    svg.insertBefore(g, last.nextSibling);
    const len = (ink as SVGPathElement).getTotalLength();
    ink.setAttribute("stroke-dasharray", `${len}`); ink.setAttribute("stroke-dashoffset", `${len}`);
    if (!reduce) this.playMarker(DUR, rows);
    await new Promise<void>((res) => {
      const a = ink.animate([{ strokeDashoffset: len }, { strokeDashoffset: 0 }], { duration: DUR, easing: "linear", fill: "forwards" });
      a.onfinish = () => res(); a.oncancel = () => res();
      this.fxInk = a;
    });
    this.fxInk = undefined;
    p.style.fill = colour; this.colouringCode = null;
    g.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 250 }).onfinish = () => g.remove();
  }
  // "Rhett claimed Tennessee" in large gold letters along the bottom of the map: it grows from small
  // to very large over ~6.8s with a few sparkles around it, then fades. The end size is as big as the
  // map's width allows (up to 1.9× the starting full size).
  private claimBanner(name: string, verb: string, state: string): Promise<void> {
    const wrap = document.querySelector<HTMLElement>("#map-modal .map-svg-wrap"); if (!wrap) return Promise.resolve();
    const r = wrap.getBoundingClientRect();
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Phone map: the board fills the screen behind the bottom button bar, so sit the line just above
    // that bar (in the gap under the map) instead of behind it.
    const bar = document.querySelector<HTMLElement>("#map-modal .cm-bar");
    const base = bar ? bar.getBoundingClientRect().top - 6 : r.bottom - 18;
    const el = h("div", { class: "claim-fx" + (reduce ? " still" : ""), style: `left:${r.left + r.width / 2}px;top:${base}px;max-width:${r.width - 24}px` }, [
      h("div", { class: "cf-text", html: `${name} <i>${verb}</i> ${state}` }),
    ]);
    for (let i = 0; i < 12; i++)
      el.appendChild(h("span", { class: "cf-spark", text: i % 3 ? "✦" : "✧",
        style: `left:${5 + Math.random() * 90}%;top:${-40 + Math.random() * 100}%;animation-delay:${300 + Math.random() * 5600}ms;font-size:${18 + Math.random() * 20}px` }));
    $("map-modal").appendChild(el); this.fxBanner = el;
    const text = el.querySelector<HTMLElement>(".cf-text")!;
    el.style.setProperty("--cf-end", String(Math.max(1, Math.min(1.9, (r.width - 40) / Math.max(1, text.scrollWidth)))));
    return sleep(reduce ? 1500 : 6800).then(() => el.remove());
  }
  // Squeaky felt-tip: one squeak per stroke — a high, wobbling tone through a narrow filter, plus a
  // little scratchy noise, swelling and fading with each pass of the pen.
  private playMarker(totalMs: number, strokes: number): void {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx ||= new Ctx();
      const ctx = this.audioCtx;
      if (ctx.state === "suspended") void ctx.resume();
      const per = totalMs / 1000 / strokes;
      const out = ctx.createGain(); out.gain.value = 0.55; out.connect(ctx.destination); this.markerOut = out;
      for (let i = 0; i < strokes; i++) {
        const t = ctx.currentTime + i * per, dur = per * (0.7 + Math.random() * 0.2);
        const f = 1900 + Math.random() * 900;
        const osc = ctx.createOscillator(); osc.type = "sawtooth";
        osc.frequency.setValueAtTime(f, t); osc.frequency.linearRampToValueAtTime(f * (i % 2 ? 0.86 : 1.12), t + dur);
        const lfo = ctx.createOscillator(); lfo.frequency.value = 38 + Math.random() * 20;
        const lfoAmt = ctx.createGain(); lfoAmt.gain.value = 140; lfo.connect(lfoAmt).connect(osc.frequency);
        const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = f * 1.3; bp.Q.value = 9;
        const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.32, t + dur * 0.25); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        osc.connect(bp).connect(g).connect(out);
        // scratch of the nib on paper
        const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate), dd = buf.getChannelData(0);
        for (let k = 0; k < dd.length; k++) dd[k] = (Math.random() * 2 - 1) * 0.5;
        const src = ctx.createBufferSource(); src.buffer = buf;
        const hp = ctx.createBiquadFilter(); hp.type = "bandpass"; hp.frequency.value = 5200; hp.Q.value = 1.2;
        const ng = ctx.createGain(); ng.gain.setValueAtTime(0.0001, t); ng.gain.exponentialRampToValueAtTime(0.12, t + dur * 0.3); ng.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        src.connect(hp).connect(ng).connect(out);
        osc.start(t); lfo.start(t); src.start(t); osc.stop(t + dur + 0.02); lfo.stop(t + dur + 0.02);
      }
    } catch { /* audio unavailable */ }
  }
  private contigNote: { title: string; sub: string } | null = null;
  private aiPickNote: { title: string; contig: boolean; yours: boolean } | null = null;
  private lastStandings: { name: string; party: string; colour: string; score: number; states: string[] }[] = [];
  onGameEnd(standings: any[]): void {
    this.gameActive = false;
    if (!this.remote) this.removeSave(this.matchKey);
    this.lastStandings = standings;
    const rows = standings.map((p, i) =>
      `<div class="rank-row"><span class="no">${i + 1}</span>` +
      `<div class="nm" style="display:flex;align-items:center;gap:7px"><span class="dot" style="background:${p.colour}"></span>${p.name}</div>` +
      `<span class="stc">${p.states.length}</span><span class="pts">${p.score}</span></div>`).join("");
    $("end-title").textContent = `🏆 ${standings[0].name} wins with ${standings[0].score} points!`;
    const cap = this.president?.capital;
    $("end-results").innerHTML = `<p class="end-pres">🏛️ <b>${standings[0].name}</b> is President${cap ? ` · <span style="white-space:nowrap">capital <b>${capitalPlace(cap)}</b> ★</span>` : ""}</p>` + rows;
    music.hush(true);                                      // the game's songs fade out under the final results
    this.openEndScreen();
    if (this.remote) $("end-record").textContent = "The table records this game.";
    else void this.recordFinishedGame();
  }
  openEndScreen(): void {
    $("end-reopen").hidden = true;
    $("endscreen").classList.remove("hidden");
    ($("end-x") as HTMLButtonElement).focus();
  }
  /** Close the final results to look over the board; a button brings them back. */
  closeEndScreen(): void {
    if ($("endscreen").classList.contains("hidden")) return;
    $("endscreen").classList.add("hidden");
    $("end-reopen").hidden = false;
    stopAnthem();                                          // the anthem fades out within a second
  }
  /** Send the finished game to the Hall of Fame (Human vs AI). Signed out, it waits until you sign in. */
  private async recordFinishedGame(): Promise<void> {
    const note = $("end-record");
    const hands = [...this.played].sort((a, b) => a.hand - b.hand);
    if (hands.length !== MATCH_HANDS) { note.textContent = "This game was started before games were recorded, so it can't go on the Hall of Fame."; return; }
    const m: SubmitMatch = {
      clientId: this.matchKey, stateMethod: this.method === "RANDOM" ? "RANDOM" : "CHOOSE", startedAt: this.startedAt, completedAt: Date.now(),
      seats: this.players.map((p, i) => ({ name: p.name, party: p.party, colour: p.colour, ai: p.isHuman ? null : this.aiProfiles[i] ?? "basic" })),
      hands,
      peeks: (this.game?.peeks ?? []).map((p) => ({ hand: p.hand, seat: p.seat, card: encodeCard(p.card) })),
      capital: this.president?.capital ?? null,
    };
    note.textContent = "Recording this game for the Hall of Fame…";
    const r = await recordMatch(m);
    if (r.recorded) hofChanged();
    note.textContent = r.message;
  }

  // ---- PDF export of the final map + scores ----
  private svgToPng(svg: SVGSVGElement, width: number): Promise<string> {
    return new Promise((resolve) => {
      const xml = new XMLSerializer().serializeToString(svg);
      const src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
      const img = new Image();
      img.onload = () => {
        const vb = svg.viewBox.baseVal;
        const ar = vb && vb.height ? vb.width / vb.height : 1028 / 746;
        const w = width, hgt = Math.round(width / ar);
        const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = hgt;
        const ctx = canvas.getContext("2d")!;
        ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, hgt); // white so a printed copy doesn't waste ink
        ctx.drawImage(img, 0, 0, w, hgt);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve("");
      img.src = src;
    });
  }
  async savePdf(): Promise<void> {
    const note = document.getElementById("pdf-note");
    const JsPDF = (window as unknown as { jspdf?: { jsPDF: new (o: object) => any } }).jspdf?.jsPDF;
    if (!JsPDF) { if (note) note.textContent = "Couldn't load the PDF library — check your connection and retry."; return; }
    const s = this.lastStandings;
    const doc = new JsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFont("times", "bold"); doc.setFontSize(24); doc.setTextColor(40, 30, 20);
    doc.text("Poli Poker — Final Results", 40, 54);
    doc.setFont("times", "normal"); doc.setFontSize(12); doc.setTextColor(90, 80, 70);
    doc.text(`${s[0]?.name ?? ""} wins with ${s[0]?.score ?? 0} points   ·   ${new Date().toLocaleDateString()}`, 40, 76);
    const live = document.querySelector("#mini-map svg") as SVGSVGElement | null;
    if (live) {
      // Print-friendly copy: unclaimed states light grey on white instead of dark on black.
      const svg = live.cloneNode(true) as SVGSVGElement;
      svg.querySelectorAll("path").forEach((p) => {
        const f = (p as SVGPathElement).style.fill.replace(/\s/g, "");
        if (!f || f === "#332e2a" || f === "rgb(51,46,42)") (p as SVGPathElement).style.fill = "#ece8e1";
        p.setAttribute("stroke", "#7d756b"); p.setAttribute("stroke-width", "0.8");
      });
      const png = await this.svgToPng(svg, 1500);
      if (png) doc.addImage(png, "PNG", 40, 96, 500, 363);
    }
    let y = 112; const x = 568, xr = 802;
    doc.setFont("times", "bold"); doc.setFontSize(16); doc.setTextColor(40, 30, 20); doc.text("Standings", x, y); y += 20;
    doc.setFontSize(10); doc.setTextColor(110, 100, 90);
    doc.text("Points", xr - 52, y, { align: "right" }); doc.text("States won", xr, y, { align: "right" }); y += 6;
    doc.setDrawColor(190, 180, 168); doc.line(x, y, xr, y); y += 16;
    s.forEach((p, i) => {
      const [r, g, b] = this.hexRgb(p.colour);
      doc.setFillColor(r, g, b); doc.rect(x, y - 8, 9, 9, "F");
      doc.setFont("times", "bold"); doc.setFontSize(12.5); doc.setTextColor(30, 25, 20);
      doc.text(`${i + 1}.  ${p.name}`, x + 16, y);
      doc.text(`${p.score}`, xr - 52, y, { align: "right" });
      doc.text(`${p.states.length}`, xr, y, { align: "right" });
      y += 13;
      doc.setFont("times", "normal"); doc.setFontSize(9.5); doc.setTextColor(95, 85, 75);
      const names = p.states.length ? [...p.states].map((c) => stateName(c)).sort().join(", ") : "No states";
      const lines = doc.splitTextToSize(`${p.party} · ${names}`, xr - x - 16);
      doc.text(lines, x + 16, y); y += lines.length * 11 + 9;
    });
    doc.setFontSize(10); doc.setTextColor(120, 110, 100);
    doc.text("Winner = most points (finish + hand value + contiguous bonus). © Chris Puccetti — Political Poker.", 40, 560);
    doc.save(`poli-poker-${new Date().toISOString().slice(0, 10)}.pdf`);
    if (note) note.textContent = "Saved to your browser's Downloads folder.";
  }
  private hexRgb(hex: string): [number, number, number] {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [128, 128, 128];
  }

  // ---- season stats ----
  private statFor(pl: { id: string; name: string; colour: string; party: string }) {
    let s = this.stats.get(pl.id);
    if (!s) { s = { name: pl.name, colour: pl.colour, party: pl.party, wins: 0, knocks: 0, knockWins: 0, knockR: [0, 0, 0], contigs: 0, cats: new Array(10).fill(0) }; this.stats.set(pl.id, s); }
    else { s.name = pl.name; s.colour = pl.colour; s.party = pl.party; }
    return s;
  }
  private accumulateStats(r: HandRecord): void {
    this.handsPlayed = Math.max(this.handsPlayed, r.handNumber);
    for (const res of r.results) this.statFor(this.players[res.seat]).cats[res.rank.category]++;
    const wr = r.results.find((x) => x.seat === r.winnerSeat);
    const ws = this.statFor(this.players[r.winnerSeat]);
    ws.wins++;
    if (wr && wr.breakdown.contig > 0) ws.contigs++;
    if (r.knockerSeat >= 0) {
      const ks = this.statFor(this.players[r.knockerSeat]);
      ks.knocks++;
      const bucket = r.knockRound <= 1 ? 0 : r.knockRound === 2 ? 1 : 2;
      ks.knockR[bucket]++;
      if (r.knockerSeat === r.winnerSeat) ks.knockWins++;
    }
  }
  openHandLog(): void {
    const modal = $("log-modal");
    modal.className = "map-backdrop";
    const list = h("div", { class: "log-page" });
    if (!this.history.length)
      list.appendChild(h("div", { class: "muted", style: "font-size:15px;padding:12px 0", text: "No hands played yet — the log fills as states are won." }));
    for (const e of this.history) {
      list.appendChild(h("div", { class: "log2" + (e.contig ? " win" : "") }, [
        h("div", { class: "hn" }, [h("span", { class: "num", text: String(e.hand) }), h("span", { class: "lbl", text: "hand" })]),
        h("div", { class: "body" }, [
          h("div", { class: "row1" }, [
            h("span", { class: "chip", style: `background:${e.colour}` }),
            h("span", { class: "who", text: `${e.who} ${e.stateName}` }),
            h("span", { class: "tag2", text: e.cat }),
          ]),
          h("div", { class: "dt", html: `${e.posPts} finish + ${e.hv} hand${e.contig ? ' + <span style="color:var(--a300)">1 border 🎗️</span>' : ""} = <b style="color:var(--n200)">${e.total}</b> pts` }),
        ]),
      ]));
    }
    modal.innerHTML = "";
    modal.appendChild(h("div", { class: "map-shell log-shell", on: { click: (e) => e.stopPropagation() } }, [
      h("div", { style: "display:flex;align-items:baseline;justify-content:space-between;border-bottom:1px solid var(--n800);padding-bottom:var(--s2)" }, [
        h("div", {}, [h("div", { class: "kicker", text: "Hand log" }), h("div", { class: "sheet-title", text: `${this.history.length} ${this.history.length === 1 ? "hand" : "hands"} played` })]),
        h("button", { style: "background:none;border:none;color:var(--n200);font-size:30px;font-weight:700;line-height:1;cursor:pointer;padding:0 6px", text: "×", attr: { title: "Close" }, on: { click: () => this.closeHandLog() } }),
      ]),
      list,
    ]));
    modal.onclick = () => this.closeHandLog();
  }
  closeHandLog(): void { $("log-modal").classList.add("hidden"); }

  openStats(): void {
    const modal = $("stats-modal");
    modal.className = "map-backdrop";
    const players = this.board?.players ?? [];
    const seatOrder = players.map((p) => p.id);
    const ranked = [...players].sort((a, b) => b.score - a.score || b.states.length - a.states.length);
    const bestCat = (cats: number[]) => { for (let i = cats.length - 1; i >= 0; i--) if (cats[i] > 0) return CATEGORY_NAME[i]; return "—"; };
    const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) + "%" : "—");

    const rowFor = (p: PublicPlayer, i: number) => {
      const s = this.statFor(p);
      return h("div", { class: "st-row" }, [
        h("div", { class: "st-name" }, [
          h("span", { class: "st-rank", text: String(i + 1) }),
          h("span", { class: "dot", style: `background:${p.colour}` }),
          h("div", { style: "display:flex;flex-direction:column;min-width:0" }, [
            h("span", { style: "font-size:16px;color:var(--n100)", text: p.name }),
            h("span", { style: "font-size:11px;color:var(--n500)", text: p.party }),
          ]),
        ]),
        h("span", { class: "st-num big", text: String(p.score) }),
        h("span", { class: "st-num", text: String(p.states.length) }),
        h("span", { class: "st-num", text: String(s.wins) }),
        h("span", { class: "st-num", text: `${s.knockWins}/${s.knocks}` }),
        h("span", { class: "st-num", text: pct(s.knockWins, s.knocks) }),
        h("span", { class: "st-num", text: `${s.knockR[0]} · ${s.knockR[1]} · ${s.knockR[2]}` }),
        h("span", { class: "st-num", text: String(s.contigs) }),
        h("span", { class: "st-num", style: "font-size:13px", text: bestCat(s.cats) }),
      ]);
    };
    const header = h("div", { class: "st-row st-head" }, [
      h("span", { text: "Player" }),
      h("span", { class: "st-num", text: "Points" }),
      h("span", { class: "st-num", text: "States" }),
      h("span", { class: "st-num", text: "Hands won" }),
      h("span", { class: "st-num", text: "Knock W/A" }),
      h("span", { class: "st-num", text: "Knock %" }),
      h("span", { class: "st-num", text: "Quocks · rd 2 · 3+" }),
      h("span", { class: "st-num", text: "Contig +1" }),
      h("span", { class: "st-num", text: "Best hand" }),
    ]);

    void seatOrder;
    modal.innerHTML = "";
    modal.appendChild(h("div", { class: "map-shell", on: { click: (e) => e.stopPropagation() } }, [
      h("div", { style: "display:flex;align-items:baseline;justify-content:space-between;border-bottom:1px solid var(--n800);padding-bottom:var(--s2)" }, [
        h("div", {}, [h("div", { class: "kicker", text: "Season stats" }), h("div", { class: "sheet-title", text: `Through ${this.handsPlayed} ${this.handsPlayed === 1 ? "hand" : "hands"}` })]),
        h("button", { style: "background:none;border:none;color:var(--n200);font-size:30px;font-weight:700;line-height:1;cursor:pointer;padding:0 6px", text: "×", attr: { title: "Close" }, on: { click: () => this.closeStats() } }),
      ]),
      h("div", { class: "st-table" }, [header, ...ranked.map(rowFor)]),
      h("button", { class: "map-hof-btn st-hof", text: "🏆 All-time leaderboards and every game: Hall of Fame →", attr: { "data-hof": "" } }),
      h("div", { class: "st-note", text: "Winner = most points (finish + hand value + contiguous bonus), not most states. Knock W/A = hands won when you knocked, out of times you knocked. Knock rd shows how often each player knocked on the 1st · 2nd · 3rd-or-later round of a hand." }),
    ]));
    modal.onclick = () => this.closeStats();
  }
  closeStats(): void { $("stats-modal").classList.add("hidden"); }

  // ---- human turn ----
  private handOrderHand = 0;                 // hand number the current arrangement belongs to
  private manualOrder = false;               // you dragged your cards this hand: stop auto-sorting
  private setHand(cards: Card[]): void {
    this.yourHand = cards;
    if (!cards.length) this.manualOrder = false;
    this.handOrder = this.manualOrder && this.isPerm(cards) ? this.handOrder : this.autoOrder(cards);
  }
  private isPerm(cards: Card[]): boolean {
    return this.handOrder.length === cards.length && [...this.handOrder].sort((a, b) => a - b).every((v, i) => v === i);
  }
  // Best arrangement left to right: pairs/trips grouped (highest first), straights in sequence,
  // flushes and high cards by rank — the same order the hand results use.
  private autoOrder(cards: Card[]): number[] {
    if (cards.length < 5) return cards.map((_, i) => i);
    return sortForDisplay(cards, evaluate(cards).category).flat().map((c) => cards.indexOf(c));
  }
  // The engine swaps/draws in place (index stable), so a dragged permutation stays valid across
  // rounds of the SAME hand. Until you drag, every change re-sorts the hand. Reset on a new deal.
  private syncHand(cards: Card[], handNumber: number): void {
    this.yourHand = cards;
    if (handNumber !== this.handOrderHand) { this.manualOrder = false; this.handOrderHand = handNumber; }
    if (!this.manualOrder || !this.isPerm(cards)) this.handOrder = this.autoOrder(cards);
  }

  // Your five cards arrive with everyone else's, before the first player acts.
  async receiveDeal(hand: Card[], handNumber: number): Promise<void> {
    this.syncHand(hand, handNumber);
    this.dealtYouHand = handNumber; this.freshDeal = true; this.playDeal(5, 230);
    this.handName = CATEGORY_NAME[evaluate(hand).category];
    this.renderAll();
    await sleep(5 * 230 + 400);   // let the deal animation finish before play starts
  }

  // Your move is in: clear your buttons so they never linger once you've played.
  closeTurn(): void { this.turnOpen = false; this.actions = []; this.renderAll(); }

  // Your exchange just landed: show the new hand at once, re-sorted into its best order.
  receiveOwnHand(hand: Card[]): void {
    this.manualOrder = false;
    this.syncHand(hand, this.handOrderHand);
    this.handName = CATEGORY_NAME[evaluate(hand).category];
    this.renderAll();
  }

  beginYourTurn(v: TurnView): void {
    this.human.view = v; this.syncHand(v.hand, v.handNumber); this.activeSeat = 0; this.mode = "idle"; this.giveIndex = -1;
    this.turnOpen = true;
    if (v.handNumber !== this.dealtYouHand) { this.dealtYouHand = v.handNumber; this.freshDeal = true; this.playDeal(5, 230); }
    const cat = evaluate(v.hand).category;
    this.handName = CATEGORY_NAME[cat];
    const L = new Set(v.legal);
    const A: typeof this.actions = [];
    // Swapping (drag a card onto the middle) and the bones (drag from the pile onto a card) are drags
    // on the table; the buttons are for the moves that aren't.
    if (L.has("REVEAL")) A.push({ label: "Turn the middle face up", primary: true, side: "l", onClick: () => this.human.submit({ type: "REVEAL" }) });
    // Taking happens at once — no "are you sure" (only knocking asks).
    if (L.has("TAKE_MIDDLE")) A.push({ label: v.middleRevealed ? "Take all five" : "Take all five blind", side: "l", onClick: () => this.human.submit({ type: "TAKE_MIDDLE" }) });
    if (L.has("KNOCK")) A.push({ label: "Knock", side: "r", onClick: () => this.human.submit({ type: "KNOCK" }) });
    if (L.has("KEEP")) A.push({ label: "Pass", onClick: () => this.human.submit({ type: "KEEP" }) });
    this.actions = A;
    this.status = "";
    this.renderAll();
  }
  /** Someone has knocked (only one knock a hand): this is your last turn. */
  private lastTurnNote(): string {
    const k = this.board?.players.find((p) => p.knocked);
    return k ? `${k.id === "you" ? "You" : k.name} knocked, so this is your last turn.` : "";
  }
  askKnock(v: TurnView): void {
    this.human.view = v; this.syncHand(v.hand, v.handNumber); this.mode = "knock"; this.giveIndex = -1;
    this.turnOpen = true;
    this.handName = CATEGORY_NAME[evaluate(v.hand).category];
    this.actions = [
      { label: "End turn", primary: true, onClick: () => this.human.submitKnock(false) },
      { label: "Knock", side: "r", onClick: () => this.human.submitKnock(true) },
    ];
    this.status = "";
    this.renderAll();
  }
  // After swapping one card with the middle: put that same card back (once), knock, or end the turn.
  askAfterSwap(v: TurnView, opts: { canUndo: boolean; canKnock: boolean }): void {
    this.human.view = v; this.syncHand(v.hand, v.handNumber); this.mode = "knock"; this.giveIndex = -1;
    this.turnOpen = true;
    this.handName = CATEGORY_NAME[evaluate(v.hand).category];
    const A: typeof this.actions = [{ label: "End turn", primary: true, onClick: () => this.human.submitAfterSwap("END") }];
    if (opts.canUndo) A.push({ label: "Swap it back", side: "l", onClick: () => this.human.submitAfterSwap("UNDO") });
    if (opts.canKnock) A.push({ label: "Knock", side: "r", onClick: () => this.human.submitAfterSwap("KNOCK") });
    this.actions = A;
    this.status = opts.canKnock ? "" : this.lastTurnNote();
    this.renderAll();
  }
  // Drops happen at once — no "are you sure": a wrong drop is bad luck, as at the real table.
  private dropSwap(give: number, take: number): void { if (this.human.view?.middle[take]) this.human.submit({ type: "SWAP_ONE", give, take }); }
  private dropBones(give: number): void { this.human.submit({ type: "BLIND", give }); }
  private canDragSwap(): boolean { return this.turnOpen && this.mode === "idle" && !!this.human.view?.legal.includes("SWAP_ONE"); }
  private canDragBones(): boolean { return this.turnOpen && this.mode === "idle" && !!this.human.view?.legal.includes("BLIND"); }

  /** The topmost element under the pointer that matches `sel` (looks through the card being dragged). */
  private static under(x: number, y: number, sel: string): HTMLElement | null {
    for (const el of document.elementsFromPoint(x, y)) { const m = (el as HTMLElement).closest<HTMLElement>(sel); if (m) return m; }
    return null;
  }
  private static markDrop(sel: string, el: HTMLElement | null): void {
    document.querySelectorAll(`${sel}.drop-target`).forEach((x) => { if (x !== el) x.classList.remove("drop-target"); });
    el?.classList.add("drop-target");
  }
  enterMapSelect(unclaimed: string[]): void {
    this.mode = "map-select"; this.claimMode = true; this.mapOpen = true;
    this.selectable = new Set(unclaimed);
    this.status = "You won the hand — pick an open state on the board.";
    this.actions = [];
    this.renderAll(); this.renderMap();
  }

  // ---- rendering ----
  renderAll(): void {
    document.body.classList.toggle("compact", COMPACT.matches);
    if (COMPACT.matches) { this.renderHeader(); this.renderCompact(); return; }
    this.renderHeader(); this.renderTable(); this.renderSheet();
  }

  /**
   * A phone held sideways (or any short landscape screen): the whole game on one screen, no scrolling.
   * A slim bar on top (hand, whose turn, the dealer, the board, the players, a menu), the felt with the
   * middle, the bones and your hand, and your moves in a panel on the right, over the theme's room.
   * Swap card / Draw bones (or tapping a card) pick the card you give away; then tap a middle card or the bones.
   */
  private cmpPop: "" | "players" | "menu" | "music" = "";
  private cmpIntent: "" | "swap" | "bones" = "";
  private renderCompact(): void {
    const host = $("table-host");
    host.innerHTML = "";
    const b = this.board; if (!b) return;
    const fresh = b.handNumber !== this.dealtHand;
    if (fresh) { this.dealtHand = b.handNumber; this.prevHandKey = ""; }
    const dealer = b.dealerSeat ?? 0;
    const canSwap = this.canDragSwap(), canBones = this.canDragBones(), picking = canSwap || canBones;
    if (!picking) { this.giveIndex = -1; this.cmpIntent = ""; }
    if (this.cmpIntent === "swap" && !canSwap || this.cmpIntent === "bones" && !canBones) this.cmpIntent = "";
    const pick = this.giveIndex;
    const redraw = () => this.renderAll();
    const giveToBones = (gi: number) => { this.giveIndex = -1; this.cmpIntent = ""; this.dropBones(gi); };

    // top bar: hand, whose turn, the dealer, then the board, the players and a menu
    const who = this.turnOpen ? "Your turn" : this.mode === "map-select" ? "Pick your state" : this.activeSeat > 0 ? `${b.players[this.activeSeat]?.name}'s turn` : "";
    const knocker = b.players.find((p) => p.knocked);
    const pop = (k: "players" | "menu" | "music") => () => { this.cmpPop = this.cmpPop === k ? "" : k; redraw(); };
    const songs = true;   // every table has music: its theme's songs, or the mix of them all
    const barBtn = (label: string, ico: string, onClick: () => void, extra = "") => h("button", { class: "cb-btn" + extra, attr: { type: "button", "aria-label": label },
      html: `${ico}<span class="cb-lbl">${label}</span>`, on: { click: onClick } });
    const top = h("div", { class: "cb-top" }, [
      h("span", { class: "cb-hand tnum", html: `${CI.hand}Hand <b>${b.handNumber}</b> / 50` }),
      who ? h("span", { class: "cb-turn" + (this.turnOpen || this.mode === "map-select" ? " mine" : ""), html: `<i></i>${who}` }) : null,
      h("span", { class: "cb-sp" }),
      h("span", { class: "cb-dealer", text: `Dealer: ${b.players[dealer].name}` }),
      barBtn("Map", CI.map, () => $("board-btn").click()),
      barBtn("Players", CI.players, pop("players"), (this.cmpPop === "players" ? " on" : "") + (knocker ? " knock" : "")),
      songs ? h("button", { class: "cb-btn cb-spk" + (this.cmpPop === "music" ? " on" : ""), attr: { type: "button", "aria-label": music.audible ? "Music" : "Music (muted)" },
        html: music.audible ? ICON_ON : ICON_OFF, on: { click: pop("music") } }) : null,
      h("button", { class: "cb-btn cb-menu" + (this.cmpPop === "menu" ? " on" : ""), attr: { type: "button", "aria-label": "Menu" }, html: CI.menu, on: { click: pop("menu") } }),
    ]);

    // pop-ups from the bar: the five players' scores, and the menu
    let popup: HTMLElement | null = null;
    if (this.cmpPop === "players") {
      popup = h("div", { class: "cb-pop cb-players" }, b.players.map((p, seat) => {
        const knock = p.knocked ? (b.knockRound === 1 ? "Quocked" : "Knocked") : "";
        return h("div", { class: "cb-p" + (this.activeSeat === seat ? " active" : "") + (seat === 0 ? " me" : ""), style: `--c:${p.colour}` }, [
          h("span", { class: "cb-dot" }),
          h("span", { class: "cb-nm", text: p.name }),
          seat === dealer ? h("span", { class: "cb-tag", text: "Dealer" }) : null,
          knock ? h("span", { class: "cb-tag knock", html: `${GAVEL}${knock}` }) : null,
          h("span", { class: "cb-sc tnum", html: `${p.score} <small>pts</small>` }),
          h("span", { class: "cb-st tnum", text: `${p.states.length} ${p.states.length === 1 ? "state" : "states"}` }),
        ]);
      }));
    } else if (this.cmpPop === "menu") {
      const item = (label: string, ico: string, run: () => void) => h("button", { class: "cb-mi", attr: { type: "button" }, html: `<span aria-hidden="true">${ico}</span>${label}`,
        on: { click: () => { this.cmpPop = ""; redraw(); run(); } } });
      popup = h("div", { class: "cb-pop cb-menu-pop" }, [
        item("Scores", "📊", () => $("stats-btn").click()), item("Every move this hand", "📜", () => this.openHandLog()),
        item("Music", "🎵", () => { this.cmpPop = "music"; redraw(); }),
        item("Hall of Fame", "🏆", () => $("hof-btn").click()), item("Theme", "🎨", () => $("theme-btn").click()), item("Rules", "❔", () => $("info-btn").click()),
        item(this.remote ? "Exit (the table carries on)" : "Save & exit", "💾", () => this.openLeave()),
        this.remote ? null : item("Abandon game", "🗑", () => { this.openLeave(); ($("leave-end") as HTMLButtonElement).click(); }),
      ]);
    } else if (this.cmpPop === "music") {
      // the same as the PC's music panel: volume, mute, and the song that's playing with its story
      const n = songs && music.nowPlaying() ? songNote(music.nowPlaying()!) : null;
      const pct = h("output", { class: "cb-pct", text: music.prefs.muted ? "Muted" : `${Math.round(music.prefs.volume * 100)}%` });
      const vol = h("input", { attr: { type: "range", min: "0", max: "100", step: "1", value: String(Math.round(music.prefs.volume * 100)), "aria-label": "Music volume" } }) as HTMLInputElement;
      vol.addEventListener("input", () => { music.setVolume(Number(vol.value) / 100); pct.textContent = music.prefs.muted ? "Muted" : `${vol.value}%`; });
      popup = h("div", { class: "cb-pop cb-music" }, songs ? [
        h("div", { class: "cb-mrow" }, [h("span", { class: "cb-k", text: "Music" }), pct]),
        h("div", { class: "cb-mrow" }, [
          h("button", { class: "cb-mute", attr: { type: "button", "aria-pressed": String(!music.audible) }, html: `${music.audible ? ICON_ON : ICON_OFF}<span>${music.prefs.muted || music.prefs.volume === 0 ? "Unmute" : "Mute"}</span>`,
            on: { click: () => { music.toggleMute(); redraw(); } } }),
          vol,
        ]),
        h("div", { class: "cb-song" }, [
          h("span", { class: "cb-k", text: "Now playing" }),
          n ? h("p", {}, [h("b", { text: `“${n.title}”` }), n.year ? `, ${n.year}.` : n.text ? "." : "", n.text ? ` ${n.text}` : ""]) : h("p", { class: "dim", text: music.audible ? "Starting the next song…" : "Nothing: the music is muted." }),
          n?.source ? h("a", { attr: { href: n.source.url, target: "_blank", rel: "noopener" }, text: `Source: ${n.source.label} ↗` }) : null,
        ]),
        songList(() => redraw()),
      ] : [h("p", { class: "cb-song dim", text: "This table theme has no music. Las Vegas, Wild West Saloon, Roaring Twenties and Orient Express have songs." })]);
    }

    // the middle (tap a card to swap the one you picked) and the bones (tap to draw)
    const midLive = canSwap && pick >= 0;
    const cells: (HTMLElement | null)[] = [];
    b.middle.forEach((c, i) => {
      const el = this.card(c, "mid", { deal: fresh, dealIndex: i, dealGapMs: 170 });
      el.dataset.mid = String(i);
      if (i === this.midFlash) el.classList.add("just-left");
      if (midLive && c) { el.classList.add("click", "target"); el.addEventListener("click", () => { this.giveIndex = -1; this.cmpIntent = ""; this.dropSwap(pick, i); }); }
      cells.push(el);
    });
    if (fresh) this.playDeal(5, 170);
    const left = b.stockCount + b.discardCount;
    const bonesLive = canBones && pick >= 0;
    const bones = h("button", { class: "cb-bones" + (bonesLive ? " live" : ""), attr: { type: "button", "aria-label": `The bones, ${left} cards`, ...(bonesLive ? {} : { disabled: "" }) },
      on: { click: () => { if (canBones && this.giveIndex >= 0) giveToBones(this.giveIndex); } } }, [
      h("div", { class: "pcard mid back" }),
      h("span", { class: "cb-lbl2 tnum", text: `Bones ${left}` }),
    ]);

    // your hand: tap to pick the card to give away (tap again to put it back)
    if (this.handOrder.length !== this.yourHand.length) this.handOrder = this.yourHand.map((_, i) => i);
    const keys = this.yourHand.map((c) => c.rank + c.suit);
    const prev = this.prevHandKey ? this.prevHandKey.split(",") : [];
    const handEls = this.handOrder.map((gi, d) => {
      const el = this.card(this.yourHand[gi], "big", {
        selected: pick === gi, clickable: picking, deal: this.freshDeal, dealIndex: d, dealGapMs: 230,
        pop: !this.freshDeal && prev.length > 0 && prev[gi] !== String(keys[gi]),
        // after Draw bones, the card you tap goes straight to the bones
        onClick: picking ? () => { if (this.cmpIntent === "bones") { giveToBones(gi); return; } this.giveIndex = this.giveIndex === gi ? -1 : gi; redraw(); } : undefined,
      });
      el.dataset.eng = String(gi);
      if (picking && pick < 0) el.classList.add("pickable");
      return el;
    });
    const arrived = !this.freshDeal && prev.length > 0 ? keys.filter((k, i) => prev[i] !== String(k)).length : 0;
    if (arrived) this.playDeal(arrived, 150);
    this.prevHandKey = keys.join(",");
    this.freshDeal = false;
    const cat = this.yourHand.length === 5 ? CATEGORY_NAME[evaluate(this.yourHand).category] : "";
    const playingFor = this.method === "RANDOM" && this.targetState && !this.claimMode ? stateName(this.targetState) : "";

    // the felt: middle + bones on top, your hand below, and what you're holding in the corner
    const felt = h("div", { class: "cb-felt" }, [
      h("div", { class: "cb-grid" + (midLive ? " live" : "") + (picking ? " picking" : "") }, [
        ...cells, bones, ...handEls,
        h("div", { class: "cb-cat" }, [
          h("span", { class: "cb-k", text: "You're holding" }), h("b", { text: cat || "—" }),
          playingFor ? h("span", { class: "cb-k for", text: `Playing for ${playingFor}` }) : null,
        ]),
      ]),
    ]);

    // the move panel: what to do now, then the moves (each with its picture)
    const intent = this.cmpIntent;
    // Only one knock a hand: once someone has, nobody else can, and this is everyone's last turn.
    const lastTurn = knocker && this.turnOpen ? `${knocker === b.players[0] ? "You" : knocker.name} knocked, so this is your last turn. ` : "";
    const canKnockNow = this.actions.some((a) => /knock/i.test(a.label));
    const hint = !this.turnOpen ? "" : this.mode === "knock" ? (canKnockNow ? "Knock, or end your turn." : `${lastTurn || "No knocking now. "}End your turn.`)
      : !picking ? "" : intent === "bones" ? "Tap the card you'll throw on the bones. A card from the bones takes its place."
      : pick >= 0 ? `Now tap a middle card to swap${canBones ? ", or the bones to draw" : ""}. Tap your card again to cancel.`
      : intent === "swap" ? "Tap the card in your hand you'll give away."
      : `${lastTurn}Tap a card in your hand, or choose a move.`;
    const moves: HTMLElement[] = [];
    const mv = (label: string, ico: string, onClick: () => void, cls = "") => h("button", { class: "cb-act" + cls, attr: { type: "button" }, html: `${ico}<span>${label}</span>`, on: { click: onClick } });
    if (canSwap) moves.push(mv("Swap card", CI.swap, () => { this.cmpIntent = this.cmpIntent === "swap" ? "" : "swap"; redraw(); }, intent === "swap" || (pick >= 0 && intent !== "bones") ? " on" : ""));
    if (canBones) moves.push(mv("Draw bones", CI.bones, () => {
      if (this.giveIndex >= 0) { giveToBones(this.giveIndex); return; }
      this.cmpIntent = this.cmpIntent === "bones" ? "" : "bones"; redraw();
    }, intent === "bones" ? " on" : ""));
    for (const a of this.actions) {
      const ico = /take all/i.test(a.label) ? CI.take : /knock/i.test(a.label) ? GAVEL : /pass/i.test(a.label) ? CI.pass : /turn the middle/i.test(a.label) ? CI.reveal
        : /swap it back/i.test(a.label) ? CI.undo : CI.end;
      moves.push(mv(a.label, ico, a.onClick, (a.primary ? " primary" : "") + (/^pass$/i.test(a.label) ? " small" : "")));
    }
    const lastMove = this.turnLog[this.turnLog.length - 1];
    const plain = (x: string) => x.replace(/<[^>]*>/g, "").replace(/[.\s]+$/, "");
    const waiting = !this.turnOpen && this.status && !(lastMove && plain(lastMove) === plain(this.status)) ? this.status : "";
    const side = h("div", { class: "cb-side" }, [
      hint ? h("p", { class: "cb-hint", text: hint }) : waiting ? h("p", { class: "cb-hint dim", text: waiting }) : null,
      moves.length ? h("div", { class: "cb-acts" }, moves) : null,
      lastMove ? h("button", { class: "cb-last", attr: { type: "button", title: "Every move this hand" }, html: lastMove, on: { click: () => this.openHandLog() } }) : null,
    ]);

    host.appendChild(h("div", { class: "cmp" }, [
      top,
      h("div", { class: "cb-main" }, [felt, side]),
      popup ? h("div", { class: "cb-shade", on: { click: () => { this.cmpPop = ""; redraw(); } } }) : null,
      popup,
    ]));
  }

  private claimedMap(): Record<string, PublicPlayer> {
    const m: Record<string, PublicPlayer> = {};
    for (const p of this.board?.players ?? []) for (const s of p.states) m[s] = p;
    return m;
  }

  private renderHeader(): void {
    $("app").className = `layout-${this.layout}`;
    const b = this.board;
    $("hand-counter").textContent = `${b?.handNumber ?? 1} / 50`;
    const claimed = this.claimedMap();
    $("board-count").textContent = `${Object.keys(claimed).length} / 50`;
    for (const [code, p] of this.miniPaths) p.style.fill = claimed[code] ? claimed[code].colour : "#332e2a";
    // ribbon (layout B)
    const ribbon = $("ribbon"); ribbon.innerHTML = "";
    const ranked = [...(b?.players ?? [])].sort((x, y) => y.score - x.score);
    for (const p of ranked) {
      ribbon.appendChild(h("div", { class: "ribbon-p", style: `border-top:2px solid ${p.colour}` }, [
        h("span", { style: "font-size:10px;color:var(--n500);white-space:nowrap", text: p.name }),
        h("div", { style: "display:flex;align-items:baseline;gap:6px" }, [
          h("span", { class: "heading tnum", style: "font-size:19px;color:var(--n100);line-height:1", text: String(p.score) }),
          h("span", { class: "tnum", style: "font-size:9px;color:var(--n600)", text: `${p.states.length} st` }),
        ]),
      ]));
    }
  }

  /** Opponents chosen on the New Game screen (persona ids; the rest of the seats are random). Remembered on this device. */
  opponents: string[] = (() => { try { const v = JSON.parse(localStorage.getItem("poli-opponents") ?? "[]"); return Array.isArray(v) ? v.filter((x) => persona(x)).slice(0, 4) : []; } catch { return []; } })();
  showOpponents(): void {
    const list = $("ng-opps-list");
    const chosen = this.opponents.map((id) => persona(id)!).filter(Boolean);
    list.innerHTML = chosen.length
      ? chosen.map((p) => `<span class="ng-opp">${personaPic(p, 40)}<span>${p.name}<small>${p.style}</small></span></span>`).join("")
        + (chosen.length < 4 ? `<span class="ng-opp">+ ${4 - chosen.length} at random</span>` : "")
      : "Random — a different group of AI players every game.";
    $("ng-choose-opps").textContent = chosen.length ? "Change opponents" : "Choose opponents";
  }
  async chooseOpponents(): Promise<void> {
    const got = await pickOpponents({ max: 4, chosen: this.opponents });
    if (got === null) return;
    this.opponents = got;
    try { localStorage.setItem("poli-opponents", JSON.stringify(got)); } catch { /* storage unavailable */ }
    this.showOpponents();
  }
  /** The AI opponents modelled on real players, with how each plays: [name, style]. */
  private lineup(): [string, string][] {
    return this.players.flatMap((p, i) => { const st = persona(this.aiProfiles[i])?.style; return !p.isHuman && st ? [[p.name, st] as [string, string]] : []; });
  }
  private avatarEl(p: { name: string; colour: string; id?: string }, size: number): HTMLElement {
    const el = this.avatarInner(p, size);
    const seat = this.players.findIndex((x) => !x.isHuman && x.name === p.name);
    const st = seat >= 0 ? persona(this.aiProfiles[seat])?.style : undefined;
    if (st) el.title = `${p.name} (AI) — ${st}`;
    return el;
  }
  private avatarInner(p: { name: string; colour: string; id?: string }, size: number): HTMLElement {
    if (p.id === "you") {
      if (this.avatar.photo)
        return h("img", { class: "avatar-img", style: `width:${size}px;height:${size}px;border:3px solid ${p.colour};object-fit:cover`, attr: { src: this.avatar.photo, alt: p.name } });
      return h("div", { class: "avatar-img", style: `width:${size}px;height:${size}px;border:3px solid ${p.colour};overflow:hidden`,
        html: avatarSvg(this.avatar, p.colour) });
    }
    const seat = this.players.findIndex((x) => !x.isHuman && x.name === p.name);
    // an AI seat: its player's picture (solo games know the persona; multiplayer AI seats go by name)
    const pic = seat >= 0 ? personaPicById(this.aiProfiles[seat], size) ?? personaPicById(PERSONAS.find((x) => x.name === p.name)?.id, size) : undefined;
    if (pic) { const el = h("div", { class: "avatar-img", style: `width:${size}px;height:${size}px;border:3px solid ${p.colour};overflow:hidden;border-radius:50%`, html: pic });
      const img = el.querySelector("img, svg") as HTMLElement | null; if (img) { img.style.width = "100%"; img.style.height = "100%"; }
      return el; }
    // multiplayer: another person at the table shows their photo, or their drawn avatar
    const look = this.remote && p.id ? tableLooks().get(p.id) : undefined;
    if (look?.avatarUrl) {
      const img = h("img", { class: "avatar-img", style: `width:${size}px;height:${size}px;border:3px solid ${p.colour};object-fit:cover`, attr: { src: look.avatarUrl, alt: p.name } });
      img.addEventListener("error", () => img.replaceWith(this.initialAvatar(p, size)));
      return img;
    }
    if (look?.avatarCfg) {
      const svg = drawnAvatar(look.avatarCfg, p.colour);
      if (svg) return h("div", { class: "avatar-img", style: `width:${size}px;height:${size}px;border:3px solid ${p.colour};overflow:hidden`, html: svg });
    }
    const src = AVATARS[p.name];
    if (src) {
      const img = h("img", { class: "avatar-img", style: `width:${size}px;height:${size}px;border:3px solid ${p.colour}`, attr: { src, alt: p.name } });
      img.addEventListener("error", () => img.replaceWith(this.initialAvatar(p, size)));
      return img;
    }
    return this.initialAvatar(p, size);
  }
  private initialAvatar(p: { name: string; colour: string }, size: number): HTMLElement {
    return h("div", { class: "avatar", style: `width:${size}px;height:${size}px;border:3px solid ${p.colour}`, text: (p.name || "?").slice(0, 1).toUpperCase() });
  }

  private card(c: Card | null, size: "mid" | "big", opt: { clickable?: boolean; selected?: boolean; highlight?: boolean; disabled?: boolean; onClick?: () => void; deal?: boolean; dealIndex?: number; dealGapMs?: number; pop?: boolean }): HTMLElement {
    const anim = opt.deal ? " deal" : opt.pop ? " pop" : "";
    let el: HTMLElement;
    if (!c) el = h("div", { class: `pcard ${size} back${anim}` });
    else
      el = h("div", { class: `pcard cface ${size}${RED(c.suit) ? " red" : ""}${c.rank === 14 ? " ace" : ""}${opt.clickable && !opt.disabled ? " click" : ""}${opt.selected ? " sel" : ""}${opt.highlight ? " hl" : ""}${opt.disabled ? " disabled" : ""}${anim}`,
        html: faceHtml(c), attr: { "data-key": `${c.rank}${c.suit}` }, on: opt.onClick && !opt.disabled ? { click: opt.onClick } : undefined });
    if (opt.deal) el.style.animationDelay = `${(opt.dealIndex ?? 0) * (opt.dealGapMs ?? 65)}ms`;
    return el;
  }

  // Pointer-drag reorder of your own hand (idle / knock modes only). We reorder the
  // display permutation, never the engine hand, so swap indices stay correct.
  private attachCardDrag(el: HTMLElement, di: number, row: HTMLElement): void {
    el.style.touchAction = "none";
    el.addEventListener("pointerdown", (ev) => {
      if (!(this.mode === "idle" || this.mode === "knock")) return;
      ev.preventDefault();   // no text selection / native drag, which would cancel the gesture
      this.dragState = { di, startX: ev.clientX, startY: ev.clientY, el, row, moved: false, pid: ev.pointerId, swap: this.canDragSwap(), bones: this.canDragBones() };
      // Grab immediately: in the fan only a ~50px strip of each card shows, so a quick drag would
      // otherwise leave the card before the 5px threshold and the move would be lost.
      try { el.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
    });
    el.addEventListener("pointermove", (ev) => {
      const ds = this.dragState; if (!ds || ds.el !== el) return;
      const dx = ev.clientX - ds.startX, dy = ev.clientY - ds.startY;
      const lift = ds.swap || ds.bones;
      if (!ds.moved && Math.hypot(dx, lift ? dy : 0) > 5) { ds.moved = true; el.classList.add("dragging"); }
      if (!ds.moved) return;
      // On your turn the card can be carried onto a middle card (swap) or onto the bones (discard it and
      // draw blind); otherwise it only slides sideways.
      el.style.transform = `translate(${dx}px, ${lift ? dy : -16}px)`;
      if (ds.swap) App.markDrop(".felt-area .pcard[data-mid]", App.under(ev.clientX, ev.clientY, ".felt-area .pcard.cface[data-mid]"));
      if (ds.bones) App.markDrop(".bones", App.under(ev.clientX, ev.clientY, ".bones"));
    });
    const end = (ev: PointerEvent) => {
      const ds = this.dragState; if (!ds || ds.el !== el) return;
      this.dragState = null;
      try { el.releasePointerCapture(ds.pid); } catch { /* ignore */ }
      el.classList.remove("dragging"); el.style.transform = "";
      if (!ds.moved) return; // a plain click — leave for the click handler
      if (ds.swap) {
        App.markDrop(".felt-area .pcard[data-mid]", null);
        const t = App.under(ev.clientX, ev.clientY, ".felt-area .pcard.cface[data-mid]");
        if (t) { this.dropSwap(this.handOrder[ds.di], Number(t.dataset.mid)); return; }
      }
      if (ds.bones) {
        App.markDrop(".bones", null);
        if (App.under(ev.clientX, ev.clientY, ".bones")) { this.dropBones(this.handOrder[ds.di]); return; }
      }
      // In the fan only a strip of each card shows. Dropping onto a card's visible strip takes that
      // card's place: the new slot = how many OTHER cards have their strip's midpoint left of the
      // pointer. Uses resting layout positions (offsetLeft), unaffected by the fan's tilt or the drag.
      const cards = Array.from(row.querySelectorAll(":scope > .pcard")) as HTMLElement[];
      const gap = cards.length > 1 ? (cards[cards.length - 1].offsetLeft - cards[0].offsetLeft) / (cards.length - 1) : el.offsetWidth;
      const rr = row.getBoundingClientRect(), px = ev.clientX - rr.left, py = ev.clientY - rr.top;
      // A tilted card's left edge shifts with height (cards pivot on their top-left corner), so find
      // where each card's edge actually is at the pointer's height.
      const edge = (c: HTMLElement) => c.offsetLeft - (py - c.offsetTop) * Math.sin(((parseFloat(c.style.getPropertyValue("--rot")) || 0) * Math.PI) / 180);
      // Moving right: passing the start of a card's strip puts you after it. Moving left: you take
      // the place of whichever card's strip you're over (cards whose strip ends before you stay left).
      let to = ev.clientX >= ds.startX
        ? cards.filter((c, i) => i < ds.di || (i > ds.di && edge(c) + 3 < px)).length
        : cards.filter((c, i) => i < ds.di && edge(c) + gap - 3 < px).length;
      to = Math.max(0, Math.min(this.handOrder.length - 1, to));
      if (to === ds.di) return;
      const [m] = this.handOrder.splice(ds.di, 1);
      this.handOrder.splice(to, 0, m);
      this.manualOrder = true;
      this.renderAll();
    };
    el.addEventListener("pointerup", end);
    el.addEventListener("pointercancel", end);
  }

  private dealtHand = 0;
  private prevHandKey = "";
  private renderTable(): void {
    const host = $("table-host");
    // Remember where each of your cards was, so a card that leaves your hand can fly off (discard).
    const before = new Map<string, { r: DOMRect; w: number; html: string; cls: string }>();
    host.querySelectorAll<HTMLElement>(".photofan .pcard[data-key]").forEach((el) =>
      before.set(el.dataset.key!, { r: el.getBoundingClientRect(), w: el.offsetWidth, html: el.innerHTML, cls: el.className }));
    host.innerHTML = "";
    const b = this.board; if (!b) return;
    const claimed = this.claimedMap();
    const you = b.players[0];
    const fresh = b.handNumber !== this.dealtHand;
    if (fresh) { this.dealtHand = b.handNumber; this.prevHandKey = ""; }

    // felt area
    const felt = h("div", { class: "felt-area" }, [
      h("div", { class: "felt-rim" }), h("div", { class: "felt" }), h("div", { class: "felt-line" }),
    ]);
    // center
    // Glows when you can drop one of your cards on it (no text prompt — the table shows what's live).
    const midCards = h("div", { class: "cardrow" + (this.canDragSwap() ? " live" : "") });
    b.middle.forEach((c, i) => {
      const el = this.card(c, "mid", { deal: fresh, dealIndex: i, dealGapMs: 170 });
      el.dataset.mid = String(i);
      if (i === this.midFlash) el.classList.add("just-left");
      midCards.appendChild(el);
    });
    if (fresh) this.playDeal(5, 170);
    // The bones: the face-down stock (plus discards, which are shuffled back in when it runs out).
    const left = b.stockCount + b.discardCount;
    const bones = h("div", { class: "bones" + (this.canDragBones() ? " live" : ""), attr: { title: "The bones — drag one of your cards here to swap it for a card from the bones" } }, [
      // A visible stack: up to five backs, thinner as the bones run down.
      h("div", { class: "bones-stack" }, Array.from({ length: Math.min(5, Math.ceil(left / 5)) }, (_, k) => h("div", { class: "pcard mid back", style: `--k:${k}` }))),
      h("div", { class: "bones-lbl", text: `Bones · ${left}` }),
    ]);
    // The felt shows just the middle cards (the hand number is in the header). In a random-order game
    // the state being played for isn't shown anywhere else, so it stays above the cards.
    const playingFor = this.method === "RANDOM" && this.targetState && !this.claimMode ? stateName(this.targetState) : "";
    // Your buttons sit on the table's bottom rail: Knock alone at the right end, well away from End turn
    // and the rest at the left end (placed by fitTopSeats from the rail's measured position).
    const railBtn = (a: (typeof this.actions)[number]) => h("button", { class: "rail-btn" + (a.primary ? " primary" : ""), text: a.label, on: { click: a.onClick } });
    felt.appendChild(h("div", { class: "rail-acts" }, [
      h("div", { class: "ra-l" }, this.actions.filter((a) => a.side !== "r").map(railBtn)),
      // where Knock would be, after someone has knocked: why there's no Knock
      h("div", { class: "ra-r" }, this.actions.some((a) => a.side === "r") ? this.actions.filter((a) => a.side === "r").map(railBtn)
        : this.turnOpen && this.lastTurnNote() ? [h("span", { class: "rail-note", text: this.lastTurnNote() })] : []),
    ]));
    felt.appendChild(h("div", { class: "center" }, [
      playingFor ? h("div", { class: "pot" }, [h("span", { class: "tl", text: playingFor })]) : null,
      midCards,
    ]));
    // Seat that is dealt first / acts first this hand (left of the dealer).
    const dealer = b.dealerSeat ?? 0, firstSeat = (dealer + 1) % 5;
    // Standing in the game (#1 = most points; states break a tie, as in the standings).
    const rankOf = new Map([...b.players].sort((x, y) => y.score - x.score || y.states.length - x.states.length).map((p, i) => [p.seat, i + 1]));
    // AI seats
    // One player's name box: portrait, name, party, score, and badges (deals first / knocked / took).
    const plaqueFor = (p: typeof you, seat: number, cls: string, style = "") => {
      const active = this.activeSeat === seat;
      // A knock on the knocker's first turn is a quock (the duck itself is kept for the Hall of Fame).
      const badge = p.knocked ? (b.knockRound === 1 ? h("span", { class: "badge knock quock", html: `${GAVEL} Quocked` })
        : h("span", { class: "badge knock", html: `${GAVEL} Knocked` })) : active ? h("span", { class: "badge", text: "To act" }) : null;
      const marks = h("div", { class: "marks" }, [
        // The dealer plays LAST (and may not take the middle blind); the next player clockwise starts.
        seat === dealer ? h("span", { class: "deal-first", text: "🂠 Dealer" }) : null,
        badge,
        this.seatTook.has(seat) ? h("span", { class: "took", html: this.seatTook.get(seat)! }) : null,
      ]);
      return h("div", { class: cls, style, attr: { "data-seat": String(seat) } }, [
        h("div", { class: "top" }, [this.avatarEl(p, 64)]),
        h("div", { class: "plaque" + (active ? " active" : "") + (p.knocked ? " knocked" : "") }, [
          h("div", { class: "pl-row" }, [
            h("div", { class: "who" }, [
              h("span", { class: "nm" }, [h("span", { class: "rk", text: `#${rankOf.get(seat)} ` }), p.name]),
              h("span", { class: "pt" + (inkOn(p.colour) === "#fff" ? " ink-light" : ""), style: `background:${p.colour};color:${inkOn(p.colour)}`, text: p.party }),
            ]),
            h("div", { class: "tally" }, [
              h("span", { class: "sc", text: String(p.score) }),
              h("span", { class: "st", text: `${p.states.length} states` }),
            ]),
          ]),
          h("div", { class: "bar", style: `background:${p.colour}` }),
          marks,
        ]),
      ]);
    };
    // AI seats
    SLOT_PLAYER.forEach((pi, slot) => felt.appendChild(plaqueFor(b.players[pi], pi, `seat s${slot}`, SEAT_ANCHOR[slot])));
    requestAnimationFrame(() => this.fitTopSeats());

    // bottom action bar
    const handCards = h("div", { class: "photofan" });
    if (this.handOrder.length !== this.yourHand.length) this.handOrder = this.yourHand.map((_, i) => i);
    const keys = this.yourHand.map((c) => c.rank + c.suit);           // engine order
    const prev = this.prevHandKey ? this.prevHandKey.split(",") : [];
    const reorderable = this.mode === "idle" || this.mode === "knock";
    this.handOrder.forEach((gi, d) => {
      const el = this.card(this.yourHand[gi], "big", {
        selected: this.giveIndex === gi,
        deal: this.freshDeal, dealIndex: d, dealGapMs: 230,
        pop: !this.freshDeal && prev.length > 0 && prev[gi] !== String(keys[gi]),
      });
      if (reorderable) el.classList.add("reorderable");
      el.dataset.eng = String(gi);
      // sit the card where the photo's card was (centre slots used when holding fewer than five)
      const slot = PHOTO_SLOTS[Math.min(PHOTO_SLOTS.length - 1, d + Math.floor((PHOTO_SLOTS.length - this.handOrder.length) / 2))];
      el.style.left = `${(slot[0] / PHOTO_FAN.w) * 100}%`;
      el.style.top = `${(slot[1] / PHOTO_FAN.h) * 100}%`;
      el.style.setProperty("--rot", `${slot[2]}deg`);
      el.style.zIndex = String(d + 1);
      this.attachCardDrag(el, d, handCards);
      handCards.appendChild(el);
    });
    if (this.handOrder.length) handCards.appendChild(handsImg(this.avatar.skin));
    // A card arriving in your hand (swap, bones, taking the middle) gets the same "handed to you"
    // sound as the deal — once per new card.
    const arrived = !this.freshDeal && prev.length > 0 ? keys.filter((k, i) => prev[i] !== String(k)).length : 0;
    if (arrived) this.playDeal(arrived, 150);
    this.prevHandKey = keys.join(",");
    this.freshDeal = false;

    const bar = h("div", { class: "actionbar" }, [
      h("div", { class: "you-col" }, [
        h("div", { class: "tl-wrap" }, [h("div", { class: "turn-log" }, [
          h("div", { class: "tl-head", text: `Hand ${b.handNumber} — moves` }),
          h("div", { class: "tl-line", html: `${dealer === 0 ? `<span class="tl-who" style="--c:${you.colour}">You</span> deal` : `<b>${b.players[dealer].name}</b> deals`} — <b>${firstSeat === 0 ? "you" : b.players[firstSeat].name}</b> ${firstSeat === 0 ? "go" : "goes"} first` }),
          ...this.turnLog.map((l) => h("div", { class: "tl-line", html: l })),
          this.turnLog.length ? null : h("div", { class: "tl-line tl-empty", text: "No moves yet" }),
          // before the first move of the game: who you're up against, and how each of them plays
          ...(b.handNumber === 1 && !this.turnLog.length && this.lineup().length ? [h("div", { class: "tl-head", style: "margin-top:10px", text: "Tonight's table" }),
            ...this.lineup().map(([n, st]) => h("div", { class: "tl-line tl-lineup" }, [h("b", { text: n }), ` — ${st}`]))] : []),
        ])]),
      ]),
      // No name panel for you (your score is in the running totals): your buttons sit beside your hand.
      h("div", { class: "hand-col" }, [
        handCards,
        h("div", { class: "my-acts" }, [
          dealer === 0 ? h("span", { class: "deal-first me-dealer", text: "🂠 You're the dealer" }) : null,
          // No prompts or narrative here — every move is listed in the log on the left, and nothing asks "are you sure".
        ]),
      ]),
      // The bones sit off the table, bottom right.
      // (wrapped: the layout code pads this column's first child to clear the right-hand player)
      h("div", { class: "act-col", style: "display:flex;flex-direction:column;gap:8px;min-width:0" }, [h("div", { class: "bones-wrap" }), bones]),
    ]);

    host.appendChild(h("div", { class: "table-wrap" }, [felt, bar]));
    const log = host.querySelector<HTMLElement>(".turn-log"); if (log) log.scrollTop = log.scrollHeight;
    if (!fresh && before.size) this.animateDiscards(before, new Set(keys.map(String)));
  }
  // A card that left your hand (swapped into the middle / discarded) glides toward the middle and fades.
  private animateDiscards(before: Map<string, { r: DOMRect; w: number; html: string; cls: string }>, now: Set<string>): void {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const mid = document.querySelector("#table-host .felt-area .cardrow")?.getBoundingClientRect();
    for (const [key, b] of before) {
      if (now.has(key) || !mid) continue;
      const ghost = h("div", { class: b.cls.replace(/\b(deal|pop|sel|click|reorderable)\b/g, "") + " discard-ghost", html: b.html });
      const gh = b.w * 129 / 92;
      Object.assign(ghost.style, { left: `${b.r.left + (b.r.width - b.w) / 2}px`, top: `${b.r.top + (b.r.height - gh) / 2}px`, width: `${b.w}px`, height: `${gh}px` });
      document.body.appendChild(ghost);
      const dx = mid.left + mid.width / 2 - (b.r.left + b.r.width / 2), dy = mid.top + mid.height / 2 - (b.r.top + b.r.height / 2);
      ghost.animate([{ transform: "none", opacity: 1 }, { transform: `translate(${dx}px, ${dy}px) rotate(8deg) scale(.7)`, opacity: 0 }],
        { duration: 520, easing: "cubic-bezier(.4,0,.2,1)" }).onfinish = () => ghost.remove();
    }
  }

  private renderSheet(): void {
    const host = $("sheet-host"); host.innerHTML = "";
    if (this.layout === "b") return;
    const strip = this.layout === "c";
    const b = this.board; const players = b?.players ?? [];
    const max = Math.max(1, ...players.map((p) => p.score));
    const ranked = [...players].sort((x, y) => y.score - x.score || y.states.length - x.states.length);

    const head = h("div", { class: "sheet-head" }, [
      h("div", {}, [h("div", { class: "kicker", text: "Score sheet" }), h("div", { class: "sheet-title", text: "Running totals" })]),
      h("div", { class: "seg" }, [
        h("button", { class: this.sheetTab === "standings" ? "on" : "", text: "Standings", on: { click: () => { this.sheetTab = "standings"; this.renderSheet(); } } }),
        // The hand log opens as its own page (previous hands only, scrolling) rather than inside this panel.
        h("button", { text: "Hand log", on: { click: () => this.openHandLog() } }),
      ]),
    ]);
    const body = h("div", { class: "sheet-body" });

    if (this.sheetTab === "standings") {
      if (strip) {
        const grid = h("div", { class: "strip-grid" });
        ranked.forEach((p, i) =>
          grid.appendChild(h("div", { class: "strip-card", style: `border-top:3px solid ${p.colour}` }, [
            h("div", { style: "display:flex;align-items:baseline;justify-content:space-between;gap:8px" }, [
              h("span", { style: "font-size:15px;color:var(--n100)" }, [h("span", { class: "rk", text: `#${i + 1} ` }), p.name]),
            ]),
            h("span", { class: "sc", text: String(p.score) }),
            h("div", { style: "display:flex;justify-content:space-between" }, [
              h("span", { style: "font-size:12px;color:var(--n500)", text: p.party }),
              h("span", { class: "tnum", style: "font-size:12px;color:var(--a400)" }, [`${p.states.length} states`]),
            ]),
          ])));
        body.appendChild(grid);
      } else {
        ranked.forEach((p, i) =>
          body.appendChild(h("div", { class: "rank-row" }, [
            h("span", { class: "no", text: `#${i + 1}` }),
            h("div", { style: "min-width:0" }, [
              h("div", { style: "display:flex;align-items:center;gap:7px;min-width:0" }, [
                h("span", { class: "dot", style: `background:${p.colour}` }),
                h("span", { class: "nm", text: p.name }),
              ]),
              h("span", { class: "party", style: "display:block", text: p.party }),
              h("span", { class: "hold", text: p.states.length ? p.states.slice(-8).join(" ") : "no states yet" }),
            ]),
            h("span", { class: "stc", text: String(p.states.length) }),
            h("span", { class: "pts", text: String(p.score) }),
          ])));
      }
    }
    host.appendChild(h("div", { class: "sheet" }, [head, body]));
  }

  // ---- map ----
  private buildMaps(): void {
    const build = (interactive: boolean, store: Map<string, SVGPathElement>): SVGElement => {
      // The board map gets extra Atlantic on the right: Maine reaches the edge of the stock viewBox,
      // leaving nowhere for the New England labels that are moved offshore.
      const [vx, vy, vw, vh] = USAMap.viewBox.split(/[ ,]+/).map(Number);
      const svg = svgEl("svg", { viewBox: interactive ? `${vx} ${vy} ${vw + 170} ${vh}` : USAMap.viewBox });
      for (const loc of USAMap.locations) {
        const code = loc.id.toUpperCase(); if (code === "DC") continue;
        const p = svgEl("path", { d: loc.path, class: "state-path", fill: "#332e2a", stroke: "#14100e", "stroke-width": "0.9" }) as SVGPathElement;
        (p as any).dataset.code = code;
        if (interactive) {
          // Readout follows CLICK only — hovering another state must not change it.
          p.addEventListener("click", () => {
            if (this.suppressClick) return; // a pan-drag just ended — ignore
            this.focusCode = code; this.renderMapReadout(); this.onMapPick(code);
            if (COMPACT.matches && !this.claimMode) this.cmapOpenDetails?.();
          });
          // Hovering just NAMES the state (a floating tip) so you can learn the map without selecting.
          const tip = $("map-tip");
          p.addEventListener("mouseenter", () => { if (!this.mapOpen) return; tip.textContent = stateName(code); tip.hidden = false; });
          p.addEventListener("mousemove", (e) => { tip.style.left = (e as MouseEvent).clientX + 16 + "px"; tip.style.top = (e as MouseEvent).clientY + "px"; });
          p.addEventListener("mouseleave", () => { tip.hidden = true; });
        }
        svg.appendChild(p); store.set(code, p);
      }
      return svg;
    };
    const mini = build(false, this.miniPaths);
    $("mini-map").innerHTML = ""; $("mini-map").appendChild(mini);
    // modal map built lazily in renderMap
    this._modalSvg = build(true, this.modalPaths);
  }
  // The two top seats sit where the oval curves away, so fixed % offsets put them on the felt at
  // some screen sizes and far off it at others. Slide each sideways until its gap to the rim
  // matches the gap of the side seats (which sit on the oval's flat ends).
  private _seatResize = false;
  fitTopSeats(): void {
    if (!this._seatResize) { this._seatResize = true; window.addEventListener("resize", () => this.fitTopSeats()); }
    const rimEl = document.querySelector(".felt-rim");
    const seats = [...document.querySelectorAll<HTMLElement>(".seat")];
    if (!rimEl || seats.length < 4) return;
    seats.forEach((el) => (el.style.transform = ""));
    // Middle cards grow to fill the table: as tall as it allows (under the state label, when there is
    // one), and clear of the rounded ends.
    const fa = document.querySelector<HTMLElement>(".felt-area"), feltEl = document.querySelector<HTMLElement>(".felt");
    const potH = document.querySelector<HTMLElement>(".felt-area .pot")?.offsetHeight ?? 0;
    if (fa && feltEl) {
      const f = (document.querySelector<HTMLElement>(".felt-rim") ?? feltEl).getBoundingClientRect();
      // The table is a stadium (round ends of radius h/2): a row of cards of height ch, centred,
      // has (w - h) + 2·√(r² − (ch/2)²) of width at its top and bottom edges.
      const r = f.height / 2;
      // Never smaller than the old fixed size (it may run into the rail, as it always did on short tables).
      const before = Math.min(122, Math.max(96, innerWidth * 0.066));
      // (46px kept clear at the bottom so the rail buttons don't sit on the cards)
      let cw = Math.max(before, Math.min(190, (f.height - potH - 42 - 46) * 92 / 129));
      const fits = (c: number) => 5 * c + 4 * 18 + 40 <= f.width - f.height + 2 * Math.sqrt(r * r - Math.min(r * 0.8, c * 129 / 92 / 2 + 12) ** 2);
      while (cw > before && !fits(cw)) cw -= 2;
      while (cw > 64 && 5 * cw + 4 * 18 + 40 > f.width) cw -= 2;   // the old size, if the table is wide enough
      fa.style.setProperty("--mid-w", `${Math.round(Math.max(64, cw))}px`);
      // Rail buttons: centred on the bottom rail (between the felt's edge and the table's outer edge),
      // inset from the round ends.
      const fr = feltEl.getBoundingClientRect(), ar = fa.getBoundingClientRect();
      fa.style.setProperty("--rail-y", `${Math.round((Math.min(fr.bottom, f.bottom - 6) + f.bottom) / 2 - ar.top)}px`);
      fa.style.setProperty("--rail-l", `${Math.round(f.left - ar.left + f.height * 0.36)}px`);
      fa.style.setProperty("--rail-r", `${Math.round(ar.right - f.right + f.height * 0.36)}px`);
    }
    const rim = rimEl.getBoundingClientRect();
    const cx = rim.x + rim.width / 2, cy = rim.y + rim.height / 2, a = rim.width / 2, b = rim.height / 2;
    const pts: [number, number][] = [];
    for (let i = 0; i < 360; i++) { const t = (i / 360) * 2 * Math.PI; pts.push([cx + a * Math.cos(t), cy + b * Math.sin(t)]); }
    // Smallest distance from the seat box (shifted by dx) to the rim; -1 if the rim runs through it.
    const gap = (r: DOMRect, dx: number) => {
      let min = Infinity;
      for (const [x, y] of pts) {
        const l = r.left + dx, rt = r.right + dx;
        if (x > l && x < rt && y > r.top && y < r.bottom) return -1;
        min = Math.min(min, Math.hypot(Math.max(l - x, 0, x - rt), Math.max(r.top - y, 0, y - r.bottom)));
      }
      return min;
    };
    const target = Math.max(0, gap(seats[2].getBoundingClientRect(), 0));
    [[seats[0], -1], [seats[1], 1]].forEach(([el, dir]) => {
      const seat = el as HTMLElement, out = dir as number, r = seat.getBoundingClientRect();
      let dx = 0;
      if (gap(r, 0) < target) { while (gap(r, dx) < target && Math.abs(dx) < 400) dx += out * 2; }
      else { while (gap(r, dx + -out * 2) >= target && Math.abs(dx) < 400) dx -= out * 2; }
      seat.style.transform = `translateX(${dx}px)`;
    });
    // On short tables the side players ride up into the top players above them. Keep a clear gap
    // by sliding each side player down below the one above it.
    // Both sides move by the same amount so the table stays symmetrical.
    const GAP = 14;
    const dy = Math.max(0, ...[[seats[0], seats[2]], [seats[1], seats[3]]].map(([top, side]) => {
      const a = top.getBoundingClientRect(), c = side.getBoundingClientRect();
      if (a.right + GAP <= c.left || c.right + GAP <= a.left) return 0; // already side by side
      return a.bottom + GAP - c.top;
    }));
    if (dy > 0) seats[2].style.transform = seats[3].style.transform = `translateY(${dy}px)`;
    // The status line and buttons sit under the right-hand players; start them below the lower one.
    const log = document.querySelector<HTMLElement>(".turn-log"); // its box may have just shrunk — keep the newest move in view
    queueMicrotask(() => { if (log) log.scrollTop = log.scrollHeight; });
    // Likewise your portrait under the left-hand players. The bar centres its columns, so padding
    // shifts them by part of its height — settle it in a few passes.
    // Keep every player fully on screen (Rail's table is narrow; top players slide outward above).
    const host = document.querySelector(".felt-area")?.getBoundingClientRect();
    if (host) seats.forEach((el) => {
      const r = el.getBoundingClientRect(), lo = Math.max(4, host.left), hi = Math.min(innerWidth - 4, host.right);
      const dx = r.left < lo ? lo - r.left : r.right > hi ? hi - r.right : 0;
      if (dx) el.style.transform = `${el.style.transform} translateX(${Math.round(dx)}px)`.trim();
    });
    // Finally, shrink the middle cards if they still reach a player (Rail's narrower table).
    if (fa) {
      const mids = [...fa.querySelectorAll<HTMLElement>(".cardrow .pcard, .pot")];
      const hit = () => mids.some((m) => { const a = m.getBoundingClientRect(); return seats.some((el) => { const r = el.getBoundingClientRect(); return a.left < r.right && r.left < a.right && a.top < r.bottom && r.top < a.bottom; }); });
      let cw = parseFloat(fa.style.getPropertyValue("--mid-w")) || 0;
      while (cw > 64 && hit()) { cw -= 4; fa.style.setProperty("--mid-w", `${cw}px`); }
      // The bones (off the table, in the bar below) use the same card size as the middle.
      document.querySelector<HTMLElement>(".actionbar")?.style.setProperty("--mid-w", fa.style.getPropertyValue("--mid-w"));
    }
    const band = !!document.querySelector("#app.layout-c");
    ([[".act-col", seats[3]], [".you-col", seats[2]]] as [string, HTMLElement][]).forEach(([sel, seat]) => {
      const col = document.querySelector<HTMLElement>(sel); if (!col) return;
      col.style.paddingTop = ""; col.style.transform = "";
      const f0 = (col.firstElementChild as HTMLElement | null) ?? col; f0.style.paddingRight = f0.style.paddingLeft = "";
      // The moves list (left) settles below the left-hand player the same way in every layout.
      const asBand = band || sel === ".you-col";
      if (!asBand) {
        // Rail/Full: first slide the status and buttons down into any empty space under the last button.
        const sr = seat.getBoundingClientRect(), cr = col.getBoundingClientRect();
        if (sr.left >= cr.right || sr.right <= cr.left) return;
        const bottom = Math.max(...[...col.querySelectorAll<HTMLElement>(".status-txt, button")].map((e) => e.getBoundingClientRect().bottom));
        const shift = Math.min(sr.bottom + 10 - cr.top, col.parentElement!.getBoundingClientRect().bottom - bottom);
        if (shift > 0) col.style.transform = `translateY(${Math.floor(shift)}px)`;
        return;
      }
      const first = ([...col.children] as HTMLElement[]).find((c) => c.offsetParent) ?? col;
      // Never push it past the bottom of the bar (it would run over the scores); on a very short
      // window the text just keeps clear of the player sideways instead.
      const bar = col.parentElement!.getBoundingClientRect();
      let pad = 0;
      (first as HTMLElement).style.paddingRight = "";
      for (let k = 0; k < 4; k++) {
        const sr = seat.getBoundingClientRect(), cr = first.getBoundingClientRect();
        if (sr.left >= cr.right || sr.right <= cr.left) break;
        // room = empty space between the column's last item and the bottom of the bar
        // (measured to the last button/text — the buttons' box itself may stretch to the bottom)
        const bottom = Math.max(...[...col.querySelectorAll<HTMLElement>(".status-txt, button")].map((e) => e.getBoundingClientRect().bottom));
        const room = sel === ".you-col" ? Infinity : bar.bottom - bottom; // the log shrinks instead
        const under = Math.min(sr.bottom + 10 - cr.top, room);
        if (under < 1) break;
        pad += Math.floor(under); col.style.paddingTop = `${pad}px`;
      }
      const sr = seat.getBoundingClientRect(), cr = first.getBoundingClientRect();
      if (band && sel === ".act-col" && sr.bottom > cr.top && sr.left < cr.right) (first as HTMLElement).style.paddingRight = `${Math.ceil(cr.right - sr.left + 10)}px`;
    });
  }
  private _modalSvg?: SVGElement;
  private _mapLabeled = false;
  // Old-chart dressing for the Riverboat map: sailing ships and paddle steamers in open water,
  // plus a compass rose. Drawn in local 0–100 units, then placed. (Wild West shows its map picture only.)
  private static shipSvg(): string {
    const sail = (x: number, y: number, w: number, hh: number) =>
      `<path class="paper" d="M${x - w} ${y} C ${x - w * .6} ${y + hh * .2}, ${x + w * .6} ${y + hh * .2}, ${x + w} ${y} L ${x + w * .92} ${y + hh} C ${x + w * .5} ${y + hh * 1.15}, ${x - w * .5} ${y + hh * 1.15}, ${x - w * .92} ${y + hh} Z"/>`;
    return `<path class="rig" d="M32 60 L32 16 M52 60 L52 6 M70 60 L70 18 M90 58 L106 46"/>` +
      sail(52, 12, 14, 17) + sail(52, 33, 16, 20) + sail(70, 22, 11, 14) + sail(70, 40, 13, 16) + sail(32, 20, 10, 13) + sail(32, 38, 12, 16) +
      `<path class="paper" d="M71 22 L104 48 L73 50 Z"/>` +
      `<path class="ink" d="M52 6 L63 9 L52 12 Z M70 18 L78 20 L70 22 Z M32 16 L40 18 L32 20 Z"/>` +
      `<path class="ink" d="M6 58 L94 58 L86 72 Q 50 79 14 72 Z M6 58 L4 47 L22 47 L22 58 Z"/>` +
      `<path class="rig" d="M-4 80 q 8 -5 16 0 t 16 0 t 16 0 t 16 0 t 16 0 t 16 0 t 16 0"/>`;
  }
  private static steamSvg(): string {
    const spokes = Array.from({ length: 8 }, (_, i) => { const a = (i * Math.PI) / 4; return `M80 56 L ${(80 + Math.cos(a) * 14).toFixed(1)} ${(56 + Math.sin(a) * 14).toFixed(1)}`; }).join(" ");
    return `<path class="ink" d="M4 60 L 96 60 L 88 70 L 12 70 Z"/>` +
      `<rect class="paper" x="14" y="44" width="58" height="16"/><rect class="paper" x="24" y="32" width="38" height="12"/><rect class="paper" x="34" y="24" width="16" height="8"/>` +
      `<rect class="ink" x="28" y="2" width="5" height="30"/><rect class="ink" x="50" y="2" width="5" height="30"/>` +
      `<circle class="paper" cx="80" cy="56" r="14"/><path class="rig" d="${spokes}"/>` +
      `<path class="rig" d="M-2 78 q 8 -5 16 0 t 16 0 t 16 0 t 16 0 t 16 0 t 16 0 t 16 0"/>`;
  }
  private static compassSvg(): string {
    const pts = (r1: number, r2: number, a0: number) => Array.from({ length: 4 }, (_, i) => {
      const a = a0 + (i * Math.PI) / 2, b = a + Math.PI / 4, c = a - Math.PI / 4;
      return `M50 50 L ${(50 + Math.cos(c) * r2).toFixed(1)} ${(50 + Math.sin(c) * r2).toFixed(1)} L ${(50 + Math.cos(a) * r1).toFixed(1)} ${(50 + Math.sin(a) * r1).toFixed(1)} L ${(50 + Math.cos(b) * r2).toFixed(1)} ${(50 + Math.sin(b) * r2).toFixed(1)} Z`;
    }).join(" ");
    return `<circle class="rig" cx="50" cy="50" r="38"/><circle class="rig" cx="50" cy="50" r="33"/>` +
      `<path class="paper" d="${pts(30, 7, Math.PI / 4)}"/><path class="ink" d="${pts(44, 9, -Math.PI / 2)}"/>` +
      `<text x="50" y="0" text-anchor="middle" font-size="12">N</text>`;
  }
  // Zoomed-in labels. Each state gets one label block — its name with the capital directly beneath —
  // in fixed sizes (names 11, capitals 7.5). The capital's dot sits at its real location. A block
  // that doesn't fit inside its state moves out (east-coast states to a column in the Atlantic,
  // others to the nearest clear spot) with ONE pointer line to the dot. No label overlaps another,
  // and no pointer line crosses another line or a label.
  private labelModalMap(): void {
    const svg = this._modalSvg as SVGSVGElement | undefined;
    if (this._mapLabeled || !svg) return;
    const NAME_FS = 11, CAP_FS = 7.5;
    const wasShown = svg.classList.contains("show-cities");
    svg.classList.add("show-cities"); // labels are hidden at full view; hidden text measures as 0×0
    type Box = { x: number; y: number; w: number; h: number };
    const paths = [...this.modalPaths].map(([code, p]) => {
      const g = p as unknown as SVGGeometryElement;
      let bb: DOMRect | null = null;
      try { bb = g.getBBox(); } catch { /* not rendered */ }
      return { code, g, bb };
    }).filter((p) => p.bb && p.bb.width) as { code: string; g: SVGGeometryElement; bb: DOMRect }[];
    if (!paths.length) { if (!wasShown) svg.classList.remove("show-cities"); return; }
    const vb = svg.viewBox.baseVal;
    const pt = svg.createSVGPoint();
    const inFill = (g: SVGGeometryElement, x: number, y: number) => { pt.x = x; pt.y = y; return g.isPointInFill(pt); };
    const onLand = (x: number, y: number) =>
      paths.some((p) => x >= p.bb.x && x <= p.bb.x + p.bb.width && y >= p.bb.y && y <= p.bb.y + p.bb.height && inFill(p.g, x, y));
    const samples = (b: Box) => {
      const out: [number, number][] = [];
      for (const fx of [0.04, 0.5, 0.96]) for (const fy of [0.1, 0.5, 0.9]) out.push([b.x + fx * b.w, b.y + fy * b.h]);
      return out;
    };
    // Open sea, not a lake: a straight line east, south or west reaches the map edge without land.
    const toSea = (x: number, y: number) => ([[1, 0], [0, 1], [-1, 0]] as const).some(([dx, dy]) => {
      for (let k = 4; ; k += 4) {
        const px = x + dx * k, py = y + dy * k;
        if (px < vb.x || px > vb.x + vb.width || py > vb.y + vb.height) return true;
        if (onLand(px, py)) return false;
      }
    });
    const inView = (b: Box) => b.x > vb.x + 2 && b.y > vb.y + 2 && b.x + b.w < vb.x + vb.width - 2 && b.y + b.h < vb.y + vb.height - 2;
    const inOcean = (b: Box) => inView(b) && !samples(b).some(([x, y]) => onLand(x, y)) && toSea(b.x + b.w / 2, b.y + b.h / 2);
    const boxHits = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
    const fitsIn = (g: SVGGeometryElement, b: Box) => samples(b).filter(([x, y]) => !inFill(g, x, y)).length <= 1;

    const placed: Box[] = [];            // label blocks + capital dots
    const lines: [number, number, number, number][] = [];
    const pad = (b: Box, m = 1.5): Box => ({ x: b.x - m, y: b.y - m, w: b.w + 2 * m, h: b.h + 2 * m });
    const free = (b: Box) => !placed.some((r) => boxHits(pad(b), r));
    // Segment tests for pointer lines: crossing another line, or passing through a box.
    const cross = (a: number[], b: number[]) => {
      const o = (px: number, py: number, qx: number, qy: number, rx: number, ry: number) => Math.sign((qx - px) * (ry - py) - (qy - py) * (rx - px));
      return o(a[0], a[1], a[2], a[3], b[0], b[1]) * o(a[0], a[1], a[2], a[3], b[2], b[3]) < 0 &&
             o(b[0], b[1], b[2], b[3], a[0], a[1]) * o(b[0], b[1], b[2], b[3], a[2], a[3]) < 0;
    };
    const segThrough = (s: number[], b: Box) => {
      const n = Math.max(2, Math.ceil(Math.hypot(s[2] - s[0], s[3] - s[1]) / 1.5));
      for (let i = 1; i < n; i++) {
        const x = s[0] + ((s[2] - s[0]) * i) / n, y = s[1] + ((s[3] - s[1]) * i) / n;
        if (x > b.x && x < b.x + b.w && y > b.y && y < b.y + b.h) return true;
      }
      return false;
    };
    const lineTo = (b: Box, ax: number, ay: number) =>
      [ax, ay, Math.max(b.x, Math.min(ax, b.x + b.w)), Math.max(b.y, Math.min(ay, b.y + b.h))];
    const lineClear = (seg: number[], own: Box, ownDot: Box) =>
      !lines.some((l) => cross(seg, l)) && !placed.some((r) => r !== own && r !== ownDot && segThrough(seg, r));

    // Label block: name line(s) with the capital centred beneath. Measured once; positioned by centre.
    const measure = (txt: string, fs: number, cls: string) => {
      const t = svgEl("text", { "font-size": String(fs), "text-anchor": "middle", "dominant-baseline": "central", class: cls });
      t.textContent = txt; svg.appendChild(t);
      const m = (t as unknown as SVGGraphicsElement).getBBox();
      return { t, w: m.width, h: m.height };
    };
    type Block = { g: SVGElement; parts: { t: SVGElement; w: number; h: number }[]; w: number; h: number };
    const makeBlock = (nameLines: string[], cap: string): Block => {
      const g = svgEl("g", { class: "state-label" });
      svg.appendChild(g);
      const parts = [...nameLines.map((l) => measure(l, NAME_FS, "state-name")), measure(cap, CAP_FS, "cap-label")];
      parts.forEach((p) => g.appendChild(p.t));
      const h = parts.reduce((sum, p) => sum + p.h * 0.92, 0);
      return { g, parts, w: Math.max(...parts.map((p) => p.w)), h };
    };
    const boxAt = (k: Block, cx: number, cy: number): Box => ({ x: cx - k.w / 2, y: cy - k.h / 2, w: k.w, h: k.h });
    const putBlock = (k: Block, b: Box) => {
      let y = b.y;
      for (const p of k.parts) {
        p.t.setAttribute("x", (b.x + b.w / 2).toFixed(1));
        p.t.setAttribute("y", (y + (p.h * 0.92) / 2).toFixed(1));
        y += p.h * 0.92;
      }
      placed.push(b);
    };
    const pointer = (k: Block, b: Box, ax: number, ay: number) => {
      const seg = lineTo(b, ax, ay);
      lines.push(seg as [number, number, number, number]);
      k.g.insertBefore(svgEl("line", { x1: seg[0].toFixed(1), y1: seg[1].toFixed(1), x2: seg[2].toFixed(1), y2: seg[3].toFixed(1), class: "callout-line" }), k.g.firstChild);
    };

    // Capital dots at their real spot, snapped onto land (nearest point inside the state).
    const items = paths.map((p) => {
      const c = STATE_CAPITALS[p.code];
      let x = p.bb.x + c.fx * p.bb.width, y = p.bb.y + c.fy * p.bb.height;
      if (!inFill(p.g, x, y)) {
        let best: [number, number] | null = null, bd = Infinity;
        for (let dx = -30; dx <= 30; dx += 1) for (let dy = -30; dy <= 30; dy += 1) {
          const d = Math.hypot(dx, dy);
          if (d < bd && inFill(p.g, x + dx, y + dy) && inFill(p.g, x + dx + 1.5, y + dy) && inFill(p.g, x + dx - 1.5, y + dy)) { bd = d; best = [x + dx, y + dy]; }
        }
        if (best) [x, y] = best;
      }
      const dotBox: Box = { x: x - 2.5, y: y - 2.5, w: 5, h: 5 };
      placed.push(dotBox);
      const dg = svgEl("g", { class: "capital" });
      dg.appendChild(svgEl("circle", { cx: x.toFixed(1), cy: y.toFixed(1), r: "2.3", class: "cap-dot" }));
      svg.appendChild(dg);
      return { ...p, cap: c.cap, x, y, dotBox };
    });

    // Decor: ships / steamers / compass in open water near a few target spots (reserved so labels avoid them).
    const decor: [number, number, number, string][] = [   // fx, fy of viewBox, size, kind
      [0.12, 0.40, 115, "ship"], [0.86, 0.62, 130, "ship"], [0.55, 0.88, 105, "ship"],
      [0.93, 0.86, 110, "compass"], [0.87, 0.36, 100, "steam"], [0.72, 0.9, 90, "steam"],
    ];
    for (const [fx, fy, size, kind] of decor) {
      const want = { x: vb.x + fx * vb.width, y: vb.y + fy * vb.height };
      let spot: Box | null = null;
      for (let r = 0; r <= 160 && !spot; r += 8)
        for (let k = 0; k < (r ? 16 : 1) && !spot; k++) {
          const a = (k / 16) * 2 * Math.PI, b: Box = { x: want.x + Math.cos(a) * r - size / 2, y: want.y + Math.sin(a) * r - size / 2, w: size, h: size };
          if (inOcean(b) && free(b)) spot = b;
        }
      if (!spot) continue;
      placed.push(spot);
      const g = svgEl("g", { class: "deco", transform: `translate(${spot.x.toFixed(1)} ${spot.y.toFixed(1)}) scale(${(size / 106).toFixed(3)})` });
      g.innerHTML = kind === "ship" ? App.shipSvg() : kind === "steam" ? App.steamSvg() : App.compassSvg();
      svg.insertBefore(g, svg.firstChild?.nextSibling ?? null);
    }

    // Pass 1: blocks that fit inside their own state (centre outward; two-word names may wrap).
    const leftovers: (typeof items[number])[] = [];
    for (const it of [...items].sort((u, v) => u.bb.width * u.bb.height - v.bb.width * v.bb.height)) {
      const name = stateName(it.code);
      const cx0 = it.bb.x + it.bb.width / 2, cy0 = it.bb.y + it.bb.height / 2;
      const tries: [number, number][] = [];
      for (let i = 1; i < 10; i++) for (let j = 1; j < 10; j++) tries.push([it.bb.x + (i / 10) * it.bb.width, it.bb.y + (j / 10) * it.bb.height]);
      tries.sort((u, v) => Math.hypot(u[0] - cx0, u[1] - cy0) - Math.hypot(v[0] - cx0, v[1] - cy0));
      let done = false;
      for (const nameLines of name.includes(" ") ? [[name], name.split(" ")] : [[name]]) {
        const k = makeBlock(nameLines, it.cap);
        const hit = tries.map(([x, y]) => boxAt(k, x, y)).find((b) => fitsIn(it.g, b) && free(b));
        if (hit) { putBlock(k, hit); done = true; break; }
        k.g.remove();
      }
      if (!done) leftovers.push(it);
    }

    // Pass 2: east-coast leftovers → a left-aligned column in the Atlantic, rows in north→south order
    // of their dots; rows are swapped until no two pointer lines cross.
    // Only states whose capital is within ~45 units of the Atlantic due east (the crowded northeast).
    const nearEastCoast = (x: number, y: number) => {
      for (let k = 0; k <= 45; k += 3) {
        if (onLand(x + k, y)) continue;
        for (let e = x + k; ; e += 4) { if (e > vb.x + vb.width) return true; if (onLand(e, y)) break; }
      }
      return false;
    };
    let east = leftovers.filter((o) => o.code !== "HI" && o.code !== "AK" && nearEastCoast(o.x, o.y)).sort((u, v) => u.y - v.y);
    // A capital far west of the column (e.g. Columbia, SC) would need a long pointer — label it near home instead.
    const colEdge = Math.max(...east.map((o) => o.x), -Infinity);
    east = east.filter((o) => colEdge - o.x < 70 || o.code === "MD");   // Maryland too: on land it crowds the Chesapeake states
    const rest = leftovers.filter((o) => !east.includes(o));
    if (east.length) {
      const blocks = east.map((o) => makeBlock([stateName(o.code)], o.cap));
      const rowH = Math.max(...blocks.map((k) => k.h)) + 3;
      const rowY: number[] = [];
      east.forEach((o, i) => rowY.push(Math.max(o.y, i ? rowY[i - 1] + rowH : -Infinity)));
      // Column x: far enough east that every row is in open water and clear of other labels.
      let X = Math.max(...east.map((o) => o.x)) + 8;
      const rowOk = (k: Block, y: number) => { const b: Box = { x: X, y: y - k.h / 2, w: k.w, h: k.h }; return inOcean(b) && free(b); };
      while (X < vb.x + vb.width && !blocks.every((k, i) => rowOk(k, rowY[i]))) X += 2;
      const order = east.map((_, i) => i);   // order[row] = which state sits in that row
      const segFor = (row: number) => {
        const k = blocks[order[row]], o = east[order[row]];
        return lineTo({ x: X, y: rowY[row] - k.h / 2, w: k.w, h: k.h }, o.x, o.y);
      };
      for (let pass = 0; pass < 60; pass++) {
        let swapped = false;
        for (let i = 0; i < order.length && !swapped; i++)
          for (let j = i + 1; j < order.length && !swapped; j++)
            if (cross(segFor(i), segFor(j))) { [order[i], order[j]] = [order[j], order[i]]; swapped = true; }
        if (!swapped) break;
      }
      order.forEach((idx, row) => {
        const k = blocks[idx], o = east[idx];
        const b: Box = { x: X, y: rowY[row] - k.h / 2, w: k.w, h: k.h };
        putBlock(k, b);
        pointer(k, b, o.x, o.y);
      });
    }

    // Pass 3: other leftovers (inland, Hawaii, Alaska) → nearest clear spot whose pointer crosses nothing.
    for (const o of rest) {
      let k = makeBlock([stateName(o.code)], o.cap);
      let found: Box | null = null;
      for (let r = 10; r <= 120 && !found; r += 4)
        for (let a = 0; a < 32 && !found; a++) {
          const ang = (a / 32) * 2 * Math.PI;
          const b = boxAt(k, o.x + Math.cos(ang) * (r + k.w / 2), o.y + Math.sin(ang) * (r + k.h / 2));
          if (inView(b) && free(b) && !lines.some((l) => segThrough(l, pad(b))) && lineClear(lineTo(b, o.x, o.y), b, o.dotBox)) found = b;
        }
      if (!found) { k.g.remove(); k = makeBlock(stateName(o.code).split(" "), o.cap); found = boxAt(k, o.x, o.y + 10 + k.h / 2); }
      putBlock(k, found);
      pointer(k, found, o.x, o.y);
    }
    if (!wasShown) svg.classList.remove("show-cities");
    this._mapLabeled = true;
  }
  // Outline the clicked state so you can see where it is. Drawn as a copy of its outline just above
  // the state shapes (below the labels), so neighbouring states can't cover the stroke.
  private markSelected(code: string | null): void {
    const svg = this._modalSvg; if (!svg) return;
    svg.querySelector(".sel-ring")?.remove();
    const p = code ? this.modalPaths.get(code) : undefined; if (!p) return;
    const d = p.getAttribute("d") ?? "";
    const g = svgEl("g", { class: "sel-ring" });
    g.appendChild(svgEl("path", { d, class: "sel-out" }));
    g.appendChild(svgEl("path", { d, class: "sel-in" }));
    const last = [...this.modalPaths.values()].pop()!;
    svg.insertBefore(g, last.nextSibling);
  }
  private colourAll(code: string, colour: string): void {
    this.modalPaths.get(code) && (this.modalPaths.get(code)!.style.fill = colour);
    this.miniPaths.get(code) && (this.miniPaths.get(code)!.style.fill = colour);
  }
  onMapPick(code: string): void {
    if (this.suppressClick) return; // a pan drag just ended — don't treat as a claim
    if (this.mode !== "map-select" || !this.selectable.has(code)) return;
    this.askClaim(code);
  }
  // Clicking an open state only selects it; the claim happens on "Yes" in the confirm box.
  // Drawn in place (no renderMap) so the player's zoom/pan is kept while they decide.
  private askClaim(code: string): void {
    this.pendingPick = code;
    for (const [c, p] of this.modalPaths) p.classList.toggle("pending", c === code);
    const wrap = this._modalSvg?.parentElement; if (!wrap) return;
    wrap.querySelector(".claim-confirm")?.remove();
    const mine = this.board?.players.find((p) => p.id === "you")?.states ?? [];
    const contig = mine.some((s) => borders(s, code));
    const cap = this.capitalMode;
    wrap.appendChild(h("div", { class: "claim-confirm", on: { click: (e) => e.stopPropagation(), pointerdown: (e) => e.stopPropagation() } }, [
      h("div", { class: "cc-q", text: cap ? `Make ${capitalPlace(code)} your capital?` : `Claim ${stateName(code)}?` }),
      cap ? h("div", { class: "cc-bonus", text: "Ceremonial: scores and states stay as they are" }) : contig ? h("div", { class: "cc-bonus", text: "Borders your territory — +1 border bonus" }) : null,
      h("div", { class: "cc-btns" }, [
        h("button", { class: "btn primary", text: cap ? "Yes, make it the capital" : "Yes, claim it", on: { click: () => this.confirmClaim() } }),
        h("button", { class: "btn", text: "No", on: { click: () => this.cancelClaim() } }),
      ]),
    ]));
  }
  private cancelClaim(): void {
    this.pendingPick = null;
    for (const p of this.modalPaths.values()) p.classList.remove("pending");
    this._modalSvg?.parentElement?.querySelector(".claim-confirm")?.remove();
  }
  private confirmClaim(): void {
    const code = this.pendingPick;
    if (!code || this.mode !== "map-select" || !this.selectable.has(code)) return;
    this.cancelClaim();
    if (this.capitalMode) {   // once: the President's choice goes in and the map waits for the inauguration
      this.capitalMode = false; this.claimMode = false; this.mode = "idle"; this.selectable.clear();
      this.human.submitCapital(code);
      return;
    }
    // Stay on the map after claiming: switch to review mode and let the engine colour
    // the state (which fires onStateWon, where we celebrate + wait for "Continue").
    this.mode = "idle"; this.claimMode = false; this.claimReview = true;
    this.selectable.clear(); this.contigNote = null;
    this.human.submitState(code);
  }
  // ---- map zoom / pan ----
  private applyMapTransform(): void {
    if (!this._modalSvg) return;
    this._modalSvg.style.transformOrigin = "center center";
    this._modalSvg.style.transform = `translate(${this.mapPanX}px, ${this.mapPanY}px) scale(${this.mapZoom})`;
    this._modalSvg.parentElement?.classList.toggle("zoomed", this.mapZoom > 1);
    this._modalSvg.classList.toggle("show-cities", this.mapZoom > 1.01); // names + capitals from the first zoom step
  }
  private clampPan(): void {
    const wrap = this._modalSvg?.parentElement;
    if (!wrap) return;
    const r = wrap.getBoundingClientRect();
    const maxX = ((this.mapZoom - 1) * r.width) / 2;
    const maxY = ((this.mapZoom - 1) * r.height) / 2;
    this.mapPanX = Math.max(-maxX, Math.min(maxX, this.mapPanX));
    this.mapPanY = Math.max(-maxY, Math.min(maxY, this.mapPanY));
  }
  zoomBy(d: number): void {
    this.mapZoom = Math.max(1, Math.min(4, +(this.mapZoom + d).toFixed(2)));
    if (this.mapZoom === 1) { this.mapPanX = 0; this.mapPanY = 0; }
    this.clampPan();
    this.applyMapTransform();
  }
  resetZoom(): void { this.mapZoom = 1; this.mapPanX = 0; this.mapPanY = 0; this.applyMapTransform(); }
  private attachPan(wrap: HTMLElement): void {
    let sx = 0, sy = 0, panning = false, moved = false;
    wrap.addEventListener("pointerdown", (e) => {
      if (this.mapZoom <= 1 || (e.target as Element).closest(".map-zoom")) return;
      panning = true; moved = false; sx = e.clientX - this.mapPanX; sy = e.clientY - this.mapPanY;
      // NB: do NOT setPointerCapture — capturing swallows the click on state paths.
    });
    wrap.addEventListener("pointermove", (e) => {
      if (!panning) return;
      const nx = e.clientX - sx, ny = e.clientY - sy;
      if (Math.abs(nx - this.mapPanX) + Math.abs(ny - this.mapPanY) > 3) moved = true;
      this.mapPanX = nx; this.mapPanY = ny; this.clampPan(); this.applyMapTransform();
    });
    const end = () => { if (panning && moved) { this.suppressClick = true; setTimeout(() => (this.suppressClick = false), 30); } panning = false; };
    wrap.addEventListener("pointerup", end);
    wrap.addEventListener("pointerleave", end);
    // Two fingers: pinch to zoom, keeping the spot between your fingers where it is (and drag with both).
    let pinch: { d: number; z: number; px: number; py: number } | null = null;
    const two = (e: TouchEvent) => {
      const [a, b] = [e.touches[0], e.touches[1]], r = wrap.getBoundingClientRect();
      return { d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1,
        mx: (a.clientX + b.clientX) / 2 - (r.left + r.width / 2), my: (a.clientY + b.clientY) / 2 - (r.top + r.height / 2) };
    };
    wrap.addEventListener("touchstart", (e) => {
      if (e.touches.length !== 2) return;
      panning = false;
      const t = two(e);
      pinch = { d: t.d, z: this.mapZoom, px: (t.mx - this.mapPanX) / this.mapZoom, py: (t.my - this.mapPanY) / this.mapZoom };
    }, { passive: true });
    wrap.addEventListener("touchmove", (e) => {
      if (!pinch || e.touches.length !== 2) return;
      e.preventDefault();
      const t = two(e);
      this.mapZoom = Math.max(1, Math.min(4, pinch.z * (t.d / pinch.d)));
      this.mapPanX = t.mx - this.mapZoom * pinch.px; this.mapPanY = t.my - this.mapZoom * pinch.py;
      this.clampPan(); this.applyMapTransform();
      this.suppressClick = true;
    }, { passive: false });
    wrap.addEventListener("touchend", (e) => {
      if (!pinch || e.touches.length >= 2) return;
      pinch = null;
      if (this.mapZoom < 1.05) this.resetZoom();
      setTimeout(() => (this.suppressClick = false), 60);
    });
  }
  openMap(elevated = false): void { this.mapOpen = true; this.renderMap(); const m = $("map-modal"); m.style.zIndex = elevated ? "90" : ""; }
  closeMap(): void { if (this.claimMode || this.claimReview) return; this.mapOpen = false; const m = $("map-modal"); m.classList.add("hidden"); m.style.zIndex = ""; $("map-tip").hidden = true; }
  private reopenShowdown(): void { if (this.lastRecord) void this.showShowdownModal(this.lastRecord, this.players[this.lastRecord.winnerSeat].id === "you", true, undefined, true); }
  private renderMap(): void {
    const modal = $("map-modal");
    if (!this.mapOpen) { modal.classList.add("hidden"); return; }
    const claimed = this.claimedMap();
    for (const [code, p] of this.modalPaths) {
      const owner = claimed[code]; const isTarget = this.targetState === code;
      const pick = this.claimMode && (this.capitalMode ? this.selectable.has(code) : !owner);
      p.style.fill = code === this.colouringCode ? "" : owner ? owner.colour : isTarget ? "#5a3b0a" : ""; // "" = unclaimed: use the theme's CSS fill
      p.setAttribute("stroke", pick ? "#c28d41" : isTarget ? "#e1ad66" : "#14100e");
      p.setAttribute("stroke-width", pick || isTarget ? "1.6" : "0.9");
      p.classList.toggle("pick", pick);
      p.classList.toggle("pres-own", !!this.capitalWatch?.owned.includes(code));
    }
    const total = this.modalPaths.size; const cc = Object.keys(claimed).length;
    // each player's Drought: hands since they last won a state (the longest is marked)
    const droughts = (this.board?.players ?? []).map((p) => this.drought(p.id));
    const longest = Math.max(0, ...droughts);
    const legend = (this.board?.players ?? []).map((p, i) => {
      const d = droughts[i], you = p.id === "you", who = you ? "you" : p.name;
      return h("div", { class: "legend-p" + (you ? " lg-you" : ""), style: `border-left-color:${p.colour}` }, [
        h("span", { class: "lg-nm" }, [p.name]),
        h("span", { class: "heading tnum lg-sc", text: `${p.score}` }),
        h("span", { class: "lg-pts", text: "pts" }),
        h("span", { class: "tnum lg-st", text: `${p.states.length} ${p.states.length === 1 ? "state" : "states"}` }),
        this.lastClaim ? h("span", { class: "tnum lg-dr" + (d > 0 && d === longest ? " long" : ""),
          attr: { title: d ? `${d} ${d === 1 ? "hand" : "hands"} since ${who} last won a state${this.lastWin.has(p.id) ? "" : " (none yet this game)"}` : `${you ? "You" : p.name} won the last state` },
          html: d ? `<span class="dr-l">Drought </span>${d}` : `<span class="dr-l">Just won</span><span class="dr-s">✓</span>` }) : null,
      ]);
    });
    const myDrought = this.players.some((p) => p.id === "you") && this.lastClaim ? this.drought("you") : -1;
    const myDroughtEl = myDrought >= 0 ? h("div", { class: "my-drought" + (myDrought >= 5 ? " dry" : "") }, [
      h("span", { class: "kicker", text: "Your drought" }),
      h("span", { class: "heading tnum", text: myDrought ? `${myDrought} ${myDrought === 1 ? "hand" : "hands"}` : "You just won a state" }),
      myDrought ? h("span", { class: "md-sub", text: this.lastWin.has("you") ? `since you last won a state (hand ${this.lastWin.get("you")})` : "and no state yet this game" }) : null,
    ]) : null;
    this.mapZoom = 1; this.mapPanX = 0; this.mapPanY = 0; // fresh view each open
    const svgWrap = h("div", { class: "map-svg-wrap" }, [this._modalSvg!]);
    if (this.claimMode) svgWrap.appendChild(h("div", { class: "claim-hint" }, [h("span", { text: this.capitalMode ? "Choose one of your states as your capital" : "Click an open state to claim it" })]));
    if (this.claimMode && this.pendingPick) requestAnimationFrame(() => this.pendingPick && this.askClaim(this.pendingPick));
    svgWrap.appendChild(h("div", { class: "map-zoom" }, [
      h("button", { class: "zbtn", text: "+", attr: { title: "Zoom in" }, on: { click: (e) => { e.stopPropagation(); this.zoomBy(0.5); } } }),
      h("button", { class: "zbtn", text: "−", attr: { title: "Zoom out" }, on: { click: (e) => { e.stopPropagation(); this.zoomBy(-0.5); } } }),
      h("button", { class: "zbtn small", text: "⤢", attr: { title: "Reset zoom" }, on: { click: (e) => { e.stopPropagation(); this.resetZoom(); } } }),
    ]));
    this.attachPan(svgWrap);

    modal.className = "map-backdrop";
    modal.innerHTML = "";
    if (COMPACT.matches) { this.renderMapCompact(modal, svgWrap, myDroughtEl ? [myDroughtEl, ...legend] : legend, cc, total); return; }
    modal.appendChild(h("div", { class: "map-shell", on: { click: (e) => e.stopPropagation() } }, [
      h("div", { style: "display:flex;align-items:baseline;justify-content:space-between;border-bottom:1px solid var(--n800);padding-bottom:var(--s2)" }, [
        h("div", {}, [h("div", { class: "kicker", text: "The board" }), h("div", { class: "sheet-title", text: this.claimReview ? `You claimed ${this.focusCode ? stateName(this.focusCode) : "your state"}` : this.aiPickNote ? this.aiPickNote.title : this.capitalMode ? "Choose your capital" : this.claimMode ? "Claim your state" : "Fifty states, fifty hands" })]),
        h("div", { class: "row" }, [
          h("span", { class: "tnum", style: "font-size:16px;color:var(--n100)", text: `${cc} claimed · ${total - cc} open`, attr: { id: "map-remain" } }),
          h("button", { class: "map-hof-btn", text: "🏆 Hall of Fame", attr: { "data-hof": "", title: "Leaderboards, records and every game ever played" } }),
          // No × while you still have to pick a state (claim-select). During a review (your claim
          // or an AI's pick) the × acts as "Continue to next hand"; otherwise it just closes the board.
          this.claimMode ? null : h("button", { style: "background:none;border:none;color:var(--n200);font-size:30px;font-weight:700;line-height:1;cursor:pointer;padding:0 6px", text: "×", attr: { title: (this.claimReview || this.aiPickNote) ? "Continue to next hand" : "Close" }, on: { click: (e) => { e.stopPropagation(); if (this.claimReview || this.aiPickNote) this.rClaimContinue?.(); else this.closeMap(); } } }),
        ]),
      ]),
      // Map fills everything left of the side column, top to bottom. All the reading — scores,
      // notes, the selected state, its winning hand and fact, and the buttons — lives on the right.
      h("div", { class: "map-row" }, [svgWrap, h("div", { class: "map-side" }, [
        myDroughtEl,
        h("div", { class: "legend-col" }, legend),
        this.capitalWatch ? h("div", { class: "pick-note" }, [
          h("div", { class: "cn-title", text: `🏛️ President ${this.capitalWatch.name} is choosing a capital` }),
          h("div", { class: "cn-sub", text: `It will be one of their ${this.capitalWatch.owned.length} states, lit up on the map.` }),
        ]) : null,
        this.contigNote ? h("div", { class: "contig-note" }, [
          h("div", { class: "cn-title", text: this.contigNote.title }),
          h("div", { class: "cn-sub", text: this.contigNote.sub }),
        ]) : null,
        this.aiPickNote ? h("div", { class: this.aiPickNote.contig ? "contig-note" : "pick-note" }, [
          h("div", { class: "cn-title", text: `🗺️ ${this.aiPickNote.title}` }),
          h("div", { class: "cn-sub", text: this.aiPickNote.contig ? `A contiguous state — it borders ${this.aiPickNote.yours ? "your" : "their"} territory (+1 bonus)` : "The hand winner claims a state" }),
        ]) : null,
        h("div", { class: "map-read" }, [
          h("span", { class: "sheet-title", style: "font-size:30px;line-height:1.1", attr: { id: "map-read-name" }, text: "—" }),
          h("span", { style: "font-size:19px;color:var(--a300)", attr: { id: "map-read-cap" } }),
          h("span", { style: "font-size:18px;color:var(--n300)", attr: { id: "map-read-sub" }, text: "Click a state" }),
          h("div", { style: "font-size:19px;color:var(--n200)", attr: { id: "map-read-won" } }),
          h("div", { style: "font-size:18px;color:var(--n300)", attr: { id: "map-read-hand" } }),
        ]),
        h("div", { attr: { id: "map-discover" }, class: "discover-box" }, [
          h("div", { class: "disc-text", attr: { id: "map-discover-text" } }),
          h("div", { class: "disc-meta" }, [
            h("div", { class: "disc-cat-line" }, [h("span", { class: "disc-lbl", text: "Category: " }), h("span", { attr: { id: "map-discover-cat" } })]),
            h("a", { class: "disc-src", attr: { id: "map-discover-src", href: "#", target: "_blank", rel: "noopener" }, text: "Source ↗" }),
          ]),
        ]),
        h("div", { class: "map-actions" }, (this.claimReview || this.aiPickNote) ? [
          h("button", { class: "btn primary", text: "Continue to next hand →", on: { click: (e) => { e.stopPropagation(); this.rClaimContinue?.(); } } }),
          h("button", { class: "btn", text: "See hand results", on: { click: (e) => { e.stopPropagation(); this.reopenShowdown(); } } }),
        ] : (!this.claimMode && this.mapContinue) ? [
          h("button", { class: "btn primary", text: "Continue to next hand →", on: { click: (e) => { e.stopPropagation(); this.mapContinue?.(); } } }),
        ] : []),
      ])]),
    ]));
    modal.onclick = () => this.closeMap();
    this.labelModalMap();   // add state-name + capital labels now the SVG is in the DOM (once)
    this.applyMapTransform();
    this.renderMapReadout();
  }
  // Phone held sideways: the board fills the screen. A slim bar on top says what just happened; the
  // state's details and the scores open as panels from the buttons along the bottom.
  private cmapPanel: "" | "details" | "scores" = "";
  private renderMapCompact(modal: HTMLElement, svgWrap: HTMLElement, legend: HTMLElement[], cc: number, total: number): void {
    const review = this.claimReview || !!this.aiPickNote;
    const title = this.claimReview ? `You claimed ${this.focusCode ? stateName(this.focusCode) : "your state"}` : this.aiPickNote ? this.aiPickNote.title
      : this.capitalMode ? "You're President: choose your capital" : this.claimMode ? "Claim your state" : "The board";
    const note = this.contigNote ? this.contigNote.sub : this.aiPickNote?.contig ? `A contiguous state: it borders ${this.aiPickNote.yours ? "your" : "their"} territory (+1 bonus)` : "";
    const panels = new Map<string, HTMLElement>();
    const setPanel = (k: "" | "details" | "scores") => {
      this.cmapPanel = k;
      for (const [id, el] of panels) el.hidden = id !== k;
      modal.querySelectorAll<HTMLElement>("[data-panel]").forEach((b) => b.classList.toggle("on", b.dataset.panel === k));
    };
    this.cmapOpenDetails = () => setPanel("details");
    const stop = (fn: () => void) => (e: Event) => { e.stopPropagation(); fn(); };
    const barBtn = (label: string, ico: string, fn: () => void, cls = "", panel = "") => h("button", { class: "cm-btn" + cls, attr: { type: "button", ...(panel ? { "data-panel": panel } : {}) },
      html: `${ico}<span>${label}</span>`, on: { click: stop(fn) } });
    panels.set("details", h("div", { class: "cm-panel" }, [
      h("div", { class: "map-read" }, [
        h("span", { class: "sheet-title", attr: { id: "map-read-name" }, text: "—" }),
        h("span", { class: "cm-cap", attr: { id: "map-read-cap" } }),
        h("span", { class: "cm-dim", attr: { id: "map-read-sub" }, text: "Tap a state" }),
        h("div", { attr: { id: "map-read-won" } }),
        h("div", { attr: { id: "map-read-hand" } }),
      ]),
      h("div", { attr: { id: "map-discover" }, class: "discover-box" }, [
        h("div", { class: "disc-text", attr: { id: "map-discover-text" } }),
        h("div", { class: "disc-meta" }, [
          h("div", { class: "disc-cat-line" }, [h("span", { class: "disc-lbl", text: "Category: " }), h("span", { attr: { id: "map-discover-cat" } })]),
          h("a", { class: "disc-src", attr: { id: "map-discover-src", href: "#", target: "_blank", rel: "noopener" }, text: "Source ↗" }),
        ]),
      ]),
    ]));
    panels.set("scores", h("div", { class: "cm-panel legend-col" }, legend));
    const next = review ? () => this.rClaimContinue?.() : this.mapContinue && !this.claimMode ? () => this.mapContinue?.() : null;
    modal.appendChild(h("div", { class: "map-shell cmap", on: { click: (e) => e.stopPropagation() } }, [
      svgWrap,
      h("div", { class: "cm-top" }, [
        h("span", { class: "cm-title", html: `${CI.map}<span>${title}</span>` }),
        h("span", { class: "cm-count tnum", attr: { id: "map-remain" }, text: `${cc} claimed · ${total - cc} open` }),
        h("button", { class: "cm-ico", html: "🏆", attr: { type: "button", "data-hof": "", "aria-label": "Hall of Fame", title: "Hall of Fame" } }),
        this.claimMode ? null : h("button", { class: "cm-ico", text: "×", attr: { type: "button", "aria-label": review ? "Continue to next hand" : "Close", title: review ? "Continue to next hand" : "Close" },
          on: { click: stop(() => { if (review) this.rClaimContinue?.(); else this.closeMap(); }) } }),
      ]),
      note ? h("div", { class: "cm-note", text: `🎊 ${note}` }) : null,
      ...panels.values(),
      h("div", { class: "cm-bar" }, [
        barBtn("State details", CI.map, () => setPanel(this.cmapPanel === "details" ? "" : "details"), "", "details"),
        barBtn("Scores", CI.trophy, () => setPanel(this.cmapPanel === "scores" ? "" : "scores"), "", "scores"),
        this.lastRecord ? barBtn("Hand results", CI.list, () => this.reopenShowdown()) : null,
        h("span", { class: "cb-sp" }),
        next ? barBtn("Next hand", CI.pass, next, " gold") : this.claimMode ? null : barBtn("Back to the game", CI.undo, () => this.closeMap(), " gold"),
      ]),
    ]));
    setPanel(this.cmapPanel);
    modal.onclick = () => this.closeMap();
    this.labelModalMap();
    this.applyMapTransform();
    this.renderMapReadout();
  }
  private cmapOpenDetails: (() => void) | null = null;
  private renderMapReadout(): void {
    if (!this.mapOpen) return;
    const claimed = this.claimedMap();
    const code = this.focusCode; const owner = code ? claimed[code] : null;
    const info = code ? this.stateInfo.get(code) : undefined;
    // A state's fact is revealed only once it has been won (stored in stateInfo). Unclaimed
    // states show no fact yet — you unlock it by winning the state.
    const fact = info?.fact;
    this.markSelected(code);
    const nm = $("map-read-name"); const sub = $("map-read-sub");
    const hand = document.getElementById("map-read-hand");
    if (nm) nm.textContent = code ? stateName(code) : "—";
    const capEl = document.getElementById("map-read-cap");
    if (capEl) capEl.textContent = code && STATE_CAPITALS[code] ? `Capital: ${STATE_CAPITALS[code].cap}` : "";
    // Left, under the state + capital: who won it and with what hand; then the cards and points.
    // Right: only the status of a state nobody holds yet.
    const won = document.getElementById("map-read-won");
    if (won) won.innerHTML = owner ? `Won by <b style="color:${owner.colour}">${owner.name}</b>` +
      (info ? ` with ${/^(Two Pair|Zipper)$/.test(info.cat) ? "" : "a "}<b style="color:var(--n100)">${info.cat}</b>` : ` · ${owner.party}`) +
      (info ? `<div class="map-read-handno">Hand ${info.hand} of 50</div>` : "") +
      (info?.quock ? `<div class="map-read-quock">${info.quock} quocked this hand</div>` : "") : "";
    if (sub) sub.textContent = !code ? "Click a state" : owner ? "" : this.claimMode ? "Open — click to claim" : "Unclaimed";
    if (hand) hand.innerHTML = info ? `<div class="map-hand">${info.cards}<b class="map-pts">${info.points} pts</b></div>` : "";
    const box = document.getElementById("map-discover");
    const text = document.getElementById("map-discover-text");
    const meta = box?.querySelector(".disc-meta") as HTMLElement | null;
    const catEl = document.getElementById("map-discover-cat");
    const a = document.getElementById("map-discover-src") as HTMLAnchorElement | null;
    if (fact) {
      if (box) box.hidden = false;
      if (text) text.textContent = fact.text;
      if (catEl) catEl.textContent = fact.category.replace(/_/g, " ");
      if (meta) meta.hidden = false;
      if (a) { a.href = fact.source.url; a.title = fact.source.title; }
    } else {
      if (text) text.textContent = code ? `Win ${stateName(code)} to reveal a fact about it.` : "Click a state on the board.";
      if (meta) meta.hidden = true;
    }
  }

  // ---- banners / toast ----
  private bannerTok = 0;
  private showBanner(title: string, sub: string, opts: { win?: boolean; knock?: boolean; low?: boolean; over?: boolean; ms?: number; action?: { label: string; onClick: () => void } } = {}): void {
    const el = $("banner");
    el.className = [opts.win ? "win" : opts.knock ? "knock" : "", opts.low ? "low" : "", opts.over ? "over" : ""].filter(Boolean).join(" ");
    el.classList.remove("hidden", "fade");
    el.innerHTML = `<div class="bi">${title}<div class="bs">${sub}</div>${opts.action ? `<button class="banner-btn" id="banner-act">${opts.action.label}</button>` : ""}</div>`;
    if (opts.action) { const b = document.getElementById("banner-act"); if (b) b.onclick = opts.action!.onClick; }
    // "low" banners (new hand) sit on the felt just under the middle cards, clear of your own hand
    const under = opts.low ? document.querySelector(".center .cardrow")?.getBoundingClientRect() : undefined;
    el.classList.toggle("ontable", !!under);
    if (under) { el.style.setProperty("--bx", `${under.left + under.width / 2}px`); el.style.setProperty("--by", `${under.bottom + 2}px`); }
    const tok = ++this.bannerTok;
    setTimeout(() => { if (tok !== this.bannerTok) return; el.classList.add("fade");
      setTimeout(() => { if (tok === this.bannerTok) el.classList.add("hidden"); }, 480); }, opts.ms ?? 1400);
  }
  private confetti(colour: string, slow = false, count = 110): void {
    const host = $("confetti");
    const cols = [colour, "#e1ad66", "#facb8d", "#f8f4f4", "#c28d41"];
    for (let i = 0; i < count; i++) {
      const c = document.createElement("div");
      c.className = "confo";
      c.style.left = Math.random() * 100 + "vw";
      c.style.background = cols[i % cols.length];
      const dur = (slow ? 4.6 : 2.4) + Math.random() * (slow ? 2.6 : 1.8);
      c.style.animation = `fall ${dur}s linear ${Math.random() * (slow ? 1.1 : 0.5)}s forwards`;
      c.style.transform = `translateY(-20px) rotate(${Math.random() * 360}deg)`;
      host.appendChild(c);
      setTimeout(() => c.remove(), (dur + 1.2) * 1000);
    }
  }
  private avatar: AvatarCfg = loadAvatar();
  private audioCtx?: AudioContext;
  private playDeal(count: number, gapMs = 150): void {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx ||= new Ctx();
      const ctx = this.audioCtx;
      if (ctx.state === "suspended") void ctx.resume();
      const noise = (dur: number) => {
        const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
        const src = ctx.createBufferSource(); src.buffer = buf;
        return src;
      };
      // One card handed across the table: a soft "fwip" as it slides (noise swept bright → dull),
      // then a muffled "pat" as it lands on the felt. Small random variation so no two cards match.
      const flick = (t: number) => {
        const vary = 0.85 + Math.random() * 0.3;
        const slide = 0.15 * vary;
        const s1 = noise(slide);
        const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 0.9;
        bp.frequency.setValueAtTime(4200 * vary, t);
        bp.frequency.exponentialRampToValueAtTime(1300 * vary, t + slide);
        const g1 = ctx.createGain();
        g1.gain.setValueAtTime(0.0001, t);
        g1.gain.exponentialRampToValueAtTime(0.16, t + slide * 0.35);
        g1.gain.exponentialRampToValueAtTime(0.0001, t + slide);
        s1.connect(bp).connect(g1).connect(ctx.destination); s1.start(t); s1.stop(t + slide);
        const land = t + slide * 0.85, thud = 0.06;
        const s2 = noise(thud);
        const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 700 * vary; lp.Q.value = 0.5;
        const g2 = ctx.createGain();
        g2.gain.setValueAtTime(0.3, land); g2.gain.exponentialRampToValueAtTime(0.0001, land + thud);
        s2.connect(lp).connect(g2).connect(ctx.destination); s2.start(land); s2.stop(land + thud);
      };
      const now = ctx.currentTime;
      for (let i = 0; i < count; i++) flick(now + (i * gapMs) / 1000);
    } catch { /* audio unavailable */ }
  }
  // Crowd cheering and clapping with the confetti: the crowd_cheer.mp3 recording at 75% (it is
  // loud), played through Web Audio so the level holds on iOS too. Decoded once, on first use.
  // If the file can't be loaded, a synthesised cheer stands in.
  private cheerBuf?: Promise<AudioBuffer | null>;
  private playCheer(): void {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx ||= new Ctx();
      const ctx = this.audioCtx;
      if (ctx.state === "suspended") void ctx.resume();
      this.cheerBuf ??= fetch("assets/audio/crowd_cheer.mp3")
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); })
        .then((d) => new Promise<AudioBuffer>((ok, bad) => ctx.decodeAudioData(d, ok, bad)))
        .catch(() => null);
      void this.cheerBuf.then((buf) => {
        if (!buf) return this.synthCheer();
        const src = ctx.createBufferSource(); src.buffer = buf;
        const g = ctx.createGain(); g.gain.value = 0.75;
        src.connect(g).connect(ctx.destination); src.start();
      });
    } catch { /* audio unavailable */ }
  }
  // Stand-in cheer: a swelling crowd roar, a few "woo"s and a couple of dozen people clapping out of step.
  private synthCheer(): void {
    {
      try {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        this.audioCtx ||= new Ctx();
        const ctx = this.audioCtx;
        if (ctx.state === "suspended") void ctx.resume();
        const t0 = ctx.currentTime + 0.05, len = 3.6;
        const out = ctx.createGain(); out.gain.value = 2.2; out.connect(ctx.destination);
        const noiseBuf = (dur: number) => {
          const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
          const d = buf.getChannelData(0);
          for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
          return buf;
        };
        // Crowd roar: noise through vowel-like bands that drift, swelling up then fading.
        const roar = ctx.createBufferSource(); roar.buffer = noiseBuf(len + 0.2);
        const rg = ctx.createGain();
        rg.gain.setValueAtTime(0.0001, t0);
        rg.gain.exponentialRampToValueAtTime(0.5, t0 + 0.45);
        rg.gain.setValueAtTime(0.5, t0 + 1.6);
        rg.gain.exponentialRampToValueAtTime(0.0001, t0 + len);
        for (const [f, q, g] of [[480, 3, 0.9], [1050, 4, 0.6], [2500, 5, 0.25]]) {
          const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = q;
          bp.frequency.setValueAtTime(f * 0.85, t0);
          bp.frequency.linearRampToValueAtTime(f * 1.15, t0 + 0.5);
          bp.frequency.linearRampToValueAtTime(f * 0.95, t0 + len);
          const bg = ctx.createGain(); bg.gain.value = g;
          roar.connect(bp).connect(bg).connect(rg);
        }
        rg.connect(out); roar.start(t0); roar.stop(t0 + len + 0.2);
        // A few voices whooping: gliding tones with a wobble.
        for (let k = 0; k < 5; k++) {
          const st = t0 + 0.1 + Math.random() * 1.2, d = 0.5 + Math.random() * 0.5, base = 380 + Math.random() * 380;
          const o = ctx.createOscillator(); o.type = "triangle";
          o.frequency.setValueAtTime(base, st);
          o.frequency.exponentialRampToValueAtTime(base * 1.9, st + d * 0.6);
          o.frequency.exponentialRampToValueAtTime(base * 1.5, st + d);
          const vib = ctx.createOscillator(); vib.frequency.value = 6 + Math.random() * 2;
          const vg = ctx.createGain(); vg.gain.value = base * 0.03; vib.connect(vg).connect(o.frequency);
          const og = ctx.createGain();
          og.gain.setValueAtTime(0.0001, st);
          og.gain.exponentialRampToValueAtTime(0.035, st + 0.08);
          og.gain.exponentialRampToValueAtTime(0.0001, st + d);
          o.connect(og).connect(out); o.start(st); o.stop(st + d + 0.05); vib.start(st); vib.stop(st + d + 0.05);
        }
        // Clapping: each person claps ~4–5 times a second, slightly out of step, fading out.
        const clapBuf = noiseBuf(0.03);
        for (let p = 0; p < 24; p++) {
          const rate = 3.8 + Math.random() * 1.4, pan = ctx.createStereoPanner(); pan.pan.value = Math.random() * 1.6 - 0.8; pan.connect(out);
          const tone = 900 + Math.random() * 1500;
          for (let t = t0 + 0.15 + Math.random() * 0.4; t < t0 + len - 0.3; t += (1 / rate) * (0.9 + Math.random() * 0.2)) {
            const fade = 1 - (t - t0) / len;
            const src = ctx.createBufferSource(); src.buffer = clapBuf;
            const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = tone; bp.Q.value = 1.2;
            const g = ctx.createGain();
            g.gain.setValueAtTime((0.05 + Math.random() * 0.06) * fade, t);
            g.gain.exponentialRampToValueAtTime(0.0001, t + 0.028);
            src.connect(bp).connect(g).connect(pan); src.start(t); src.stop(t + 0.03);
          }
        }
      } catch { /* audio unavailable */ }
    }
  }
  /** A duck's "quack-quack": a real recording if assets/audio/quack.mp3 is there, otherwise made here. */
  private playQuack(): void {
    new Audio("assets/audio/quack.mp3").play().catch(() => {
      try {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        this.audioCtx ||= new Ctx();
        const ctx = this.audioCtx;
        if (ctx.state === "suspended") void ctx.resume();
        const out = ctx.createGain(); out.gain.value = 0.9; out.connect(ctx.destination);
        // A quack is a nasal buzz: a reedy tone whose pitch drops, shaped by a honking resonance.
        const quack = (t: number, len: number, pitch: number) => {
          const osc = ctx.createOscillator(); osc.type = "sawtooth";
          osc.frequency.setValueAtTime(pitch * 1.25, t);
          osc.frequency.exponentialRampToValueAtTime(pitch, t + 0.04);
          osc.frequency.exponentialRampToValueAtTime(pitch * 0.72, t + len);
          const nasal = ctx.createBiquadFilter(); nasal.type = "bandpass"; nasal.Q.value = 5;
          nasal.frequency.setValueAtTime(1400, t); nasal.frequency.exponentialRampToValueAtTime(900, t + len);
          const body = ctx.createBiquadFilter(); body.type = "peaking"; body.frequency.value = 2600; body.Q.value = 3; body.gain.value = 9;
          const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(1.2, t + 0.015); g.gain.setValueAtTime(1.2, t + len * 0.55);
          g.gain.exponentialRampToValueAtTime(0.0001, t + len);
          osc.connect(nasal).connect(body).connect(g).connect(out); osc.start(t); osc.stop(t + len + 0.02);
        };
        const now = ctx.currentTime;
        quack(now, 0.2, 330); quack(now + 0.27, 0.26, 300);   // quack-quack
      } catch { /* audio unavailable */ }
    });
  }
  private playKnock(): void {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx ||= new Ctx();
      const ctx = this.audioCtx;
      if (ctx.state === "suspended") void ctx.resume();
      // Loud and punchy: a compressor keeps the boosted taps from clipping.
      const out = ctx.createDynamicsCompressor();
      out.threshold.value = -18; out.knee.value = 6; out.ratio.value = 8; out.attack.value = 0.001; out.release.value = 0.1;
      const master = ctx.createGain(); master.gain.value = 2.2;
      out.connect(master).connect(ctx.destination);
      const tap = (t: number) => {
        // short filtered noise burst = knuckle contact
        const dur = 0.07;
        const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 2);
        const src = ctx.createBufferSource(); src.buffer = buf;
        const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 650; lp.Q.value = 4;
        const g = ctx.createGain(); g.gain.value = 1.6;
        src.connect(lp).connect(g).connect(out); src.start(t);
        // low wooden thump under it
        const osc = ctx.createOscillator(); osc.type = "sine"; osc.frequency.setValueAtTime(95, t);
        const og = ctx.createGain(); og.gain.setValueAtTime(0.0001, t);
        og.gain.exponentialRampToValueAtTime(1.0, t + 0.005); og.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
        osc.connect(og).connect(out); osc.start(t); osc.stop(t + 0.18);
      };
      const now = ctx.currentTime;
      tap(now); tap(now + 0.16); // knock-knock
    } catch { /* audio unavailable */ }
  }
  /**
   * A quock: a knock on the knocker's first turn. A duck quacks, a big duck fills the screen for
   * a few seconds, and the quocker turns into a duck that zooms out and fades. Counted in the stats
   * (Knock round 1) and the Hall of Fame's Quocker rankings.
   */
  private async quock(seat: number, name: string, me: boolean): Promise<void> {
    this.playKnock();
    setTimeout(() => this.playQuack(), 350);
    this.showBanner(`<div class="quock-big" aria-hidden="true">🦆</div>${name} quocked!`,
      `Knocked on ${me ? "your" : "the"} first turn — one last turn each, then the showdown`, { knock: true, ms: 3000 });
    // The player's portrait (or your hand) becomes a duck, which then zooms out and fades.
    const target = document.querySelector<HTMLElement>(me ? ".hand-col .photofan" : `.seat[data-seat="${seat}"] .top`);
    const r = target?.getBoundingClientRect();
    if (r && r.width) {
      // Your duck sits low over your hand, clear of the banner's words.
      const size = me ? Math.min(r.width, r.height) * 0.5 : Math.max(r.width, r.height);
      const cy = me ? r.bottom - size / 2 - 8 : r.top + r.height / 2;
      const duck = h("div", { class: "quock-duck", text: "🦆", attr: { "aria-hidden": "true" },
        style: `left:${r.left + r.width / 2}px;top:${cy}px;--sz:${size}px;--c:${this.board?.players[seat]?.colour ?? "#c28d41"}` });
      document.body.appendChild(duck);
      setTimeout(() => duck.remove(), 3600);
    }
    await sleep(2000);   // let the table see it before the next move
  }
  private spokenZipperHand = -1;
  private speakGurge(hand: number): void {
    if (this.spokenZipperHand === hand) return;
    this.spokenZipperHand = hand;
    this.voiceLine("assets/audio/gurge.mp3", "You've got Gurge! Nothing to show.");
  }
  private nextStickyHand = 3 + Math.floor(Math.random() * 5);
  // A spoken line: a real recording wins (drop the mp3 in public/assets/audio/); if it's missing or
  // can't play, fall back to the browser's text-to-speech, shaped by pitch/rate (low + slow ≈ old man).
  // Firefox only starts loading its voice list on the first getVoices() call, and that first call
  // returns nothing — ask at start-up so a voice can be chosen by the time anyone speaks.
  private static voicesWarm = (() => { try { speechSynthesis.getVoices(); speechSynthesis.addEventListener("voiceschanged", () => speechSynthesis.getVoices()); } catch { /* no speech */ } return true; })();
  private voiceLine(file: string, text: string, opt: { pitch?: number; rate?: number; male?: boolean; langs?: string[] } = {}): void {
    new Audio(file).play().catch(() => {
      try {
        const u = new SpeechSynthesisUtterance(text);
        u.lang = opt.langs?.[0] ?? "en-US"; u.rate = opt.rate ?? 1; u.pitch = opt.pitch ?? 1;
        // Preferred accents (in order) outrank everything but a robotic voice.
        const want = (opt.langs ?? []).map((l) => l.toLowerCase().replace("_", "-"));
        // Firefox on Linux lists every eSpeak variant ("English (Great Britain)+Robosoft"): the plain voice wins.
        const langRank = (v: SpeechSynthesisVoice) => { const i = want.indexOf(v.lang.toLowerCase().replace("_", "-")); return i < 0 ? 0 : 90 - i * 10 + (v.name.includes("+") ? 0 : 50); };
        // Pick the most natural English voice available. On Linux, Chrome's first en-US voice is often
        // eSpeak (harsh and robotic), so rank Google/Microsoft/Apple voices first and eSpeak last.
        const score = (v: SpeechSynthesisVoice) =>
          (/espeak/i.test(v.name) ? -100 : 0) + (/Robo|Tweaky|klatt|fast_test|whisper|croak/i.test(v.name) ? -60 : 0) +
          (/Google US English/i.test(v.name) ? 50 : /Google/i.test(v.name) ? 30 : 0) +
          (/\b(Natural|Online|Aria|Jenny|Guy|Samantha|Alex)\b/i.test(v.name) ? 40 : /Microsoft/i.test(v.name) ? 20 : 0) +   // whole words: "Nguyen" is not "Guy"
          (opt.male && /\b(male|Guy|David|Daniel|Fred|George|Mark|Alex)\b/i.test(v.name) ? 35 : 0) +
          langRank(v) + (want.length ? 0 : /en[-_]US/i.test(v.lang) ? 10 : 0) + (/^en/i.test(v.lang) ? 5 : -50);
        const best = speechSynthesis.getVoices().sort((a, b) => score(b) - score(a))[0];
        if (best) u.voice = best;
        speechSynthesis.cancel();
        speechSynthesis.speak(u);
      } catch { /* speech not available */ }
    });
  }
  private factBody(f: ResolvedFact): string {
    return `${f.text} <a href="${f.source.url}" target="_blank" rel="noopener" title="${f.source.title}" style="color:var(--a300);border-bottom:1px solid rgba(194,141,65,.5)">Source ↗</a>`;
  }
  browseNext(code: string | null): void {
    if (!code) return;
    const f = nextFact(code);
    if (f) { this.browse.set(code, f); this.renderMapReadout(); }
  }
  private toastTok = 0;
  showToast(title: string, body: string): void {
    const t = $("toast");
    t.innerHTML = `<button class="x" title="close">×</button><span class="tt">${title}</span><span class="tb">${body}</span>`;
    const tok = ++this.toastTok;
    (t.querySelector(".x") as HTMLButtonElement).onclick = () => { this.toastTok++; t.classList.add("hidden"); };
    t.classList.remove("hidden");
    setTimeout(() => { if (tok === this.toastTok) t.classList.add("hidden"); }, 5000); // auto-dismiss after 5s
  }
  private pushLog(e: LogEntry): void { this.history = [e, ...this.history].slice(0, 60); }

  setLayout(l: "a" | "b" | "c"): void {
    this.layout = l;
    [...$("layout-seg").children].forEach((b) => b.classList.toggle("on", (b as HTMLElement).dataset.layout === l));
    this.renderAll();
  }
}

// ── Music controls (toolbar button + volume panel, shown only for tables that have songs) ──
const ICON_ON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor"/><path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12"/></svg>';
const ICON_OFF = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor"/><path d="M17 9l5 6M22 9l-5 6"/></svg>';
/** Choose the music: follow the table theme (its songs, or the mix), or pick any song — the songs after it are its theme's. */
function songList(changed: () => void): HTMLElement {
  const name = (id: string) => (id === "home" ? "Poli Poker" : themeById(id)?.displayName ?? id);
  const groups = new Map<string, string[]>();
  for (const s of music.songs()) (groups.get(s.theme) ?? groups.set(s.theme, []).get(s.theme)!).push(s.file);
  const now = music.nowPlaying();
  const box = h("div", { class: "mp-list", attr: { role: "group", "aria-label": "Choose the music" } }, [
    h("button", { class: "mp-follow" + (music.picked ? "" : " on"), attr: { type: "button", "aria-pressed": String(!music.picked) },
      text: "♫ Follow the table theme", on: { click: () => { music.followTheme(); changed(); } } }),
  ]);
  for (const [theme, files] of groups) {
    box.appendChild(h("span", { class: "mp-group", text: name(theme) }));
    for (const f of files) {
      const on = music.picked && f === now;
      box.appendChild(h("button", { class: "mp-pick" + (on ? " on" : ""), attr: { type: "button", "aria-pressed": String(on) },
        text: songNote(f)?.title ?? f.replace(/_/g, " ").replace(/\.mp3$/i, ""), on: { click: () => { music.playSong(theme, f); changed(); } } }));
    }
  }
  return box;
}
function wireMusic(): void {
  const wrap = $("music-wrap"), btn = $("music-btn"), panel = $("music-panel"), note = $("music-note");
  const vol = $("music-vol") as HTMLInputElement, pct = $("music-pct"), mute = $("music-mute"), ico = $("music-ico");
  const marquee = $("music-marquee"), text = $("music-text"), src = $("music-src") as HTMLAnchorElement;
  const copy = marquee.querySelector<HTMLElement>(".mp-text[aria-hidden]")!;
  let shown = "", follow = 0;
  // the song you're hearing: “Title”, year. note — scrolling only when it doesn't fit
  const showSong = () => {
    const file = music.nowPlaying() ?? "";
    if (file === shown) return;
    shown = file;
    const n = file ? songNote(file) : null;
    text.innerHTML = "";
    if (n) {
      text.append(h("b", { text: `“${n.title}”` }), n.year ? `, ${n.year}.` : n.text ? "." : "", n.text ? ` ${n.text}` : "");
    }
    copy.innerHTML = text.innerHTML;
    src.hidden = !n?.source;
    if (n?.source) { src.href = n.source.url; src.textContent = `Source: ${n.source.label} ↗`; }
    marquee.classList.remove("scroll");
    const w = text.scrollWidth;
    if (w > marquee.clientWidth) { marquee.style.setProperty("--mp-dur", `${Math.max(8, w / 38)}s`); marquee.classList.add("scroll"); }
  };
  const close = () => { if (panel.hidden) return; panel.hidden = true; btn.setAttribute("aria-expanded", "false"); clearInterval(follow); };
  const render = () => {
    wrap.hidden = false;   // every table has music: its theme's songs, or the mix of them all
    const { volume, muted } = music.prefs, p = `${Math.round(volume * 100)}%`;
    if (document.activeElement !== vol) vol.value = String(Math.round(volume * 100));
    pct.textContent = muted ? "Muted" : p;
    vol.setAttribute("aria-valuetext", muted ? `${p}, muted` : p);
    mute.textContent = muted || volume === 0 ? "Unmute" : "Mute";
    mute.setAttribute("aria-pressed", String(!music.audible));
    ico.innerHTML = music.audible ? ICON_ON : ICON_OFF;
    btn.setAttribute("aria-label", music.audible ? `Music volume ${p}` : "Music muted");
    note.hidden = !music.missing;
    if (!panel.hidden) showSong();
  };
  music.onChange = render;
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    if (panel.hidden) {
      panel.hidden = false; btn.setAttribute("aria-expanded", "true"); vol.focus();
      shown = ""; showSong();
      $("music-list").replaceChildren(songList(() => { shown = ""; showSong(); $("music-list").replaceChildren(songList(() => btn.click())); }));
      clearInterval(follow); follow = window.setInterval(showSong, 1000);   // songs change while the panel is open
    } else close();
  });
  panel.addEventListener("click", (e) => e.stopPropagation());
  panel.addEventListener("keydown", (e) => { if (e.key === "Escape") { close(); btn.focus(); } });
  document.addEventListener("click", close);
  vol.addEventListener("input", () => music.setVolume(Number(vol.value) / 100));
  vol.addEventListener("change", render);
  mute.addEventListener("click", () => music.toggleMute());
  initMusic();
  render();
}

// Entry screens' music: tries to start on page load; browsers that block autoplay hold it until the
// first click / tap / key press (typing the start code counts). Shares volume + mute with the
// in-game music panel; one player, so only one track ever plays.
let entryMusicWired = false;
function wireEntryMusic(): void {
  const box = $("entry-music");
  box.hidden = !hasMusic("home");
  if (entryMusicWired || box.hidden) return;
  entryMusicWired = true;
  const btn = $("entry-music-btn"), ico = $("entry-music-ico"), vol = $("entry-music-vol") as HTMLInputElement;
  const render = () => {
    const { volume } = music.prefs, p = `${Math.round(volume * 100)}%`;
    if (document.activeElement !== vol) vol.value = String(Math.round(volume * 100));
    ico.innerHTML = music.audible ? ICON_ON : ICON_OFF;
    btn.setAttribute("aria-pressed", String(!music.audible));
    btn.setAttribute("aria-label", music.audible ? `Mute music (volume ${p})` : "Turn music on");
    btn.title = music.audible ? "Mute music" : "Turn music on";
    vol.setAttribute("aria-valuetext", music.audible ? p : `${p}, muted`);
  };
  const prev = music.onChange;
  music.onChange = () => { prev?.(); render(); };
  // a first press on the speaker means "music on", not start-then-mute
  btn.addEventListener("click", () => { if (music.isRunning) music.toggleMute(); else { music.start(); if (!music.audible) music.toggleMute(); } });
  vol.addEventListener("input", () => { const v = Number(vol.value) / 100; music.start(); music.setVolume(v); });   // read first: start() redraws the slider
  const kick = (e: Event) => {
    if (box.contains(e.target as Node)) return;            // the control itself handles its first press
    if (!$("app").classList.contains("hidden")) return;    // the game starts its own music
    music.start();
    document.removeEventListener("pointerdown", kick, true); document.removeEventListener("keydown", kick, true);
  };
  if (!music.isRunning) { document.addEventListener("pointerdown", kick, true); document.addEventListener("keydown", kick, true); }
  music.start();                                           // autoplay where the browser allows it
  render();
}

function newMatchKey(): string {
  try { return crypto.randomUUID(); } catch { /* older browsers */ }
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => (Number(c) ^ (Math.random() * 16) >> (Number(c) / 4)).toString(16));
}

// ── Boot ─────────────────────────────────────────────────────────────────────
const app = new App();
function boot(): void {
  applyTheme(savedThemeId());
  wireMusic();
  const show = (id: "home" | "setup") => {
    for (const x of ["gate", "home", "setup", "gallery", "hof"]) $(x).classList.toggle("hidden", x !== id);
    $(id).scrollTop = 0;
    showAccountButton(id === "home");       // New Game shows the account on its player bar instead
    // the home screen has its own music; New Game plays the table theme you're choosing, so you can hear it
    music.setScene(id === "setup" ? "" : "home");
    wireEntryMusic();
  };
  const toGame = () => { music.setScene(""); $("entry-music").hidden = true; showAccountButton(false); music.start(); };
  let setupBuilt = false;
  // Playing needs an account: games are saved with it (any device can resume) and recorded on the Hall of Fame.
  const needSignIn = () => { if (myPlayerId()) return false; openSignIn(); return true; };
  const resume = (save: SaveState) => {
    if (needSignIn()) return;
    toGame(); $("home").classList.add("hidden"); $("setup").classList.add("hidden");
    app.resumeGame(save);
  };
  const openSetup = () => {
    if (needSignIn()) return;
    if (!setupBuilt) {
      app.buildColourPicker(); app.buildAvatarPicker(); setupBuilt = true;
      for (const id of ["setup-name", "setup-party"]) $(id).addEventListener("input", () => { app.showPlayerBar(); app.onProfileEdit?.(); });
    }
    app.showPlayerBar();
    app.showOpponents();
    app.buildThemePicker();
    // Games in progress carry on alongside a new one (up to MAX_GAMES); at the limit, Create waits.
    const full = app.mySaves().length >= MAX_GAMES;
    $("setup-title").textContent = "Set Up Your Game";
    $("setup-start").innerHTML = `<span class="ng-play" aria-hidden="true">▶</span> Create Game`;
    ($("setup-start") as HTMLButtonElement).disabled = full;
    $("setup-resume-note").textContent = `You have ${MAX_GAMES} games in progress. End one on the Home screen to start another.`;
    $("setup-resume-note").hidden = !full;
    show("setup");
  };
  // Home: your games in progress (resume or end each one), then New Game and the rest.
  const ago = (t: number) => {
    const m = Math.round((Date.now() - t) / 60000);
    return m < 2 ? "just now" : m < 60 ? `${m} minutes ago` : m < 120 ? "an hour ago" : m < 1440 ? `${Math.round(m / 60)} hours ago` : m < 2880 ? "yesterday" : `${Math.round(m / 1440)} days ago`;
  };
  const drawGames = () => {
    const saves = app.mySaves(), list = $("home-games-list");
    $("home-resume").hidden = !saves.length;
    $("home-games-h").textContent = saves.length === 1 ? "Your game in progress" : `Your games in progress (${saves.length} of ${MAX_GAMES})`;
    list.replaceChildren(...saves.map((sv) => {
      const me = sv.totals.find((t) => t.id === "you");
      const place = me ? [...sv.totals].sort((x, y) => y.score - x.score || y.states.length - x.states.length).indexOf(me) + 1 : 0;
      const li = h("li", { class: "hg" });
      const info = h("span", { class: "hg-info", html: `<b>Hand ${sv.startHand} / 50</b>` +
        (me ? ` · ${["1st", "2nd", "3rd", "4th", "5th"][place - 1]} with ${me.score} pts, ${me.states.length} ${me.states.length === 1 ? "state" : "states"}` : "") +
        `<small>Last played ${ago(sv.savedAt)}</small>` });
      const normal = () => li.replaceChildren(info, h("span", { class: "hg-btns" }, [
        h("button", { class: "ui-btn", text: "Resume", attr: { type: "button" }, on: { click: () => resume(sv) } }),
        h("button", { class: "ui-btn-ghost", text: "End game", attr: { type: "button" }, on: { click: ask } }),
      ]));
      const ask = () => li.replaceChildren(h("span", { class: "hg-info hg-ask", html: `<b>End this game?</b><small>It's removed from all your devices and won't go on the Hall of Fame.</small>` }), h("span", { class: "hg-btns" }, [
        h("button", { class: "ui-btn hg-danger", text: "End game", attr: { type: "button" }, on: { click: async () => { li.classList.add("busy"); await app.endSavedGame(sv.matchKey!); drawGames(); } } }),
        h("button", { class: "ui-btn-ghost", text: "Keep it", attr: { type: "button" }, on: { click: normal } }),
      ]));
      normal();
      return li;
    }));
    const full = saves.length >= MAX_GAMES;
    $("home-new").textContent = !myPlayerId() ? "Sign in to play" : "New Game";
    $("home-new").title = full ? `You have ${MAX_GAMES} games in progress. End one to start another.` : "";
    ($("home-new") as HTMLButtonElement).disabled = !!myPlayerId() && full;
  };
  const openHome = () => {
    drawGames();
    show("home");
    openInvite();
  };
  const tryEnter = () => {
    if (($("gate-code") as HTMLInputElement).value.trim() === ACCESS_CODE) { sessionStorage.setItem("poli-ok", "1"); openHome(); }
    else $("gate-error").classList.remove("hidden");
  };
  // Signing in (or out) brings this device's game in step with the account, then the home screen with it.
  app.onAccountChange = () => { void app.syncSave().then(() => { if (!$("home").classList.contains("hidden")) openHome(); }); };
  // Play with friends (multiplayer tables), when the server has it open. An invite link (?table=CODE)
  // opens that table once you're past the gate and signed in.
  const invite = new URLSearchParams(location.search).get("table");
  if (invite) { sessionStorage.setItem("poli-invite", invite.toUpperCase()); history.replaceState(null, "", location.pathname); }
  function openInvite(): void {
    const code = sessionStorage.getItem("poli-invite");
    if (!code || !myPlayerId()) return;
    sessionStorage.removeItem("poli-invite");
    $("home").classList.add("hidden"); showAccountButton(false);
    openTables(code);
  }
  // turning a phone between portrait and landscape switches between the full and compact game screens
  COMPACT.addEventListener("change", () => { if (!$("app").classList.contains("hidden")) app.renderAll(); else document.body.classList.toggle("compact", COMPACT.matches); });
  document.body.classList.toggle("compact", COMPACT.matches);
  const remote = new RemoteGame(app, app.human);
  initTables({ onClose: openHome, needSignIn, onGame: (m) => remote.receive(m, myPlayerId() ?? ""), onMoveRefused: (e) => remote.refused(e),
    // the host closed the table mid-game: start afresh on the home screen and say why
    onGameGone: (reason) => { try { sessionStorage.setItem("poli-mp-gone", reason); } catch { /* */ } location.href = "/"; } });
  try {
    if (sessionStorage.getItem("poli-ended")) { sessionStorage.removeItem("poli-ended"); app.showToast("Game ended", "That game is gone. Start a new one whenever you like."); }
    const gone = sessionStorage.getItem("poli-mp-gone");
    if (gone) { sessionStorage.removeItem("poli-mp-gone"); app.showToast("Table closed", gone); }
  } catch { /* */ }
  void multiplayerOpen().then((on) => { $("home-mp").hidden = !on; });
  $("home-mp").onclick = () => { if (needSignIn()) return; $("home").classList.add("hidden"); showAccountButton(false); openTables(); };
  initAccount(app, { openEditor: () => { openSetup(); ($("ng-edit-player") as HTMLButtonElement).click(); }, onGameRecorded: hofChanged });
  $("gate-enter").onclick = tryEnter;
  $("gate-code").addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") tryEnter(); });
  if (sessionStorage.getItem("poli-ok")) openHome();
  else { music.setScene("home"); wireEntryMusic(); }       // the gate plays the home music too
  $("home-new").onclick = openSetup;
  $("ng-choose-opps").onclick = () => void app.chooseOpponents();
  $("home-howto").onclick = () => $("info-modal").classList.remove("hidden");
  // The Hall of Fame opens from any screen. From Home it replaces Home; anywhere else (a game, the
  // map, hand results, New Game, the final results) it opens on top, and closing it goes back there.
  let hofFromHome = false;
  const openHof = () => {
    hofFromHome = !$("home").classList.contains("hidden");
    $("hof").classList.toggle("hof-over", !hofFromHome);
    $("hof-back").textContent = hofFromHome ? "‹ Home" : "‹ Back";
    if (hofFromHome) { $("home").classList.add("hidden"); showAccountButton(false); }
    openHallOfFame();
  };
  initHallOfFame({ onClose: () => { if (hofFromHome) openHome(); } });
  $("home-hof").onclick = openHof;
  document.addEventListener("click", (e) => { if ((e.target as Element).closest?.("[data-hof]")) { e.stopPropagation(); openHof(); } }, true);
  $("setup-back").onclick = openHome;
  $("ng-change-theme").onclick = () => app.openGallery();
  $("ng-preview-cards").onclick = () => openCardPreview(currentTheme().id);
  $("ng-howto").onclick = () => $("info-modal").classList.remove("hidden");
  // Edit Player drawer: the avatar customiser. Its choices save as they're made, so closing keeps them.
  const drawer = $("avatar-drawer"), editBtn = $("ng-edit-player") as HTMLButtonElement;
  const closeDrawer = () => { if (drawer.hidden) return; drawer.hidden = true; document.body.classList.remove("ng-drawer-open"); editBtn.focus(); };
  editBtn.onclick = () => { drawer.hidden = false; document.body.classList.add("ng-drawer-open"); (drawer.querySelector(".ng-drawer-x") as HTMLElement).focus(); };
  drawer.addEventListener("click", (e) => { if ((e.target as HTMLElement).closest("[data-close]")) closeDrawer(); });
  drawer.addEventListener("keydown", (e) => {
    if (e.key === "Escape") { e.stopPropagation(); closeDrawer(); return; }
    if (e.key !== "Tab") return;                           // keep Tab inside the drawer
    const f = [...drawer.querySelectorAll<HTMLElement>("button, input:not([type=file]), [tabindex]")].filter((x) => x.offsetParent);
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  $("gallery-back").onclick = () => app.closeGallery();
  $("gallery").addEventListener("keydown", (e) => { if (e.key === "Escape") app.closeGallery(); });
  $("setup-start").onclick = () => { if (needSignIn() || app.mySaves().length >= MAX_GAMES) return; toGame(); app.start(); };
  $("play-again").onclick = () => { stopAnthem(0.3); setTimeout(() => location.reload(), 300); };
  $("save-pdf").onclick = () => void app.savePdf();
  $("end-x").onclick = () => app.closeEndScreen();
  $("end-reopen").onclick = () => app.openEndScreen();
  $("endscreen").addEventListener("keydown", (e) => { if (e.key === "Escape") { e.stopPropagation(); app.closeEndScreen(); } });
  $("board-btn").onclick = () => app.openMap();
  document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !$("log-modal").classList.contains("hidden")) app.closeHandLog(); });
  $("stats-btn").onclick = () => app.openStats();
  $("theme-btn").onclick = () => app.openThemeModal();
  $("leave-btn").onclick = () => app.openLeave();
  // Android: Back closes whatever is open on top first; in a game it offers Save & exit; on Home it closes the app.
  const shown = (id: string) => { const e = document.getElementById(id); return !!e && !e.hidden && !e.classList.contains("hidden"); };
  const escape = (id: string) => document.getElementById(id)?.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
  const onBack = () => {
    const panel = document.querySelector(".peek-panel"); if (panel) { panel.remove(); return; }
    const inaug = document.querySelector<HTMLElement>(".inaug"); if (inaug) { inaug.click(); return; }
    if (app.closeCompactPop()) return;
    const ask = document.querySelector<HTMLElement>(".claim-confirm .btn:not(.primary)"); if (ask) { ask.click(); return; }
    const closers: [string, () => void][] = [
      ["leave-modal", () => $("leave-cancel").click()], ["theme-modal", () => $("theme-cancel").click()], ["info-modal", () => $("info-close").click()],
      ["stats-modal", () => app.closeStats()], ["log-modal", () => app.closeHandLog()], ["lightbox", () => escape("lightbox")], ["signin", () => escape("signin")],
      ["avatar-drawer", () => (document.querySelector("#avatar-drawer [data-close]") as HTMLElement | null)?.click()],
      ["hof", () => $("hof-back").click()], ["gallery", () => $("gallery-back").click()],
    ];
    for (const [id, close] of closers) if (shown(id)) { close(); return; }
    if (shown("map-modal") && app.closeMapIfAllowed()) return;
    if (shown("app") && app.isActive()) { app.openLeave(); return; }
    if (shown("app") || shown("endscreen")) { location.href = "/"; return; }
    if (shown("mp")) { $("mp-back").click(); return; }
    if (shown("setup")) { $("setup-back").click(); return; }
    void capApp()?.exitApp();
  };
  void capApp()?.addListener("backButton", onBack);
  // auto-save is after every choice; leaving the screen also sends it to your account (best effort)
  void capApp()?.addListener("pause", () => app.saveOnHide());
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") app.saveOnHide(); });
  $("info-btn").onclick = () => $("info-modal").classList.remove("hidden");
  $("info-close").onclick = () => $("info-modal").classList.add("hidden");
  $("info-modal").onclick = (e) => { if (e.target === $("info-modal")) $("info-modal").classList.add("hidden"); };
  for (const b of [...$("layout-seg").children]) (b as HTMLElement).onclick = () => app.setLayout((b as HTMLElement).dataset.layout as "a" | "b" | "c");
  // Warn before an accidental refresh/close loses the in-progress game.
  window.addEventListener("beforeunload", (e) => { if (app.isActive()) { e.preventDefault(); e.returnValue = ""; } });
}
boot();
